import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminDashboard.css';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';
import BulkCourseImport from './BulkCourseImport';
import BulkRegistrationImport from './BulkRegistrationImport';
import AdminTagManagement from './AdminTagManagement';
import ProjectTagAllocationHub from './ProjectTagAllocationHub';
import ReportDashboard from './ReportDashboard';
import AdminAuditLogs from './AdminAuditLogs';
import SystemSettings from './SystemSettings';
import AdminFeedbackManagement from './AdminFeedbackManagement';
import AccountRegistrationApprovals from '../../components/Admin/AccountRegistrationApprovals';
import PendingAccountUpdates from '../../components/Admin/PendingAccountUpdates';
import NotificationCentre from '../../components/NotificationCentre/NotificationCentre';

// ── Visual Rich Text Description Editor (WYSIWYG - Bold, Italic, Underline, Bullet) ──
const RichTextEditor = ({
  label = "Course Overview / Description",
  value,
  onChange,
  placeholder = "e.g. 3 days training. Including Lunch, e-Student guide and exam voucher.",
  minHeight = "100px"
}) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    insertUnorderedList: false
  });

  const checkActiveFormats = useCallback(() => {
    try {
      if (!editorRef.current) return;
      const isBold = document.queryCommandState('bold');
      const isItalic = document.queryCommandState('italic');
      const isUnderline = document.queryCommandState('underline');
      const isList = document.queryCommandState('insertUnorderedList');
      setActiveFormats(prev => {
        if (
          prev.bold === isBold &&
          prev.italic === isItalic &&
          prev.underline === isUnderline &&
          prev.insertUnorderedList === isList
        ) {
          return prev;
        }
        return { bold: isBold, italic: isItalic, underline: isUnderline, insertUnorderedList: isList };
      });
    } catch (e) {
      // Ignore if document not focused or queryCommandState not supported
    }
  }, []);

  // Sync value into contentEditable ONLY when value changes externally and editor is not actively focused
  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      if (editorRef.current.innerHTML !== (value || '')) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      const cleanHtml = (html === '<br>' || html === '<p><br></p>' || html === '<div><br></div>') ? '' : html;
      onChange(cleanHtml);
    }
  };

  const executeCmd = (command) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, null);
      emitChange();
      setTimeout(checkActiveFormats, 10);
    }
  };

  return (
    <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
        {label}
      </label>

      {/* Formatting Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 8px',
        background: '#f8fafc',
        border: '1px solid #cbd5e1',
        borderBottom: 'none',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          className={`desc-toolbar-btn ${activeFormats.bold ? 'active' : ''}`}
          title="Bold (Ctrl+B)"
          onMouseDown={(e) => { e.preventDefault(); executeCmd('bold'); }}
          style={{
            fontWeight: '800',
            fontSize: '0.85rem',
            padding: '4px 10px',
            borderRadius: '4px',
            border: `1px solid ${activeFormats.bold ? '#4338ca' : '#cbd5e1'}`,
            background: activeFormats.bold ? '#4f46e5' : '#ffffff',
            color: activeFormats.bold ? '#ffffff' : '#1e293b',
            cursor: 'pointer',
            minWidth: '28px',
            boxShadow: activeFormats.bold ? 'inset 0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          <strong>B</strong>
        </button>

        <button
          type="button"
          className={`desc-toolbar-btn ${activeFormats.italic ? 'active' : ''}`}
          title="Italic (Ctrl+I)"
          onMouseDown={(e) => { e.preventDefault(); executeCmd('italic'); }}
          style={{
            fontStyle: 'italic',
            fontWeight: '600',
            fontSize: '0.85rem',
            padding: '4px 10px',
            borderRadius: '4px',
            border: `1px solid ${activeFormats.italic ? '#4338ca' : '#cbd5e1'}`,
            background: activeFormats.italic ? '#4f46e5' : '#ffffff',
            color: activeFormats.italic ? '#ffffff' : '#1e293b',
            cursor: 'pointer',
            minWidth: '28px',
            boxShadow: activeFormats.italic ? 'inset 0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          <em>I</em>
        </button>

        <button
          type="button"
          className={`desc-toolbar-btn ${activeFormats.underline ? 'active' : ''}`}
          title="Underline (Ctrl+U)"
          onMouseDown={(e) => { e.preventDefault(); executeCmd('underline'); }}
          style={{
            textDecoration: 'underline',
            fontWeight: '600',
            fontSize: '0.85rem',
            padding: '4px 10px',
            borderRadius: '4px',
            border: `1px solid ${activeFormats.underline ? '#4338ca' : '#cbd5e1'}`,
            background: activeFormats.underline ? '#4f46e5' : '#ffffff',
            color: activeFormats.underline ? '#ffffff' : '#1e293b',
            cursor: 'pointer',
            minWidth: '28px',
            boxShadow: activeFormats.underline ? 'inset 0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          <u>U</u>
        </button>

        <div style={{ width: '1px', height: '18px', background: '#cbd5e1', margin: '0 4px' }} />

        <button
          type="button"
          className={`desc-toolbar-btn ${activeFormats.insertUnorderedList ? 'active' : ''}`}
          title="Bullet List"
          onMouseDown={(e) => { e.preventDefault(); executeCmd('insertUnorderedList'); }}
          style={{
            fontSize: '0.78rem',
            fontWeight: '600',
            padding: '4px 8px',
            borderRadius: '4px',
            border: `1px solid ${activeFormats.insertUnorderedList ? '#4338ca' : '#cbd5e1'}`,
            background: activeFormats.insertUnorderedList ? '#4f46e5' : '#ffffff',
            color: activeFormats.insertUnorderedList ? '#ffffff' : '#334155',
            cursor: 'pointer',
            boxShadow: activeFormats.insertUnorderedList ? 'inset 0 1px 2px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          • Bullet List
        </button>
      </div>

      {/* Visual ContentEditable Description Box */}
      <div
        ref={editorRef}
        contentEditable
        onInput={() => { emitChange(); }}
        onBlur={() => { emitChange(); setTimeout(checkActiveFormats, 10); }}
        onKeyUp={() => setTimeout(checkActiveFormats, 10)}
        onMouseUp={() => setTimeout(checkActiveFormats, 10)}
        data-placeholder={placeholder}
        className="rich-desc-editor"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.65rem 0.85rem',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
          border: '1px solid #cbd5e1',
          fontSize: '0.875rem',
          outline: 'none',
          background: '#ffffff',
          minHeight: minHeight,
          maxHeight: '260px',
          overflowY: 'auto',
          lineHeight: '1.5',
          textAlign: 'left',
          color: '#1e293b'
        }}
      />
    </div>
  );
};

const AdminDashboard = ({ user, onLogout, onSwitchView, activeView }) => {
  const userRole = (user?.role || user?.Role || '').trim().toLowerCase();
  const isEditor = userRole === 'editor';
  const isReportViewer = userRole === 'report viewer' || userRole === 'reportviewer' || userRole === 'viewer';
  const [toast, setToast] = useState(null);
  const [pendingList, setPendingList] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [sendingAdminDigest, setSendingAdminDigest] = useState(false);
  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [upcomingViewMode, setUpcomingViewMode] = useState('table'); // 'table' | 'cards'
  const [openExportModalOnReportView, setOpenExportModalOnReportView] = useState(false);

  const DEFAULT_ONSITE_PAYMENT_BODY = `Dear Sir / Madam,

Thank you for registering for the {CourseName} course. We are excited to have you join us and look forward to providing you with a valuable learning experience.

Please be informed that enrollment in this course requires a fee of {Fee}. This training will be held onsite at {Venue}.

For your convenience, we have attached the payment link, and we kindly request that you complete the payment before the course begins to confirm your participation.

{PaymentLink}

Kindly be informed that CCoE reserve the right to reschedule the class. We appreciate your prompt attention to this matter. If you have any questions or require further assistance, please do not hesitate to contact us.

See you there!

Regards,
Ezzah Khan (she/her)
Education Programs Registrar, CCoE, Malaysia
Office: +601 73870241
CCoE Website

BlackBerry® Intelligent Security. Everywhere.`;

  const DEFAULT_ONLINE_PAYMENT_BODY = `Dear Sir / Madam,

Thank you for registering for the {CourseName} course. We are excited to have you join us and look forward to providing you with a valuable virtual learning experience.

Please be informed that enrollment in this course requires a fee of {Fee}. This training will be conducted live online via our virtual classroom platform. Joining links and access instructions will be sent prior to the session start.

For your convenience, we have attached the payment link, and we kindly request that you complete the payment before the course begins to confirm your participation.

{PaymentLink}

Kindly be informed that CCoE reserve the right to reschedule the class. We appreciate your prompt attention to this matter. If you have any questions or require further assistance, please do not hesitate to contact us.

See you online!

Regards,
Ezzah Khan (she/her)
Education Programs Registrar, CCoE, Malaysia
Office: +601 73870241
CCoE Website

BlackBerry® Intelligent Security. Everywhere.`;

  const DEFAULT_PAYMENT_BODY = DEFAULT_ONSITE_PAYMENT_BODY;

  // Cancel Session Modal state
  const [cancelModalSchedule, setCancelModalSchedule] = useState(null);
  const [cancelEmailSubject, setCancelEmailSubject] = useState('');
  const [cancelEmailBody, setCancelEmailBody] = useState('');
  const [isCancellingSchedule, setIsCancellingSchedule] = useState(false);

  // Session Registration tab state ('pending' | 'payment')
  const [approvalTab, setApprovalTab] = useState('pending');

  // Master Course Creation Modal state (+ Create Course)
  const [isMasterCourseModalOpen, setIsMasterCourseModalOpen] = useState(false);
  const [editingMasterCourse, setEditingMasterCourse] = useState(null);
  const [masterCertBody, setMasterCertBody] = useState('ISC2');
  const [masterCourseTitle, setMasterCourseTitle] = useState('');
  const [masterLevel, setMasterLevel] = useState('Entry');
  const [masterCourseDesc, setMasterCourseDesc] = useState('');
  const [masterDeliveryMode, setMasterDeliveryMode] = useState('Onsite');
  const [masterFee, setMasterFee] = useState('RM 2,100');
  const [masterPaymentLink, setMasterPaymentLink] = useState('https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
  const [masterPaymentSubject, setMasterPaymentSubject] = useState('{CourseName} Course Payment');
  const [masterPaymentBody, setMasterPaymentBody] = useState(DEFAULT_ONSITE_PAYMENT_BODY);
  const [savingMasterCourse, setSavingMasterCourse] = useState(false);
  const [availableCertTemplates, setAvailableCertTemplates] = useState([]);
  const [masterAwardedTemplates, setMasterAwardedTemplates] = useState(['MCMC']);

  // Courses View active tab ('courses' | 'classes')
  const [coursesActiveTab, setCoursesActiveTab] = useState('courses');
  const [masterCourseSearch, setMasterCourseSearch] = useState('');

  // Course Session Creation Modal state (+ Create Session)
  const [isCreateSessionModalOpen, setIsCreateSessionModalOpen] = useState(false);
  const [masterCoursesList, setMasterCoursesList] = useState([]);
  const [loadingMasterCourses, setLoadingMasterCourses] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const courseDropdownRef = useRef(null);

  const [sessionStartDate, setSessionStartDate] = useState('');
  const [sessionEndDate, setSessionEndDate] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState('09:00 AM');
  const [sessionEndTime, setSessionEndTime] = useState('05:00 PM');
  const [sessionSegments, setSessionSegments] = useState([
    { startDate: '', endDate: '', startTime: '09:00 AM', endTime: '05:00 PM' }
  ]);
  const [sessionTrainerId, setSessionTrainerId] = useState('');
  const [sessionTrainerSearchTerm, setSessionTrainerSearchTerm] = useState('');
  const [isSessionTrainerDropdownOpen, setIsSessionTrainerDropdownOpen] = useState(false);
  const sessionTrainerDropdownRef = useRef(null);
  const [sessionMaxCap, setSessionMaxCap] = useState(10);
  const [sessionFee, setSessionFee] = useState('RM 2,100');
  const [sessionPaymentLink, setSessionPaymentLink] = useState('https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
  const [sessionPaymentSubject, setSessionPaymentSubject] = useState('{CourseName} Course Payment');
  const [sessionPaymentBody, setSessionPaymentBody] = useState(DEFAULT_ONSITE_PAYMENT_BODY);
  const [sessionDeliveryMode, setSessionDeliveryMode] = useState('Onsite');
  const [savingSession, setSavingSession] = useState(false);

  // Edit Existing Course Schedule Modal state
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [certBody, setCertBody] = useState('ISC2');
  const [courseTitle, setCourseTitle] = useState('');
  const [courseType, setCourseType] = useState('Training & Exam');
  const [courseDesc, setCourseDesc] = useState('');
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [trainersList, setTrainersList] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00 AM');
  const [endTime, setEndTime] = useState('05:00 PM');
  const [editSegments, setEditSegments] = useState([
    { startDate: '', endDate: '', startTime: '09:00 AM', endTime: '05:00 PM' }
  ]);
  const [maxCap, setMaxCap] = useState(10);
  const [fee, setFee] = useState('RM 2,100');
  const [paymentLink, setPaymentLink] = useState('https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
  const [paymentSubject, setPaymentSubject] = useState('{CourseName} Course Payment');
  const [paymentBody, setPaymentBody] = useState(DEFAULT_PAYMENT_BODY);
  const [savingCourse, setSavingCourse] = useState(false);
  const [viewBankSlip, setViewBankSlip] = useState(null);

  // Payment Email Preview Modal State
  const [paymentEmailModal, setPaymentEmailModal] = useState(null);
  const [isSendingPaymentEmail, setIsSendingPaymentEmail] = useState(false);

  // Certificate Management Modal State
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [selectedCertSchedule, setSelectedCertSchedule] = useState(null);
  const [certScheduleData, setCertScheduleData] = useState(null);
  const [loadingCertData, setLoadingCertData] = useState(false);
  const [issuingCertRegId, setIssuingCertRegId] = useState(null);
  const [regeneratingCertId, setRegeneratingCertId] = useState(null);

  const [trainerSearchTerm, setTrainerSearchTerm] = useState('');
  const [isTrainerDropdownOpen, setIsTrainerDropdownOpen] = useState(false);
  const trainerDropdownRef = useRef(null);

  const handleSelectCourseForSession = (course) => {
    setSelectedCourseId(String(course.CourseID));
    setCourseSearchTerm(course.CourseName);
    setIsCourseDropdownOpen(false);
    const mode = course.DeliveryMode || 'Onsite';
    setSessionDeliveryMode(mode);
    setSessionFee(course.Fee || 'RM 2,100');
    setSessionPaymentLink(course.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
    setSessionPaymentSubject(course.PaymentEmailSubject || '{CourseName} Course Payment');
    setSessionPaymentBody(course.PaymentEmailTemplate || (mode === 'Online' ? DEFAULT_ONLINE_PAYMENT_BODY : DEFAULT_ONSITE_PAYMENT_BODY));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // 1. Edit modal trainer dropdown
      if (trainerDropdownRef.current && !trainerDropdownRef.current.contains(event.target)) {
        setIsTrainerDropdownOpen(false);
        if (!selectedTrainerId && trainerSearchTerm.trim()) {
          const clean = trainerSearchTerm.trim().toLowerCase();
          const match = trainersList.find(t => (t.FullName || '').toLowerCase() === clean);
          if (match) {
            setSelectedTrainerId(String(match.TrainerID));
            setTrainerSearchTerm(match.FullName);
          } else if (clean === 'tba' || clean.includes('no trainer')) {
            setSelectedTrainerId('');
            setTrainerSearchTerm('');
          } else {
            setTrainerSearchTerm('');
          }
        }
      }

      // 2. Create Session: Course search dropdown
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(event.target)) {
        setIsCourseDropdownOpen(false);
        if (!selectedCourseId && courseSearchTerm.trim()) {
          const clean = courseSearchTerm.trim().toLowerCase();
          const match = masterCoursesList.find(c =>
            (c.CourseName || '').toLowerCase() === clean ||
            `${c.CertificationBody || ''} ${c.CourseName || ''}`.toLowerCase() === clean
          );
          if (match) {
            handleSelectCourseForSession(match);
          } else {
            setCourseSearchTerm('');
          }
        }
      }

      // 3. Create Session: Trainer dropdown
      if (sessionTrainerDropdownRef.current && !sessionTrainerDropdownRef.current.contains(event.target)) {
        setIsSessionTrainerDropdownOpen(false);
        if (!sessionTrainerId && sessionTrainerSearchTerm.trim()) {
          const clean = sessionTrainerSearchTerm.trim().toLowerCase();
          const match = trainersList.find(t => (t.FullName || '').toLowerCase() === clean);
          if (match) {
            setSessionTrainerId(String(match.TrainerID));
            setSessionTrainerSearchTerm(match.FullName);
          } else if (clean === 'tba' || clean.includes('no trainer')) {
            setSessionTrainerId('');
            setSessionTrainerSearchTerm('');
          } else {
            setSessionTrainerSearchTerm('');
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedTrainerId, trainerSearchTerm, trainersList, selectedCourseId, courseSearchTerm, masterCoursesList, sessionTrainerId, sessionTrainerSearchTerm]);

  const handleOpenCertModal = async (s) => {
    setSelectedCertSchedule(s);
    setIsCertModalOpen(true);
    setLoadingCertData(true);
    setCertScheduleData(null);
    try {
      const scheduleId = s.id || s.ScheduleID;
      const res = await fetch(`/api/admin/certificates/schedule/${scheduleId}`);
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setCertScheduleData(data.data);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to load certificate eligibility.' });
      }
    } catch (err) {
      console.error('Error fetching certificate data:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setLoadingCertData(false);
    }
  };

  const handleIssueCertificate = async (regId, force = false) => {
    setIssuingCertRegId(regId);
    try {
      const res = await fetch('/api/admin/certificates/issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationId: regId,
          issuedByAdminId: user?.adminId || null,
          force
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || 'Certificate issued successfully!' });
        if (selectedCertSchedule) {
          const scheduleId = selectedCertSchedule.id || selectedCertSchedule.ScheduleID;
          const refreshRes = await fetch(`/api/admin/certificates/schedule/${scheduleId}`);
          const refreshData = await refreshRes.json();
          if (refreshData.status === 'success') {
            setCertScheduleData(refreshData.data);
          }
        }
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to issue certificate.' });
      }
    } catch (err) {
      console.error('Error issuing certificate:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIssuingCertRegId(null);
    }
  };

  const handleRegenerateCert = async (certId) => {
    setRegeneratingCertId(certId);
    try {
      const res = await fetch(`/api/admin/certificates/regenerate/${certId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          issuedByAdminId: user?.adminId || null
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || 'Certificate regenerated with updated learner name!' });
        if (selectedCertSchedule) {
          const scheduleId = selectedCertSchedule.id || selectedCertSchedule.ScheduleID;
          const refreshRes = await fetch(`/api/admin/certificates/schedule/${scheduleId}`);
          const refreshData = await refreshRes.json();
          if (refreshData.status === 'success') {
            setCertScheduleData(refreshData.data);
          }
        }
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to regenerate certificate.' });
      }
    } catch (err) {
      console.error('Error regenerating certificate:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setRegeneratingCertId(null);
    }
  };

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatToDMY = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split(' ')[0].split('-');
    if (parts.length < 3) return dateStr;
    const [y, m, d] = parts;
    return `${d}/${m}/${y}`;
  };

  const calculateSegmentDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
    const diffTime = Math.abs(e - s);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const calculateTotalTrainingDays = (segments) => {
    if (!Array.isArray(segments) || segments.length === 0) return 0;
    return segments.reduce((sum, seg) => sum + calculateSegmentDays(seg.startDate, seg.endDate), 0);
  };

  const parseDateStr = (str) => {
    if (!str) return null;
    if (typeof str === 'string' && str.includes(' ')) {
      return new Date(str.replace(' ', 'T'));
    }
    return new Date(str);
  };

  const getMonthInfo = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d || isNaN(d.getTime())) {
      return {
        key: '9999-99',
        shortMonth: 'OTHER',
        fullLabel: 'Unscheduled / Other',
        year: 9999,
        monthNum: 99
      };
    }
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const shortMonth = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const fullLabel = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return { key, shortMonth, fullLabel, year, monthNum: month, timestamp: d.getTime() };
  };

  const fetchPendingApprovals = useCallback(async () => {
    setLoadingApprovals(true);
    try {
      const res = await fetch('/api/admin/pending-registrations');
      const data = await res.json();
      if (data.status === 'success') {
        setPendingList(data.data);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to fetch pending approvals.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setLoadingApprovals(false);
    }
  }, []);

  const [allSchedules, setAllSchedules] = useState([]);
  const [coursesSearch, setCoursesSearch] = useState('');
  const [coursesTimeframeTab, setCoursesTimeframeTab] = useState('upcoming'); // 'upcoming' | 'past'

  const fetchUpcomingCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const res = await fetch('/api/schedules');
      const data = await res.json();
      if (data.status === 'success') {
        const today = new Date();
        const day = today.getDay();
        const diffToMonday = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(today.setDate(diffToMonday));
        monday.setHours(0, 0, 0, 0);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        const mappedSchedules = data.data.map(s => {
          const enrollCount = s.EnrolledCount || 0;
          const maxCapVal = s.MaxCapacity || 10;
          const isClosed = s.IsClosed === 1;
          const isCancelled = s.IsCancelled === 1 || s.Status === 'Cancelled';

          let status = 'Open';
          let statusClass = 'status-open';
          if (isCancelled) {
            status = 'Cancelled';
            statusClass = 'status-rejected';
          } else if (isClosed) {
            status = 'Closed';
            statusClass = 'status-full';
          } else if (enrollCount >= maxCapVal) {
            status = 'Full';
            statusClass = 'status-full';
          }

          let parsedSegments = null;
          if (s.SessionSegments) {
            try {
              parsedSegments = typeof s.SessionSegments === 'string' ? JSON.parse(s.SessionSegments) : s.SessionSegments;
            } catch (e) {
              parsedSegments = null;
            }
          }

          const hasMultiSegments = Array.isArray(parsedSegments) && parsedSegments.length > 1;

          return {
            id: s.ScheduleID,
            courseId: s.CourseID,
            certificationBody: s.CertificationBody || '',
            courseName: s.CourseName || '',
            level: s.Level || 'Entry',
            type: s.Type || 'Training',
            description: s.Description || '',
            course: `${s.CertificationBody || ''} ${s.CourseName}`.trim(),
            trainer: s.TrainerName || 'TBA',
            trainerId: s.TrainerID,
            startDate: s.StartDate,
            endDate: s.EndDate,
            startTime: s.StartTime || '09:00 AM',
            endTime: s.EndTime || '05:00 PM',
            sessionSegments: parsedSegments,
            dates: hasMultiSegments
              ? `${formatToDMY(s.StartDate)} - ${formatToDMY(s.EndDate)} (${parsedSegments.length} Parts)`
              : `${formatToDMY(s.StartDate)} - ${formatToDMY(s.EndDate)}`,
            venue: s.Type === 'Online' ? 'Online' : 'CCoE, Cyberjaya',
            enrolledCount: enrollCount,
            maxCapacity: maxCapVal,
            fee: s.Fee || 'RM 2,100',
            paymentLink: s.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i',
            paymentEmailSubject: s.PaymentEmailSubject || '{CourseName} Course Payment',
            paymentEmailBody: s.PaymentEmailBody || '',
            registered: `${enrollCount} / ${maxCapVal}`,
            isClosed: isClosed,
            isCancelled: isCancelled,
            cancellationReason: s.CancellationReason || '',
            status: status,
            statusClass: statusClass
          };
        });

        setAllSchedules(mappedSchedules);

        const thisWeekCourses = mappedSchedules.filter(s => {
          const sDate = new Date(s.startDate);
          return sDate >= monday && sDate <= sunday;
        });

        setUpcomingCourses(thisWeekCourses);
      }
    } catch (err) {
      console.error('Error fetching schedules:', err);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  const fetchMasterCourses = useCallback(async () => {
    setLoadingMasterCourses(true);
    try {
      const res = await fetch('/api/admin/courses/list');
      const data = await res.json();
      if (data.status === 'success') {
        setMasterCoursesList(data.data);
      }
    } catch (err) {
      console.error('Error fetching master courses:', err);
    } finally {
      setLoadingMasterCourses(false);
    }
  }, []);

  const fetchCertificateTemplates = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/certificate-templates');
      const data = await res.json();
      if (res.ok && data.status === 'success' && Array.isArray(data.data)) {
        setAvailableCertTemplates(data.data.filter(t => t.IsActive));
      }
    } catch (e) {
      console.error('Failed to load certificate templates:', e);
    }
  }, []);

  const handleOpenCreateMasterCourseModal = () => {
    fetchCertificateTemplates();
    setEditingMasterCourse(null);
    setMasterCertBody('ISC2');
    setMasterCourseTitle('');
    setMasterLevel('Entry');
    setMasterCourseDesc('');
    setMasterDeliveryMode('Onsite');
    setMasterFee('RM 2,100');
    setMasterPaymentLink('https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
    setMasterPaymentSubject('{CourseName} Course Payment');
    setMasterPaymentBody(DEFAULT_ONSITE_PAYMENT_BODY);
    setMasterAwardedTemplates(['MCMC']);
    setIsMasterCourseModalOpen(true);
  };

  const handleOpenEditMasterCourseModal = (course) => {
    fetchCertificateTemplates();
    setEditingMasterCourse(course);
    setMasterCertBody(course.CertificationBody || 'ISC2');
    setMasterCourseTitle(course.CourseName || '');
    setMasterLevel(course.Level || 'Entry');
    setMasterCourseDesc(course.Description || '');
    setMasterDeliveryMode(course.DeliveryMode || 'Onsite');
    setMasterFee(course.Fee || 'RM 2,100');
    setMasterPaymentLink(course.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
    setMasterPaymentSubject(course.PaymentEmailSubject || '{CourseName} Course Payment');
    setMasterPaymentBody(course.PaymentEmailTemplate || (course.DeliveryMode === 'Online' ? DEFAULT_ONLINE_PAYMENT_BODY : DEFAULT_ONSITE_PAYMENT_BODY));

    let initialTemplates = ['MCMC'];
    if (course.awardedCertificateTemplates && Array.isArray(course.awardedCertificateTemplates)) {
      initialTemplates = course.awardedCertificateTemplates;
    } else {
      const cb = (course.CertificationBody || '').toLowerCase();
      const cn = (course.CourseName || '').toLowerCase();
      if (cb.includes('rcc') || cb.includes('rogers') || cb.includes('catalyst') || cn.includes('rcc') || cn.includes('rogers')) {
        initialTemplates = ['MCMC', 'RCC'];
      }
    }
    setMasterAwardedTemplates(initialTemplates);
    setIsMasterCourseModalOpen(true);
  };

  const handleMasterDeliveryModeChange = (mode) => {
    setMasterDeliveryMode(mode);
    if (mode === 'Online') {
      if (masterPaymentBody === DEFAULT_ONSITE_PAYMENT_BODY || !masterPaymentBody.trim()) {
        setMasterPaymentBody(DEFAULT_ONLINE_PAYMENT_BODY);
      }
    } else {
      if (masterPaymentBody === DEFAULT_ONLINE_PAYMENT_BODY || !masterPaymentBody.trim()) {
        setMasterPaymentBody(DEFAULT_ONSITE_PAYMENT_BODY);
      }
    }
  };

  const handleSaveMasterCourse = async (e) => {
    e.preventDefault();
    if (!masterCertBody.trim() || !masterCourseTitle.trim()) {
      setToast({ type: 'error', msg: 'Please fill in Certification Body and Course Title.' });
      return;
    }
    setSavingMasterCourse(true);
    try {
      const res = await fetch('/api/admin/courses/master', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId: editingMasterCourse ? editingMasterCourse.CourseID : null,
          certificationBody: masterCertBody.trim(),
          courseName: masterCourseTitle.trim(),
          level: masterLevel,
          description: masterCourseDesc.trim(),
          deliveryMode: masterDeliveryMode,
          fee: masterFee.trim(),
          paymentLink: masterPaymentLink.trim(),
          paymentEmailSubject: masterPaymentSubject.trim(),
          paymentEmailTemplate: masterPaymentBody.trim(),
          awardedCertificateTemplates: masterAwardedTemplates
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || (editingMasterCourse ? 'Course updated successfully!' : 'Master course created successfully!') });
        setIsMasterCourseModalOpen(false);
        setEditingMasterCourse(null);
        fetchMasterCourses();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to save master course.' });
      }
    } catch (err) {
      console.error('Error saving master course:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setSavingMasterCourse(false);
    }
  };

  const handleOpenCreateSessionModal = (initialCourse = null) => {
    fetchMasterCourses();
    fetchTrainers();

    let prefillCourseId = '';
    let prefillCourseName = '';
    let prefillMode = 'Onsite';
    let prefillFee = 'RM 2,100';
    let prefillLink = 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i';
    let prefillSubject = '{CourseName} Course Payment';
    let prefillBody = DEFAULT_ONSITE_PAYMENT_BODY;

    if (initialCourse) {
      prefillCourseId = String(initialCourse.courseId || initialCourse.CourseID || '');
      prefillCourseName = initialCourse.course || initialCourse.CourseName || '';
      prefillMode = initialCourse.deliveryMode || initialCourse.DeliveryMode || 'Onsite';
      prefillFee = initialCourse.fee || initialCourse.Fee || 'RM 2,100';
      prefillLink = initialCourse.paymentLink || initialCourse.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i';
      prefillSubject = initialCourse.paymentEmailSubject || '{CourseName} Course Payment';
      prefillBody = initialCourse.paymentEmailBody || (prefillMode === 'Online' ? DEFAULT_ONLINE_PAYMENT_BODY : DEFAULT_ONSITE_PAYMENT_BODY);
    }

    setSelectedCourseId(prefillCourseId);
    setCourseSearchTerm(prefillCourseName);
    setIsCourseDropdownOpen(false);
    setSessionStartDate('');
    setSessionEndDate('');
    setSessionStartTime('09:00 AM');
    setSessionEndTime('05:00 PM');
    setSessionSegments([
      { startDate: '', endDate: '', startTime: '09:00 AM', endTime: '05:00 PM' }
    ]);
    setSessionTrainerId('');
    setSessionTrainerSearchTerm('');
    setIsSessionTrainerDropdownOpen(false);
    setSessionMaxCap(10);
    setSessionFee(prefillFee);
    setSessionPaymentLink(prefillLink);
    setSessionPaymentSubject(prefillSubject);
    setSessionPaymentBody(prefillBody);
    setSessionDeliveryMode(prefillMode);
    setIsCreateSessionModalOpen(true);
  };

  const handleSegmentChange = (index, field, value, isEditModal = false) => {
    if (isEditModal) {
      setEditSegments(prev => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    } else {
      setSessionSegments(prev => {
        const next = [...prev];
        next[index] = { ...next[index], [field]: value };
        return next;
      });
    }
  };

  const handleAddSegment = (isEditModal = false) => {
    const newSeg = { startDate: '', endDate: '', startTime: '09:00 AM', endTime: '05:00 PM' };
    if (isEditModal) {
      setEditSegments(prev => [...prev, newSeg]);
    } else {
      setSessionSegments(prev => [...prev, newSeg]);
    }
  };

  const handleRemoveSegment = (index, isEditModal = false) => {
    if (isEditModal) {
      setEditSegments(prev => prev.filter((_, i) => i !== index));
    } else {
      setSessionSegments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSaveSession = async (e) => {
    e.preventDefault();
    if (!selectedCourseId) {
      setToast({ type: 'error', msg: 'Please select a master course from the dropdown.' });
      return;
    }

    // Validate all segments
    for (let i = 0; i < sessionSegments.length; i++) {
      const seg = sessionSegments[i];
      const partLabel = sessionSegments.length > 1 ? `Part ${i + 1}` : 'the session';
      if (!seg.startDate || !seg.endDate) {
        setToast({ type: 'error', msg: `Please select both Start Date and End Date for ${partLabel}.` });
        return;
      }
      if (new Date(seg.endDate) < new Date(seg.startDate)) {
        setToast({ type: 'error', msg: `End Date cannot be earlier than Start Date in ${partLabel}.` });
        return;
      }
    }

    let finalTrainerId = sessionTrainerId ? parseInt(sessionTrainerId, 10) : null;
    if (sessionTrainerSearchTerm.trim() && !finalTrainerId) {
      const clean = sessionTrainerSearchTerm.trim().toLowerCase();
      const match = trainersList.find(t => (t.FullName || '').toLowerCase() === clean);
      if (match) {
        finalTrainerId = match.TrainerID;
      } else if (clean !== 'tba' && !clean.includes('no trainer')) {
        setToast({ type: 'error', msg: 'Please select a valid trainer from the dropdown before saving.' });
        return;
      }
    }

    const firstSeg = sessionSegments[0];
    const lastSeg = sessionSegments[sessionSegments.length - 1];

    setSavingSession(true);
    try {
      const res = await fetch('/api/admin/schedules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseId: parseInt(selectedCourseId, 10),
          trainerId: finalTrainerId,
          startDate: firstSeg.startDate,
          endDate: lastSeg.endDate,
          startTime: firstSeg.startTime || '09:00 AM',
          endTime: firstSeg.endTime || '05:00 PM',
          sessionSegments: sessionSegments.length > 1 ? sessionSegments : null,
          maxCapacity: parseInt(sessionMaxCap, 10) || 10,
          deliveryMode: sessionDeliveryMode,
          fee: sessionFee,
          paymentLink: sessionPaymentLink,
          paymentEmailSubject: sessionPaymentSubject,
          paymentEmailBody: sessionPaymentBody
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || 'Class scheduled successfully!' });
        setIsCreateSessionModalOpen(false);
        fetchUpcomingCourses();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to schedule class.' });
      }
    } catch (err) {
      console.error('Error scheduling session:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setSavingSession(false);
    }
  };

  const handleOpenCreateCourseModal = () => {
    handleOpenCreateMasterCourseModal();
  };

  const handleOpenEditCourseModal = (s) => {
    setEditingSchedule(s);
    setCertBody(s.certificationBody || 'ISC2');
    setCourseTitle(s.courseName || '');
    setCourseType(s.type || 'Training');
    setCourseDesc(s.description || '');
    setSelectedTrainerId(s.trainerId ? String(s.trainerId) : '');
    const currentTrainer = trainersList.find(t => String(t.TrainerID) === String(s.trainerId));
    setTrainerSearchTerm(currentTrainer ? currentTrainer.FullName : (s.trainer && s.trainer !== 'TBA' ? s.trainer : ''));
    setIsTrainerDropdownOpen(false);
    fetchTrainers();
    setStartDate(s.startDate || '');
    setEndDate(s.endDate || '');
    setStartTime(s.startTime || '09:00 AM');
    setEndTime(s.endTime || '05:00 PM');

    let initialSegments = [{ startDate: s.startDate || s.StartDate || '', endDate: s.endDate || s.EndDate || '', startTime: s.startTime || s.StartTime || '09:00 AM', endTime: s.endTime || s.EndTime || '05:00 PM' }];
    const rawSegments = s.sessionSegments || s.SessionSegments;
    if (rawSegments && Array.isArray(rawSegments) && rawSegments.length > 0) {
      initialSegments = rawSegments.map(seg => ({
        startDate: seg.startDate || '',
        endDate: seg.endDate || '',
        startTime: seg.startTime || s.startTime || s.StartTime || '09:00 AM',
        endTime: seg.endTime || s.endTime || s.EndTime || '05:00 PM'
      }));
    } else if (rawSegments && typeof rawSegments === 'string') {
      try {
        const parsed = JSON.parse(rawSegments);
        if (Array.isArray(parsed) && parsed.length > 0) {
          initialSegments = parsed.map(seg => ({
            startDate: seg.startDate || '',
            endDate: seg.endDate || '',
            startTime: seg.startTime || s.startTime || s.StartTime || '09:00 AM',
            endTime: seg.endTime || s.endTime || s.EndTime || '05:00 PM'
          }));
        }
      } catch (e) {}
    }
    setEditSegments(initialSegments);

    setMaxCap(s.maxCapacity || 10);
    setFee(s.fee || 'RM 2,100');
    setPaymentLink(s.paymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i');
    setPaymentSubject(s.paymentEmailSubject || '{CourseName} Course Payment');
    setPaymentBody(s.paymentEmailBody || DEFAULT_PAYMENT_BODY);
    setIsCourseModalOpen(true);
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    if (!certBody || !courseTitle) {
      setToast({ type: 'error', msg: 'Please fill in Certification Body and Course Title.' });
      return;
    }

    // Validate all edit segments
    for (let i = 0; i < editSegments.length; i++) {
      const seg = editSegments[i];
      const partLabel = editSegments.length > 1 ? `Part ${i + 1}` : 'the session';
      if (!seg.startDate || !seg.endDate) {
        setToast({ type: 'error', msg: `Please select both Start Date and End Date for ${partLabel}.` });
        return;
      }
      if (new Date(seg.endDate) < new Date(seg.startDate)) {
        setToast({ type: 'error', msg: `End Date cannot be earlier than Start Date in ${partLabel}.` });
        return;
      }
    }

    let finalTrainerId = selectedTrainerId ? parseInt(selectedTrainerId, 10) : null;
    if (trainerSearchTerm.trim() && !finalTrainerId) {
      const clean = trainerSearchTerm.trim().toLowerCase();
      const match = trainersList.find(t => (t.FullName || '').toLowerCase() === clean);
      if (match) {
        finalTrainerId = match.TrainerID;
      } else if (clean !== 'tba' && !clean.includes('no trainer')) {
        setToast({ type: 'error', msg: 'Please select a valid trainer from the dropdown list before saving.' });
        return;
      }
    }

    const firstSeg = editSegments[0];
    const lastSeg = editSegments[editSegments.length - 1];

    setSavingCourse(true);
    try {
      const url = editingSchedule
        ? `/api/admin/schedules/${editingSchedule.id}`
        : '/api/admin/courses';
      const method = editingSchedule ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificationBody: certBody,
          courseName: courseTitle,
          type: courseType,
          description: courseDesc,
          trainerId: finalTrainerId,
          startDate: firstSeg.startDate,
          endDate: lastSeg.endDate,
          startTime: firstSeg.startTime || '09:00 AM',
          endTime: firstSeg.endTime || '05:00 PM',
          sessionSegments: editSegments.length > 1 ? editSegments : null,
          maxCapacity: parseInt(maxCap, 10) || 10,
          fee: fee,
          paymentLink: paymentLink,
          paymentEmailSubject: paymentSubject,
          paymentEmailBody: paymentBody
        })
      });

      const data = await res.json();
      if (data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setIsCourseModalOpen(false);
        fetchUpcomingCourses();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to save course.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Error saving course.' });
    } finally {
      setSavingCourse(false);
    }
  };

  const handleToggleScheduleStatus = async (scheduleId) => {
    try {
      const res = await fetch(`/api/admin/schedules/${scheduleId}/toggle-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        fetchUpcomingCourses();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to toggle session status.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  const handleUpdateScheduleCapacity = async (scheduleId, currentCap) => {
    const input = window.prompt("Enter maximum participant limit for this session:", currentCap);
    if (!input) return;
    const newCap = parseInt(input, 10);
    if (isNaN(newCap) || newCap < 1) {
      setToast({ type: 'error', msg: 'Please enter a valid participant limit (at least 1).' });
      return;
    }
    try {
      const res = await fetch(`/api/admin/schedules/${scheduleId}/capacity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxCapacity: newCap })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        fetchUpcomingCourses();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update capacity.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  // Open cancellation modal with customizable email template
  const handleOpenCancelModal = (s) => {
    setCancelModalSchedule(s);
    const courseName = s.course || s.courseName || 'Course';
    const dates = s.dates || 'Scheduled Dates';
    const time = (s.startTime && s.endTime) ? ` (${s.startTime} - ${s.endTime})` : '';

    setCancelEmailSubject(`Class Cancelled - ${courseName}`);

    const defaultBody = `Dear {LearnerName},

We regret to inform you that your upcoming training class for ${courseName} originally scheduled on ${dates}${time} has been cancelled by the administrator due to [Reason / unforeseen circumstances].

We will reallocate the class to DD/MM/YYYY - DD/MM/YYYY (09:00 AM - 05:00 PM). If you are available to attend the reallocated class, please log in to your portal to register for the updated schedule. Alternatively, you may browse our course catalog to select another suitable training date.

If you have already made payment or have any questions regarding your registration, please contact our training support team.

We apologize for any inconvenience caused and appreciate your understanding.

Regards,
Ezzah Khan (she/her)
Education Programs Registrar, CCoE, Malaysia
Office: +601 73870241
CCoE Website

BlackBerry® Intelligent Security. Everywhere.`;

    setCancelEmailBody(defaultBody);
  };

  // Submit course cancellation and send emails
  const handleConfirmCancelSchedule = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!cancelModalSchedule) return;

    setIsCancellingSchedule(true);
    try {
      const res = await fetch(`/api/admin/schedules/${cancelModalSchedule.id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailSubject: cancelEmailSubject,
          emailBody: cancelEmailBody,
          actor: user?.FullName || user?.username || 'Admin',
          actorRole: user?.role || user?.Role || 'Admin'
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || 'Class cancelled and notification emails sent.' });
        setCancelModalSchedule(null);
        fetchUpcomingCourses();
        fetchStats();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to cancel class.' });
      }
    } catch (err) {
      console.error('Error cancelling course session:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsCancellingSchedule(false);
    }
  };

  const [stats, setStats] = useState({
    pendingCount: 0,
    approvedThisWeekCount: 0,
    approvedTotalCount: 0,
    withdrawnCount: 0,
    cancelledCount: 0
  });
  const [withdrawalLookaheadDays, setWithdrawalLookaheadDays] = useState(15);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    status: '',
    data: [],
    loading: false
  });
  const [modalSearch, setModalSearch] = useState('');
  const [modalSearchScope, setModalSearchScope] = useState('all'); // 'all', 'name', 'email', 'course', 'organisation'
  const [expandedCourses, setExpandedCourses] = useState({});
  const [modalTimeframeTab, setModalTimeframeTab] = useState('upcoming'); // 'upcoming' | 'past' | 'all'

  const toggleCourseExpand = (courseKey) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseKey]: !prev[courseKey]
    }));
  };

  const [refreshingStatus, setRefreshingStatus] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, summaryRes] = await Promise.all([
        fetch('/api/admin/registration-stats'),
        fetch('/api/admin/settings/public-summary').catch(() => null)
      ]);
      const data = await statsRes.json();
      if (data.status === 'success') {
        setStats(data.data);
      }
      if (summaryRes) {
        const summaryData = await summaryRes.json();
        if (summaryData.status === 'success' && summaryData.data?.withdrawalLookaheadDays) {
          setWithdrawalLookaheadDays(summaryData.data.withdrawalLookaheadDays);
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const handleRefreshStatus = async () => {
    setRefreshingStatus(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchPendingApprovals(),
        fetchUpcomingCourses()
      ]);
      setToast({ type: 'success', msg: 'Dashboard stats refreshed!' });
    } catch (err) {
      console.error('Error refreshing status:', err);
    } finally {
      setRefreshingStatus(false);
    }
  };

  const openDetailsModal = async (statusType, title) => {
    setModalSearch('');
    setModalSearchScope('all');
    setExpandedCourses({});
    setModalTimeframeTab('upcoming');
    setModalConfig({
      isOpen: true,
      title,
      status: statusType,
      data: [],
      loading: true
    });

    try {
      const res = await fetch(`/api/admin/registrations-by-status?status=${statusType}`);
      const data = await res.json();
      if (data.status === 'success') {
        setModalConfig(prev => ({ ...prev, data: data.data, loading: false }));
      } else {
        setModalConfig(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching details:', err);
      setModalConfig(prev => ({ ...prev, loading: false }));
    }
  };

  const closeModal = () => {
    setModalConfig({ isOpen: false, title: '', status: '', data: [], loading: false });
    setModalSearch('');
    setModalSearchScope('all');
    setExpandedCourses({});
    setModalTimeframeTab('upcoming');
  };

  const [accountList, setAccountList] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [newAccountData, setNewAccountData] = useState({
    fullName: '',
    email: '',
    username: '',
    password: '',
    role: 'Admin'
  });
  const [createError, setCreateError] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);

  const generateAutoPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let autoPw = "";
    for (let i = 0; i < 12; i++) {
      autoPw += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewAccountData(prev => ({ ...prev, password: autoPw }));
    setShowTempPassword(true);
  };

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const res = await fetch(`/api/admin/accounts?t=${Date.now()}`);
      const data = await res.json();
      if (data.status === 'success') {
        setAccountList(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setCreateError('');
    setIsCreatingAccount(true);

    try {
      const res = await fetch('/api/admin/accounts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAccountData, creatorRole: user?.role || user?.Role || 'Admin' })
      });
      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setShowCreateAccountModal(false);
        setNewAccountData({ fullName: '', email: '', username: '', password: '', role: isEditor ? 'Editor' : 'Admin' });
        fetchAccounts();
      } else {
        setCreateError(data.message || 'Failed to create account.');
      }
    } catch (err) {
      console.error(err);
      setCreateError('Failed to connect to server.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteAccount = async (accountType, id, name) => {
    if (!window.confirm(`Are you sure you want to delete account for "${name}"?`)) return;

    try {
      const res = await fetch(`/api/admin/accounts/${accountType}/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        fetchAccounts();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to delete account.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  // Participant Management States
  const [learnersList, setLearnersList] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(false);
  const [participantTab, setParticipantTab] = useState('learners');
  const [participantSearch, setParticipantSearch] = useState('');
  const [directorySearchScope, setDirectorySearchScope] = useState('all'); // 'all', 'name', 'email', 'username', 'organisation', 'role'
  const [participantSort, setParticipantSort] = useState('default'); // 'default' | 'most_active' | 'least_active'

  useEffect(() => {
    if (isEditor && participantTab === 'staff') {
      setParticipantTab('learners');
    }
  }, [isEditor, participantTab]);

  // Edit Learner Modal State
  const [editingLearner, setEditingLearner] = useState(null);
  const [editLearnerForm, setEditLearnerForm] = useState({
    fullName: '', username: '', email: '', contact: '', position: '', gender: 'Prefer not to say', itBackground: false, experienceYears: '', organisationName: '', sector: ''
  });
  const [adminOrgList, setAdminOrgList] = useState([]);
  const [adminOrgSearchTerm, setAdminOrgSearchTerm] = useState('');
  const [isAdminOrgDropdownOpen, setIsAdminOrgDropdownOpen] = useState(false);
  const adminOrgDropdownRef = useRef(null);

  // Custom styled dropdown states for Gender, Sector, and Experience
  const [isAdminGenderDropdownOpen, setIsAdminGenderDropdownOpen] = useState(false);
  const adminGenderDropdownRef = useRef(null);
  const [isAdminSectorDropdownOpen, setIsAdminSectorDropdownOpen] = useState(false);
  const adminSectorDropdownRef = useRef(null);
  const [isAdminExpDropdownOpen, setIsAdminExpDropdownOpen] = useState(false);
  const adminExpDropdownRef = useRef(null);

  // Fetch all organisations for auto-matching in Edit Learner Profile
  useEffect(() => {
    const loadOrganisations = async () => {
      try {
        const res = await fetch('/api/organisations/all', {
          headers: { 'Accept': 'application/json' }
        });
        const data = await res.json();
        if (data.status === 'success' && Array.isArray(data.data)) {
          setAdminOrgList(data.data);
          return;
        }

        // Fallback to individual sectors if /all is unavailable
        const [govRes, privRes, studRes] = await Promise.allSettled([
          fetch('/api/organisations/government', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
          fetch('/api/organisations/private', { headers: { 'Accept': 'application/json' } }).then(r => r.json()),
          fetch('/api/organisations/student', { headers: { 'Accept': 'application/json' } }).then(r => r.json())
        ]);

        const combined = [];
        if (govRes.status === 'fulfilled' && govRes.value?.status === 'success' && Array.isArray(govRes.value.data)) {
          govRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Government' }));
        }
        if (privRes.status === 'fulfilled' && privRes.value?.status === 'success' && Array.isArray(privRes.value.data)) {
          privRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Private' }));
        }
        if (studRes.status === 'fulfilled' && studRes.value?.status === 'success' && Array.isArray(studRes.value.data)) {
          studRes.value.data.forEach(o => combined.push({ ...o, Sector: o.Sector || 'Student' }));
        }

        if (combined.length > 0) {
          setAdminOrgList(combined);
        }
      } catch (err) {
        console.error('Error loading organisations:', err);
      }
    };
    loadOrganisations();
  }, []);

  // Close custom dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (adminOrgDropdownRef.current && !adminOrgDropdownRef.current.contains(e.target)) {
        setIsAdminOrgDropdownOpen(false);
      }
      if (adminGenderDropdownRef.current && !adminGenderDropdownRef.current.contains(e.target)) {
        setIsAdminGenderDropdownOpen(false);
      }
      if (adminSectorDropdownRef.current && !adminSectorDropdownRef.current.contains(e.target)) {
        setIsAdminSectorDropdownOpen(false);
      }
      if (adminExpDropdownRef.current && !adminExpDropdownRef.current.contains(e.target)) {
        setIsAdminExpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Account Recovery Modal State
  const [recoveryTarget, setRecoveryTarget] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);
  // Permanent Delete Modal State
  const [deleteTarget, setDeleteTarget] = useState(null); // { accountType, id, name }
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleOpenDeleteModal = (accountType, id, name) => {
    setDeleteTarget({ accountType, id, name });
  };

  const handleConfirmDeleteAccount = async () => {
    if (!deleteTarget) return;
    setIsDeletingAccount(true);
    try {
      const res = await fetch(`/api/admin/accounts/${deleteTarget.accountType.toLowerCase()}/${deleteTarget.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setDeleteTarget(null);
        fetchLearners();
        fetchTrainers();
        fetchAccounts();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to delete account.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Error connecting to server.' });
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const getDeactivationDaysRemaining = (deactivatedDateStr, username, email) => {
    const isLuna = (username && username.toLowerCase() === 'luna262') || (email && email.toLowerCase().includes('luna262'));
    if (isLuna) return 0;
    if (!deactivatedDateStr) return 365;
    const dDate = new Date(typeof deactivatedDateStr === 'string' && deactivatedDateStr.includes(' ') ? deactivatedDateStr.replace(' ', 'T') : deactivatedDateStr);
    if (isNaN(dDate.getTime())) return 365;
    const daysSinceDeactivated = Math.max(0, Math.floor((Date.now() - dDate.getTime()) / (1000 * 60 * 60 * 24)));
    return Math.max(0, 365 - daysSinceDeactivated);
  };

  const renderActiveStatusBadge = (record) => {
    // Check if account is suspended under blackout policy
    if (record.isSuspended || record.AccountStatus === 'Suspended') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '700',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <SvgIcon name="slash" size={11} color="#dc2626" />
            Suspended ({record.blackoutDaysRemaining || 60}d remaining)
          </span>
          {record.blackoutReason && (
            <span style={{ fontSize: '0.68rem', color: '#991b1b', marginTop: '2px', maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={record.blackoutReason}>
              {record.blackoutReason}
            </span>
          )}
        </div>
      );
    }

    // Check if account was reactivated by admin and hasn't logged in since
    if (record.ReactivatedDate) {
      const rDate = new Date(typeof record.ReactivatedDate === 'string' && record.ReactivatedDate.includes(' ') ? record.ReactivatedDate.replace(' ', 'T') : record.ReactivatedDate);
      const lDate = record.LastLoginDate ? new Date(typeof record.LastLoginDate === 'string' && record.LastLoginDate.includes(' ') ? record.LastLoginDate.replace(' ', 'T') : record.LastLoginDate) : null;
      if (!lDate || lDate < rDate) {
        const daysSinceReactivated = !isNaN(rDate.getTime()) ? Math.max(0, Math.floor((Date.now() - rDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
        return (
          <span style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Active ({daysSinceReactivated === 0 ? 'Reactivated today' : `Reactivated ${daysSinceReactivated}d ago`})
          </span>
        );
      }
    }

    if (record.daysInactive >= 365) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Active ({record.daysInactive === 0 ? 'today' : `${record.daysInactive}d ago`})
          </span>
          <span style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: '3px', fontWeight: '600' }}>
            Exceeds 365d — recommend deactivation
          </span>
        </div>
      );
    }

    return (
      <span style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        Active {record.daysInactive !== null && record.daysInactive !== undefined ? (record.daysInactive === 0 ? '(today)' : `(${record.daysInactive}d ago)`) : '(No login recorded)'}
      </span>
    );
  };

  const fetchLearners = useCallback(async () => {
    setLoadingLearners(true);
    try {
      const res = await fetch(`/api/admin/learners?t=${Date.now()}`);
      const data = await res.json();
      if (data.status === 'success') {
        setLearnersList(data.data);
      }
    } catch (err) {
      console.error('Error fetching learners:', err);
    } finally {
      setLoadingLearners(false);
    }
  }, []);

  const handleOpenEditLearner = (l) => {
    setEditingLearner(l);
    setEditLearnerForm({
      fullName: l.FullName || '',
      username: l.Username || '',
      email: l.Email || '',
      contact: l.Contact || '',
      position: l.Position || '',
      gender: l.Gender || 'Prefer not to say',
      itBackground: !!l.ITBackground,
      experienceYears: l.ExperienceYears || 'Not Applicable',
      organisationName: l.OrganisationName || '',
      sector: l.Sector || ''
    });
    setAdminOrgSearchTerm(l.OrganisationName || '');
    setIsAdminOrgDropdownOpen(false);
  };

  const handleSaveLearnerProfile = async (e) => {
    e.preventDefault();
    if (!editingLearner) return;
    try {
      const res = await fetch(`/api/admin/learners/${editingLearner.LearnerID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editLearnerForm)
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setEditingLearner(null);
        fetchLearners();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update learner profile.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  // Delete Target Modal State
  const [deletingTarget, setDeletingTarget] = useState(null);

  // Learner Suspension (2-Month Blackout) Modal State
  const [suspensionModal, setSuspensionModal] = useState(null);

  const handleExecuteToggleSuspension = async () => {
    if (!suspensionModal?.learner) return;
    setSuspensionModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`/api/admin/learners/${suspensionModal.learner.LearnerID}/toggle-suspension`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-name': user?.name || 'Administrator',
          'x-admin-role': user?.role || 'Admin',
          'x-admin-id': user?.id || ''
        },
        body: JSON.stringify({ reason: suspensionModal.reason || 'Repeated no attendance' })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setSuspensionModal(null);
        fetchLearners();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update suspension status.' });
        setSuspensionModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
      setSuspensionModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingTarget) return;
    const endpoint = deletingTarget.role === 'Trainer'
      ? `/api/admin/trainers/${deletingTarget.id}`
      : `/api/admin/learners/${deletingTarget.id}`;

    try {
      const res = await fetch(endpoint, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setDeletingTarget(null);
        if (deletingTarget.role === 'Trainer') fetchTrainers();
        else fetchLearners();
      } else {
        setToast({ type: 'error', msg: data.message || `Failed to delete ${deletingTarget.role.toLowerCase()}.` });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  const handleExecuteAccountRecovery = async () => {
    if (!recoveryTarget) return;
    setIsRecovering(true);
    try {
      const endpoint = recoveryTarget.role === 'Trainer'
        ? `/api/admin/trainers/${recoveryTarget.id}/reset-password`
        : `/api/admin/learners/${recoveryTarget.id}/reset-password`;

      const res = await fetch(endpoint, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: 'Account recovery email sent successfully!' });
        setRecoveryTarget(null);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to execute account recovery.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsRecovering(false);
    }
  };

  // Account Status Toggle Modal State
  const [statusToggleTarget, setStatusToggleTarget] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const handleOpenStatusToggleModal = (role, id, currentIsActive, daysInactive, name) => {
    setStatusToggleTarget({ role, id, currentIsActive, daysInactive, name });
  };

  const handleConfirmStatusToggle = async () => {
    if (!statusToggleTarget) return;
    setIsUpdatingStatus(true);
    const { role, id, currentIsActive, daysInactive } = statusToggleTarget;
    const isDeactivating = currentIsActive !== 0;

    try {
      const res = await fetch(`/api/admin/users/${role.toLowerCase()}/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isActive: isDeactivating ? 0 : 1,
          daysInactive: daysInactive || 365,
          adminName: user?.name || user?.fullName || 'System Administrator',
          adminEmail: user?.email || 'admin@blackberry-ccoe.local'
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setStatusToggleTarget(null);
        fetchAccounts();
        fetchLearners();
        fetchTrainers();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update account status.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Trainers State
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [editTrainerForm, setEditTrainerForm] = useState({
    fullName: '', username: '', email: '', bio: '', certifications: ''
  });

  const fetchTrainers = useCallback(async () => {
    try {
      const res = await fetch(`/api/admin/trainers?t=${Date.now()}`);
      const data = await res.json();
      if (data.status === 'success') {
        setTrainersList(data.data);
      }
    } catch (err) {
      console.error('Error fetching trainers:', err);
    }
  }, []);

  const handleOpenEditTrainer = (t) => {
    setEditingTrainer(t);
    setEditTrainerForm({
      fullName: t.FullName || '',
      username: t.Username || '',
      email: t.Email || '',
      bio: t.Bio || '',
      certifications: t.Certifications || ''
    });
  };

  const handleSaveTrainerProfile = async (e) => {
    e.preventDefault();
    if (!editingTrainer) return;
    try {
      const res = await fetch(`/api/admin/trainers/${editingTrainer.TrainerID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editTrainerForm)
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message });
        setEditingTrainer(null);
        fetchTrainers();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update trainer profile.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  const getCleanNameForSort = (name) => {
    if (!name) return '';
    return name
      .replace(/^(mr\.|ms\.|mrs\.|dr\.|prof\.|miss|encik|puan|cik)\s+/i, '')
      .trim()
      .toLowerCase();
  };

  const processParticipantList = useCallback((list, search, sortOption, searchFn) => {
    let result = list.filter(item => searchFn(item, search));

    if (sortOption === 'most_active') {
      result = [...result].sort((a, b) => {
        const aActive = a.IsActive !== 0 ? 1 : 0;
        const bActive = b.IsActive !== 0 ? 1 : 0;
        if (aActive !== bActive) return bActive - aActive;

        const aDays = (a.daysInactive !== null && a.daysInactive !== undefined) ? a.daysInactive : 999999;
        const bDays = (b.daysInactive !== null && b.daysInactive !== undefined) ? b.daysInactive : 999999;
        if (aDays !== bDays) return aDays - bDays;
        return getCleanNameForSort(a.FullName).localeCompare(getCleanNameForSort(b.FullName), undefined, { sensitivity: 'base' });
      });
    } else if (sortOption === 'least_active') {
      result = [...result].sort((a, b) => {
        const aActive = a.IsActive !== 0 ? 1 : 0;
        const bActive = b.IsActive !== 0 ? 1 : 0;
        if (aActive !== bActive) return aActive - bActive;

        const aDays = (a.daysInactive !== null && a.daysInactive !== undefined) ? a.daysInactive : -1;
        const bDays = (b.daysInactive !== null && b.daysInactive !== undefined) ? b.daysInactive : -1;
        if (aDays !== bDays) return bDays - aDays;
        return getCleanNameForSort(a.FullName).localeCompare(getCleanNameForSort(b.FullName), undefined, { sensitivity: 'base' });
      });
    } else {
      // Default sorting: Alphabetical by clean FullName (A to Z), ignoring salutations (Mr./Ms./Dr.)
      result = [...result].sort((a, b) => {
        const nameA = getCleanNameForSort(a.FullName);
        const nameB = getCleanNameForSort(b.FullName);
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      });
    }

    return result;
  }, []);

  const filteredLearners = processParticipantList(
    learnersList,
    participantTab === 'learners' ? participantSearch : '',
    participantSort,
    (l, search) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      const nameMatch = Boolean(l.FullName && l.FullName.toLowerCase().includes(q));
      const emailMatch = Boolean(l.Email && l.Email.toLowerCase().includes(q));
      const usernameMatch = Boolean(l.Username && l.Username.toLowerCase().includes(q));
      const positionMatch = Boolean(l.Position && l.Position.toLowerCase().includes(q));
      const orgMatch = Boolean(
        (l.OrganisationName && l.OrganisationName.toLowerCase().includes(q)) ||
        (l.Sector && l.Sector.toLowerCase().includes(q))
      );

      if (directorySearchScope === 'name') return nameMatch;
      if (directorySearchScope === 'email') return emailMatch;
      if (directorySearchScope === 'username') return usernameMatch;
      if (directorySearchScope === 'organisation') return orgMatch;
      return nameMatch || emailMatch || usernameMatch || positionMatch || orgMatch;
    }
  );

  const filteredTrainers = processParticipantList(
    trainersList,
    participantTab === 'trainers' ? participantSearch : '',
    participantSort,
    (t, search) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      const nameMatch = Boolean(t.FullName && t.FullName.toLowerCase().includes(q));
      const emailMatch = Boolean(t.Email && t.Email.toLowerCase().includes(q));
      const usernameMatch = Boolean(t.Username && t.Username.toLowerCase().includes(q));

      if (directorySearchScope === 'name') return nameMatch;
      if (directorySearchScope === 'email') return emailMatch;
      if (directorySearchScope === 'username') return usernameMatch;
      return nameMatch || emailMatch || usernameMatch;
    }
  );

  const filteredAdmins = processParticipantList(
    accountList.filter(a => isEditor ? (a.Role === 'Editor' || a.Role === 'Report Viewer') : (a.Role === 'Admin' || a.Role === 'Editor' || a.Role === 'Report Viewer')),
    participantTab === 'staff' ? participantSearch : '',
    participantSort,
    (a, search) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      const nameMatch = Boolean(a.FullName && a.FullName.toLowerCase().includes(q));
      const emailMatch = Boolean(a.Email && a.Email.toLowerCase().includes(q));
      const usernameMatch = Boolean(a.Username && a.Username.toLowerCase().includes(q));
      const roleMatch = Boolean(a.Role && a.Role.toLowerCase().includes(q));

      if (directorySearchScope === 'name') return nameMatch;
      if (directorySearchScope === 'email') return emailMatch;
      if (directorySearchScope === 'username') return usernameMatch;
      if (directorySearchScope === 'role') return roleMatch;
      return nameMatch || emailMatch || usernameMatch || roleMatch;
    }
  );

  useEffect(() => {
    // Always fetch latest account, learner, and trainer statuses on load or tab switch
    fetchAccounts();
    fetchLearners();
    fetchTrainers();
    fetchMasterCourses();
    if (activeView === 'adminApprovals' || activeView === 'adminSessionRegistration' || activeView === 'adminDashboard') {
      fetchPendingApprovals();
    }
    if (activeView === 'adminDashboard' || activeView === 'adminCourses') {
      fetchUpcomingCourses();
      fetchStats();
    }
  }, [activeView, participantTab, fetchPendingApprovals, fetchUpcomingCourses, fetchStats, fetchAccounts, fetchLearners, fetchTrainers, fetchMasterCourses]);

  const handleUpdateStatus = async (registrationId, status, overrideReason = null) => {
    // If approving a registration that is a re-enrollment requiring override, prompt for override note if not provided
    const targetReg = pendingList.find(r => r.RegistrationID === registrationId);
    let finalOverride = overrideReason;

    if (status === 'Approved' && targetReg?.IsReEnrollment && targetReg?.RequiresOverride && !finalOverride) {
      const prevAtt = targetReg.PreviousAttendanceRate !== null ? `${targetReg.PreviousAttendanceRate}%` : 'incomplete';
      const promptRes = window.prompt(
        `Learner previously attended this course with ${prevAtt} attendance.\n\nPlease enter an administrative approval rationale / override justification:`,
        'Re-enrollment approved by administrator after attendance review.'
      );
      if (promptRes === null) return; // Cancelled by admin
      finalOverride = promptRes.trim() || 'Re-enrollment approved by administrator after review.';
    }

    try {
      const res = await fetch(`/api/admin/registrations/${registrationId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          overrideReason: finalOverride,
          adminName: user?.FullName || user?.fullName || 'Administrator',
          adminRole: user?.role || user?.Role || 'Admin'
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({
          type: 'success',
          msg: finalOverride
            ? `Registration approved with administrative override recorded.`
            : `Registration successfully ${status === 'Approved' ? 'Approved' : status === 'Rejected' ? 'Rejected' : 'updated to Pending Payment'}.`
        });
        fetchPendingApprovals();
        fetchStats();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to update registration status.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    }
  };

  const handleSendAdminReminderDigest = async () => {
    setSendingAdminDigest(true);
    try {
      const res = await fetch('/api/admin/send-admin-reminder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: data.message || 'Admin reminder alert email dispatched successfully.' });
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to dispatch admin reminder alert.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setSendingAdminDigest(false);
    }
  };

  const handleOpenPaymentEmailPreview = (reg, isResend = false) => {
    const fullCourseName = `${reg.CertificationBody ? `${reg.CertificationBody} ` : ''}${reg.CourseName || ''}`.trim();
    const learnerName = reg.LearnerName || 'Learner';
    const fee = reg.Fee || 'RM 2,100';
    const paymentLink = reg.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i';
    const venue = reg.CourseType === 'Online' ? 'Online' : 'BlackBerry Cybersecurity Center of Excellence (CCoE) Sdn Bhd, Jalan Impact, Cyber 6, 63000 Cyberjaya, Selangor.';

    let defaultSubject = reg.PaymentEmailSubject || '{CourseName} Course Payment';
    defaultSubject = defaultSubject
      .replace(/\{CourseName\}/g, fullCourseName)
      .replace(/\{LearnerName\}/g, learnerName);

    let defaultBody = reg.PaymentEmailBody || DEFAULT_PAYMENT_BODY;
    defaultBody = defaultBody
      .replace(/\{LearnerName\}/g, learnerName)
      .replace(/\{CourseName\}/g, fullCourseName)
      .replace(/\{Fee\}/g, fee)
      .replace(/\{PaymentLink\}/g, paymentLink)
      .replace(/\{Venue\}/g, venue);

    setPaymentEmailModal({
      registrationId: reg.RegistrationID,
      learnerName: reg.LearnerName,
      learnerEmail: reg.LearnerEmail,
      courseName: fullCourseName,
      fee: fee,
      paymentLink: paymentLink,
      subject: defaultSubject,
      body: defaultBody,
      isResend: isResend
    });
  };

  const handleSendPaymentEmail = async () => {
    if (!paymentEmailModal) return;
    setIsSendingPaymentEmail(true);
    try {
      const res = await fetch(`/api/admin/registrations/${paymentEmailModal.registrationId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'Pending Payment',
          customSubject: paymentEmailModal.subject,
          customBody: paymentEmailModal.body
        })
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({
          type: 'success',
          msg: paymentEmailModal.isResend
            ? 'Payment request email resent successfully!'
            : 'Payment request email sent and registration status updated to Pending Payment.'
        });
        setPaymentEmailModal(null);
        fetchPendingApprovals();
        fetchStats();
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to send payment email.' });
      }
    } catch (err) {
      console.error(err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsSendingPaymentEmail(false);
    }
  };

  // Mock upcoming courses are replaced by database schedules

  const triggerAction = (actionName) => {
    if (actionName === 'Add New Course') {
      if (onSwitchView) onSwitchView('adminCourses');
      handleOpenCreateCourseModal();
      return;
    }
    if (actionName === 'Import Registrations') {
      if (onSwitchView) onSwitchView('adminRegistrationBulkImport');
      return;
    }
    if (actionName === 'Export Report') {
      setOpenExportModalOnReportView(true);
      if (onSwitchView) onSwitchView('adminReports');
      return;
    }
    setToast({
      type: 'info',
      msg: `"${actionName}" action is a placeholder for integration in future development phases.`
    });
  };

  return (
    <div className="profile-page-layout">
      {/* Sidebar Navigation */}
      <Sidebar activeView={activeView || "adminDashboard"} onSwitchView={onSwitchView} onLogout={onLogout} user={user} />

      {/* Main Content Area */}
      <div className="profile-main-content">

        {/* ── SCREEN: Audit Logs (Sprint 5) / Reports ── */}
        {isReportViewer ? (
          <ReportDashboard user={user} initialOpenExportModal={openExportModalOnReportView} />
        ) : activeView === 'adminAuditLogs' ? (
          <AdminAuditLogs user={user} />
        ) : activeView === 'adminSystemSettings' ? (
          <SystemSettings user={user} />
        ) : activeView === 'adminFeedback' ? (
          <AdminFeedbackManagement user={user} />
        ) : activeView === 'adminReports' ? (
          <ReportDashboard user={user} initialOpenExportModal={openExportModalOnReportView} />
        ) : activeView === 'adminCourseBulkImport' ? (
          <BulkCourseImport
            onBack={() => onSwitchView ? onSwitchView('adminCourses') : null}
            onImportSuccess={(result) => {
              setToast({
                type: 'success',
                msg: result.message || 'Courses imported successfully!'
              });
              if (onSwitchView) onSwitchView('adminCourses');
              fetchUpcomingCourses();
            }}
            defaultPaymentBody={DEFAULT_PAYMENT_BODY}
          />
        ) : activeView === 'adminRegistrationBulkImport' ? (
          <BulkRegistrationImport
            onBack={() => onSwitchView ? onSwitchView('adminApprovals') : null}
            onImportSuccess={(result) => {
              setToast({
                type: 'success',
                msg: result.message || 'Registrations imported successfully!'
              });
              fetchPendingApprovals();
            }}
            user={user}
          />
        ) : activeView === 'adminTags' ? (
          <ProjectTagAllocationHub
            onBack={() => onSwitchView ? onSwitchView('adminCourses') : null}
            onSwitchView={onSwitchView}
          />
        ) : activeView === 'adminCourses' ? (
          <>
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="book" size={22} />
                <h1 className="admin-dashboard-title">Courses & Classes</h1>
              </div>
              <div className="profile-top-bar-right admin-header-actions">
                <button
                  className="admin-header-action-btn admin-btn-dark"
                  onClick={() => onSwitchView ? onSwitchView('adminTags') : null}
                  data-testid="nav-to-tag-allocation-btn"
                >
                  <SvgIcon name="tag" size={15} />
                  <span>Project Tag Allocation</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-blue"
                  onClick={() => onSwitchView ? onSwitchView('adminCourseBulkImport') : null}
                  data-testid="import-bulk-courses-btn"
                >
                  <SvgIcon name="calendar" size={15} />
                  <span>Import Bulk Classes (.ics)</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">
              {/* Tab Navigation: Courses Created vs Classes */}
              <div className="admin-tab-group" style={{ marginBottom: '1.25rem' }}>
                <button
                  type="button"
                  className={`admin-tab-btn ${coursesActiveTab === 'courses' ? 'active' : ''}`}
                  onClick={() => setCoursesActiveTab('courses')}
                >
                  <SvgIcon name="book" size={16} />
                  <span>Courses</span>
                  <span className="admin-tab-badge">{masterCoursesList.length}</span>
                </button>
                <button
                  type="button"
                  className={`admin-tab-btn ${coursesActiveTab === 'classes' ? 'active' : ''}`}
                  onClick={() => setCoursesActiveTab('classes')}
                >
                  <SvgIcon name="calendar" size={16} />
                  <span>Classes</span>
                  <span className="admin-tab-badge">{allSchedules.length}</span>
                </button>
              </div>

              {coursesActiveTab === 'courses' ? (
                /* ── TAB 1: COURSES CREATED (Course Catalog without timing & session) ── */
                <div className="admin-approvals-card" style={{ marginTop: '0px' }}>
                  <div className="approvals-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <h3 className="approvals-card-title">Course Catalog</h3>
                      <p className="approvals-card-subtitle" style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                        All courses created in the catalog. Click "Schedule Class" on any course to schedule class dates, trainer, and capacity.
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        placeholder="Search courses, cert body, level, or mode..."
                        value={masterCourseSearch}
                        onChange={(e) => setMasterCourseSearch(e.target.value)}
                        className="course-search-input"
                        style={{
                          padding: '0.5rem 0.85rem',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.875rem',
                          width: '280px'
                        }}
                      />
                      <button
                        type="button"
                        className="admin-header-action-btn admin-btn-purple"
                        onClick={handleOpenCreateMasterCourseModal}
                        style={{ padding: '0.5rem 0.9rem', fontSize: '0.8rem' }}
                      >
                        <SvgIcon name="plus" size={14} />
                        <span>Create Course</span>
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const q = masterCourseSearch.toLowerCase().trim();
                    const filtered = masterCoursesList.filter(c => {
                      if (!q) return true;
                      return (
                        (c.CourseName && c.CourseName.toLowerCase().includes(q)) ||
                        (c.CertificationBody && c.CertificationBody.toLowerCase().includes(q)) ||
                        (c.Level && c.Level.toLowerCase().includes(q)) ||
                        (c.DeliveryMode && c.DeliveryMode.toLowerCase().includes(q)) ||
                        (c.Fee && c.Fee.toLowerCase().includes(q)) ||
                        (c.Description && c.Description.toLowerCase().includes(q)) ||
                        String(c.CourseID).includes(q)
                      );
                    });

                    if (loadingMasterCourses) {
                      return (
                        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
                          Loading created courses...
                        </div>
                      );
                    }

                    if (filtered.length === 0) {
                      return (
                        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                          <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600' }}>
                            {q
                              ? `No courses match "${masterCourseSearch}".`
                              : "No courses have been created yet."}
                          </p>
                          {q ? (
                            <button
                              type="button"
                              onClick={() => setMasterCourseSearch('')}
                              style={{
                                padding: '0.4rem 1rem',
                                backgroundColor: '#e0e7ff',
                                color: '#4338ca',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.825rem',
                                fontWeight: '700',
                                cursor: 'pointer'
                              }}
                            >
                              Clear Search
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={handleOpenCreateMasterCourseModal}
                              style={{
                                padding: '0.45rem 1.1rem',
                                backgroundColor: '#4f46e5',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.825rem',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem'
                              }}
                            >
                              <SvgIcon name="plus" size={14} /> Create First Course
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div className="admin-table-wrapper" style={{ margin: 0 }}>
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th style={{ width: '85px', textAlign: 'center' }}>Course ID</th>
                              <th>Certification & Course Name</th>
                              <th style={{ width: '130px' }}>Level</th>
                              <th style={{ width: '130px' }}>Delivery Mode</th>
                              <th style={{ width: '110px' }}>Fee</th>
                              <th>Overview / Description</th>
                              <th style={{ textAlign: 'right', width: '220px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filtered.map(c => {
                              const levelColor =
                                c.Level === 'Entry' ? { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' } :
                                c.Level === 'Intermediate' ? { bg: '#f0f9ff', text: '#0284c7', border: '#bae6fd' } :
                                c.Level === 'Advanced' ? { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' } :
                                { bg: '#fffbeb', text: '#d97706', border: '#fde68a' };

                              const isOnline = c.DeliveryMode === 'Online';
                              const cleanDesc = (c.Description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

                              return (
                                <tr key={c.CourseID}>
                                  <td style={{ textAlign: 'center' }}>
                                    <span style={{ fontWeight: '700', color: '#475569', fontSize: '0.85rem' }}>
                                      #{c.CourseID}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        <span style={{
                                          padding: '2px 7px',
                                          borderRadius: '4px',
                                          background: '#eef2ff',
                                          color: '#4338ca',
                                          fontWeight: '700',
                                          fontSize: '0.72rem',
                                          letterSpacing: '0.02em',
                                          border: '1px solid #c7d2fe'
                                        }}>
                                          {c.CertificationBody || 'General'}
                                        </span>
                                        <span className="course-name-bold" style={{ fontSize: '0.92rem' }}>
                                          {c.CourseName}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span style={{
                                      padding: '3px 8px',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: '700',
                                      background: levelColor.bg,
                                      color: levelColor.text,
                                      border: `1px solid ${levelColor.border}`,
                                      display: 'inline-block'
                                    }}>
                                      {c.Level || 'Entry'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{
                                      padding: '3px 8px',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600',
                                      background: isOnline ? '#eff6ff' : '#f8fafc',
                                      color: isOnline ? '#1d4ed8' : '#334155',
                                      border: `1px solid ${isOnline ? '#bfdbfe' : '#e2e8f0'}`,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}>
                                      <SvgIcon name={isOnline ? 'globe' : 'mapPin'} size={12} />
                                      {c.DeliveryMode || 'Onsite'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{ fontWeight: '700', color: '#166534', fontSize: '0.85rem' }}>
                                      {c.Fee || 'RM 2,100'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{
                                      fontSize: '0.8rem',
                                      color: '#64748b',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      lineHeight: '1.4'
                                    }} title={cleanDesc}>
                                      {cleanDesc || <em style={{ color: '#94a3b8' }}>No description provided.</em>}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', alignItems: 'center' }}>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenCreateSessionModal(c)}
                                        style={{
                                          padding: '0.35rem 0.75rem',
                                          borderRadius: '6px',
                                          border: '1px solid #059669',
                                          background: '#ecfdf5',
                                          color: '#047857',
                                          fontWeight: '700',
                                          fontSize: '0.75rem',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.35rem',
                                          transition: 'all 0.15s ease'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.background = '#d1fae5'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                                        title="Schedule a new class for this course"
                                      >
                                        <SvgIcon name="calendar" size={12} /> Schedule Class
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleOpenEditMasterCourseModal(c)}
                                        style={{
                                          padding: '0.35rem 0.65rem',
                                          borderRadius: '6px',
                                          border: '1px solid #cbd5e1',
                                          background: '#ffffff',
                                          color: '#334155',
                                          fontWeight: '600',
                                          fontSize: '0.75rem',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '3px'
                                        }}
                                        title="Edit Course Details"
                                      >
                                        <SvgIcon name="edit" size={12} /> Edit
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* ── TAB 2: CLASSES (Scheduled Classes & Capacity Management) ── */
                <div className="admin-approvals-card" style={{ marginTop: '0px' }}>
                  <div className="approvals-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #e2e8f0' }}>
                    <div>
                      <h3 className="approvals-card-title">Training Classes</h3>
                      <p className="approvals-card-subtitle" style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                        Manage participant capacity limits (default: 10), payment templates, and Open/Close registration classes.
                      </p>
                    </div>
                    <input
                      type="text"
                      placeholder="Search class, trainer or month..."
                      value={coursesSearch}
                      onChange={(e) => setCoursesSearch(e.target.value)}
                      className="course-search-input"
                      style={{
                        padding: '0.5rem 0.85rem',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.875rem',
                        width: '260px'
                      }}
                    />
                  </div>

                  {(() => {
                    const q = coursesSearch.toLowerCase().trim();

                    // Step 1: Group all schedules into months
                    const monthMap = {};
                    allSchedules.forEach(s => {
                      const monthInfo = getMonthInfo(s.startDate);
                      if (!monthMap[monthInfo.key]) {
                        monthMap[monthInfo.key] = {
                          info: monthInfo,
                          schedules: []
                        };
                      }
                      monthMap[monthInfo.key].schedules.push(s);
                    });

                    const now = new Date();
                    const currentYear = now.getFullYear();
                    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
                    const currentMonthKey = `${currentYear}-${currentMonth}`;

                    const upcomingOrCurrentKeys = Object.keys(monthMap)
                      .filter(k => k >= currentMonthKey && k !== '9999-99')
                      .sort((a, b) => a.localeCompare(b));

                    const pastKeys = Object.keys(monthMap)
                      .filter(k => k < currentMonthKey)
                      .sort((a, b) => b.localeCompare(a));

                    const unscheduledKeys = Object.keys(monthMap).filter(k => k === '9999-99');

                    const allSortedKeys = [...upcomingOrCurrentKeys, ...pastKeys, ...unscheduledKeys];

                    const structuredMonths = allSortedKeys.map(mKey => {
                      const mObj = monthMap[mKey];

                      // Sort schedules in month: Start date ASC, End date ASC, Course name ASC
                      const sorted = [...mObj.schedules].sort((a, b) => {
                        const startA = a.startDate ? new Date(a.startDate).getTime() : 0;
                        const startB = b.startDate ? new Date(b.startDate).getTime() : 0;
                        if (startA !== startB) return startA - startB;

                        const endA = a.endDate ? new Date(a.endDate).getTime() : 0;
                        const endB = b.endDate ? new Date(b.endDate).getTime() : 0;
                        if (endA !== endB) return endA - endB;

                        return a.course.localeCompare(b.course);
                      });

                      const filtered = sorted.filter(s => {
                        if (!q) return true;
                        return (
                          (s.courseId && String(s.courseId).toLowerCase().includes(q)) ||
                          (s.course && s.course.toLowerCase().includes(q)) ||
                          (s.trainer && s.trainer.toLowerCase().includes(q)) ||
                          (s.venue && s.venue.toLowerCase().includes(q)) ||
                          (s.dates && s.dates.toLowerCase().includes(q)) ||
                          (s.fee && s.fee.toLowerCase().includes(q)) ||
                          (mObj.info.fullLabel && mObj.info.fullLabel.toLowerCase().includes(q))
                        );
                      });

                      const totalEnrolled = filtered.reduce((acc, s) => acc + (s.enrolledCount || 0), 0);
                      const isUpcomingOrCurrent = mKey >= currentMonthKey && mKey !== '9999-99';
                      const isPast = mKey < currentMonthKey;

                      return {
                        key: mKey,
                        monthInfo: mObj.info,
                        schedules: filtered,
                        totalEnrolled,
                        isUpcomingOrCurrent,
                        isPast
                      };
                    }).filter(m => m.schedules.length > 0);

                    const totalUpcomingCount = structuredMonths
                      .filter(m => m.isUpcomingOrCurrent)
                      .reduce((sum, m) => sum + m.schedules.length, 0);

                    const totalPastCount = structuredMonths
                      .filter(m => m.isPast)
                      .reduce((sum, m) => sum + m.schedules.length, 0);

                    const visibleMonths = structuredMonths.filter(m => {
                      if (coursesTimeframeTab === 'past') return m.isPast;
                      return m.isUpcomingOrCurrent;
                    });

                    return (
                      <>
                        {/* Timeframe Filter Tabs */}
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0 1.5rem 0' }}>
                          <div className="modal-timeframe-tabs" style={{ margin: 0 }}>
                            <button
                              type="button"
                              className={`modal-timeframe-tab ${coursesTimeframeTab === 'upcoming' ? 'active' : ''}`}
                              onClick={() => setCoursesTimeframeTab('upcoming')}
                            >
                              Current & Upcoming ({totalUpcomingCount})
                            </button>
                            <button
                              type="button"
                              className={`modal-timeframe-tab ${coursesTimeframeTab === 'past' ? 'active' : ''}`}
                              onClick={() => setCoursesTimeframeTab('past')}
                            >
                              Past Classes ({totalPastCount})
                            </button>
                          </div>
                        </div>

                        {loadingCourses ? (
                          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
                            Loading classes...
                          </div>
                        ) : visibleMonths.length === 0 ? (
                          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                            <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600' }}>
                              {q
                                ? "No classes match your search criteria in this view."
                                : coursesTimeframeTab === 'upcoming'
                                  ? "No current or upcoming classes found."
                                  : "No past classes found."}
                            </p>
                            {q && (
                              <button
                                type="button"
                                onClick={() => setCoursesSearch('')}
                                style={{
                                  padding: '0.4rem 1rem',
                                  backgroundColor: '#e0e7ff',
                                  color: '#4338ca',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.825rem',
                                  fontWeight: '700',
                                  cursor: 'pointer'
                                }}
                              >
                                Clear Search
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {visibleMonths.map(m => (
                              <div key={m.key} className="modal-month-box">
                                {/* Month Box Header */}
                                <div className="modal-month-box-header">
                                  <div className="modal-month-header-left">
                                    <span className="modal-month-pill">
                                      <SvgIcon name="calendar" size={14} />
                                      <span>{m.monthInfo.shortMonth}</span>
                                    </span>
                                    <span className="modal-month-fullname">{m.monthInfo.fullLabel}</span>
                                  </div>
                                  <div className="modal-month-header-right">
                                    <span className="modal-month-summary-badge">
                                      {m.schedules.length} {m.schedules.length === 1 ? 'Class' : 'Classes'} • {m.totalEnrolled} {m.totalEnrolled === 1 ? 'Registration' : 'Registrations'}
                                    </span>
                                  </div>
                                </div>

                                {/* Direct Table Content within the Month */}
                                <div className="admin-table-wrapper" style={{ margin: 0, borderTop: 'none', borderBottomLeftRadius: '14px', borderBottomRightRadius: '14px', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                                  <table className="admin-table" style={{ margin: 0 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ width: '90px', textAlign: 'center' }}>Class ID</th>
                                        <th>Class Name</th>
                                        <th>Trainer</th>
                                        <th>Dates</th>
                                        <th>Venue</th>
                                        <th>Fee</th>
                                        <th>Capacity</th>
                                        <th>Status</th>
                                        {!m.isPast && <th style={{ textAlign: 'right' }}>Actions</th>}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.schedules.map(s => (
                                        <tr key={s.id}>
                                          <td style={{ textAlign: 'center' }}>
                                            <span style={{ fontWeight: '700', color: '#475569', fontSize: '0.85rem' }}>
                                              {s.courseId ?? s.CourseID ?? '—'}
                                            </span>
                                          </td>
                                          <td>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                              <span className="course-name-bold">{s.course}</span>
                                              <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                                                Class Level: <strong style={{ color: '#4338ca' }}>{s.level || 'Entry'}</strong>
                                              </span>
                                            </div>
                                          </td>
                                          <td>
                                            <span className="trainer-cell">{s.trainer}</span>
                                          </td>
                                          <td>
                                            {s.sessionSegments && Array.isArray(s.sessionSegments) && s.sessionSegments.length > 1 ? (
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                {s.sessionSegments.map((seg, sIdx) => (
                                                  <span key={sIdx} style={{ fontSize: '0.78rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                                    <strong style={{ color: '#4338ca', marginRight: '4px' }}>Pt {sIdx + 1}:</strong>
                                                    {formatToDMY(seg.startDate)} - {formatToDMY(seg.endDate)}
                                                  </span>
                                                ))}
                                                <span style={{ fontSize: '0.7rem', color: '#047857', fontWeight: 600 }}>
                                                  {calculateTotalTrainingDays(s.sessionSegments)} Days Total
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="dates-cell">{s.dates}</span>
                                            )}
                                          </td>
                                          <td>
                                            <span className="venue-cell">{s.venue}</span>
                                          </td>
                                          <td>
                                            <span style={{ fontWeight: '700', color: '#166534', fontSize: '0.85rem' }}>{s.fee}</span>
                                          </td>
                                          <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <span style={{ fontWeight: '700', color: s.enrolledCount >= s.maxCapacity ? '#ef4444' : '#1e293b' }}>
                                                {s.registered}
                                              </span>
                                              {!m.isPast && (
                                                <button
                                                  onClick={() => handleUpdateScheduleCapacity(s.id, s.maxCapacity)}
                                                  title="Edit Capacity"
                                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', fontSize: '0.75rem', textDecoration: 'underline', padding: 0 }}
                                                >
                                                  Edit Limit
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            <span
                                              className={`status-badge ${m.isPast ? 'status-completed' : s.statusClass}`}
                                              style={
                                                m.isPast
                                                  ? { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }
                                                  : s.isCancelled
                                                  ? { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontWeight: '700' }
                                                  : {}
                                              }
                                            >
                                              {m.isPast ? 'Completed' : s.status}
                                            </span>
                                          </td>
                                          {!m.isPast && (
                                            <td style={{ textAlign: 'right' }}>
                                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', alignItems: 'center' }}>
                                                {s.isCancelled ? (
                                                  <button
                                                    type="button"
                                                    onClick={() => handleOpenCreateSessionModal(s)}
                                                    style={{
                                                      padding: '0.35rem 0.75rem',
                                                      borderRadius: '6px',
                                                      border: '1px solid #059669',
                                                      background: '#ecfdf5',
                                                      color: '#047857',
                                                      fontWeight: '700',
                                                      fontSize: '0.75rem',
                                                      cursor: 'pointer',
                                                      display: 'inline-flex',
                                                      alignItems: 'center',
                                                      gap: '0.35rem',
                                                      transition: 'all 0.15s ease'
                                                    }}
                                                    onMouseOver={(e) => { e.currentTarget.style.background = '#d1fae5'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                                                    title="Reopen Class (Schedule New Dates for this Course)"
                                                  >
                                                    <SvgIcon name="plus" size={12} /> Reopen Class
                                                  </button>
                                                ) : (
                                                  <>
                                                    <button
                                                      onClick={() => handleOpenCertModal(s)}
                                                      style={{
                                                        padding: '0.35rem 0.65rem',
                                                        borderRadius: '6px',
                                                        border: '1px solid #c7d2fe',
                                                        background: '#eef2ff',
                                                        color: '#4338ca',
                                                        fontWeight: '700',
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '3px'
                                                      }}
                                                      title="Issue & Manage Certificates of Completion"
                                                    >
                                                      <SvgIcon name="graduationCap" size={13} /> Certs
                                                    </button>
                                                    <button
                                                      onClick={() => handleOpenEditCourseModal(s)}
                                                      style={{
                                                        padding: '0.35rem 0.65rem',
                                                        borderRadius: '6px',
                                                        border: '1px solid #cbd5e1',
                                                        background: '#ffffff',
                                                        color: '#334155',
                                                        fontWeight: '600',
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer'
                                                      }}
                                                      title="Edit Details & Email Template"
                                                    >
                                                      <SvgIcon name="edit" size={12} /> Edit
                                                    </button>
                                                    <button
                                                      className={s.isClosed ? 'action-btn-approve' : 'action-btn-reject'}
                                                      onClick={() => handleToggleScheduleStatus(s.id)}
                                                      style={{
                                                        padding: '0.35rem 0.65rem',
                                                        borderRadius: '6px',
                                                        fontWeight: '700',
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer'
                                                      }}
                                                    >
                                                      {s.isClosed ? 'Open' : 'Close'}
                                                    </button>
                                                    <button
                                                      onClick={() => handleOpenCancelModal(s)}
                                                      style={{
                                                        padding: '0.35rem 0.65rem',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ef4444',
                                                        background: '#ffffff',
                                                        color: '#dc2626',
                                                        fontWeight: '700',
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s ease'
                                                      }}
                                                      onMouseOver={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                                                      onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; }}
                                                      title="Cancel Class & Notify Learners via Email"
                                                    >
                                                      Cancel Class
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </>
        ) : activeView === 'adminAccountUpdates' ? (
          <>
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="clock" size={22} />
                <div>
                  <h1 className="admin-dashboard-title" style={{ margin: 0 }}>Pending Account Updates</h1>
                </div>
              </div>
              <div className="profile-top-bar-right admin-header-actions">
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={() => onSwitchView ? onSwitchView('adminAccountRegistration') : null}
                  data-testid="nav-to-registration-approvals-btn"
                >
                  <SvgIcon name="clipboard" size={15} />
                  <span>Registration Approvals</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={() => onSwitchView ? onSwitchView('adminAccounts') : null}
                  data-testid="nav-to-user-directory-btn"
                >
                  <SvgIcon name="users" size={15} />
                  <span>User Directory & Roles</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">
              <PendingAccountUpdates adminUser={user} setToast={setToast} />
            </div>
          </>
        ) : activeView === 'adminAccountRegistration' ? (
          <>
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="users" size={22} />
                <div>
                  <h1 className="admin-dashboard-title" style={{ margin: 0 }}>Account Registration</h1>
                </div>
              </div>
              <div className="profile-top-bar-right admin-header-actions">
                <button
                  className="admin-header-action-btn admin-btn-dark"
                  onClick={() => onSwitchView ? onSwitchView('adminAccountUpdates') : null}
                  data-testid="nav-to-account-updates-btn"
                >
                  <SvgIcon name="clock" size={15} />
                  <span>Pending Account Updates</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={() => onSwitchView ? onSwitchView('adminAccounts') : null}
                  data-testid="nav-to-user-directory-btn"
                >
                  <SvgIcon name="users" size={15} />
                  <span>User Directory & Roles</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">
              <AccountRegistrationApprovals adminUser={user} setToast={setToast} />
            </div>
          </>
        ) : (activeView === 'adminAccounts' || activeView === 'adminLearners') ? (
          <>
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="users" size={22} />
                <div>
                  <h1 className="admin-dashboard-title" style={{ margin: 0 }}>Account Registration</h1>
                </div>
              </div>
              <div className="profile-top-bar-right admin-header-actions">
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={() => onSwitchView ? onSwitchView('adminAccountUpdates') : null}
                  data-testid="nav-to-account-updates-from-dir-btn"
                >
                  <SvgIcon name="clock" size={15} />
                  <span>Pending Account Updates</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-dark"
                  onClick={() => onSwitchView ? onSwitchView('adminAccountRegistration') : null}
                  data-testid="nav-to-registration-approvals-btn"
                >
                  <SvgIcon name="clipboard" size={15} />
                  <span>Registration Approvals</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={() => {
                    fetchAccounts();
                    fetchLearners();
                    fetchTrainers();
                    setToast({ type: 'success', msg: 'Latest account statuses refreshed!' });
                  }}
                  title="Refresh latest status"
                >
                  <SvgIcon name="refresh" size={14} />
                  <span>Refresh Status</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-blue"
                  onClick={() => {
                    setCreateError('');
                    setNewAccountData(prev => ({ ...prev, role: isEditor ? 'Editor' : 'Admin' }));
                    setShowCreateAccountModal(true);
                  }}
                >
                  <SvgIcon name="plus" size={15} />
                  <span>Create Account</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">
              {/* Directory Sub-Tabs */}
              <div className="td-tabs-bar" style={{ marginBottom: '1.25rem' }}>
                <button
                  className={`td-tab-btn ${participantTab === 'learners' ? 'active' : ''}`}
                  onClick={() => { setParticipantTab('learners'); setDirectorySearchScope('all'); }}
                >
                  <span style={{ fontSize: '1rem', lineHeight: 1, marginRight: '4px', color: participantTab === 'learners' ? '#4f46e5' : '#94a3b8' }}>•</span> Learners ({filteredLearners.length})
                </button>
                <button
                  className={`td-tab-btn ${participantTab === 'trainers' ? 'active' : ''}`}
                  onClick={() => { setParticipantTab('trainers'); setDirectorySearchScope('all'); }}
                >
                  <span style={{ fontSize: '1rem', lineHeight: 1, marginRight: '4px', color: participantTab === 'trainers' ? '#4f46e5' : '#94a3b8' }}>•</span> Trainers ({filteredTrainers.length})
                </button>
                {!isEditor && (
                  <button
                    className={`td-tab-btn ${participantTab === 'staff' ? 'active' : ''}`}
                    onClick={() => { setParticipantTab('staff'); setDirectorySearchScope('all'); }}
                  >
                    <span style={{ fontSize: '1rem', lineHeight: 1, marginRight: '4px', color: participantTab === 'staff' ? '#4f46e5' : '#94a3b8' }}>•</span> Admin ({filteredAdmins.length})
                  </button>
                )}
              </div>

              {participantTab === 'learners' ? (
                <div className="admin-widget-card" style={{ marginTop: '0px' }}>
                  <div className="widget-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div className="header-left">
                      <SvgIcon name="user" size={18} />
                      <h3>Registered Learners</h3>
                      <span className="header-badge">{filteredLearners.length}</span>
                    </div>

                    <div className="unified-search-box" style={{ width: '460px', maxWidth: '100%' }}>
                      <select
                        value={directorySearchScope}
                        onChange={(e) => setDirectorySearchScope(e.target.value)}
                        className="unified-search-select"
                        title="Select search category"
                      >
                        <option value="all">All Fields</option>
                        <option value="name">Name</option>
                        <option value="email">Email</option>
                        <option value="username">Username</option>
                        <option value="organisation">Organisation</option>
                      </select>
                      <div className="unified-search-input-wrap">
                        <span className="unified-search-icon">
                          <SvgIcon name="search" size={14} />
                        </span>
                        <input
                          type="text"
                          placeholder={
                            directorySearchScope === 'name' ? 'Search by learner name...' :
                              directorySearchScope === 'email' ? 'Search by email...' :
                                directorySearchScope === 'username' ? 'Search by username...' :
                                  directorySearchScope === 'organisation' ? 'Search by organisation or sector...' :
                                    'Search learners...'
                          }
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          className="unified-search-input"
                        />
                        {participantSearch && (
                          <button
                            type="button"
                            onClick={() => setParticipantSearch('')}
                            className="unified-search-clear"
                            title="Clear search"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {loadingLearners ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                      Loading learners directory...
                    </div>
                  ) : filteredLearners.length === 0 ? (
                    <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
                      No learners match your search query.
                    </div>
                  ) : (
                    <div className="admin-table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px', textAlign: 'center' }}>ID</th>
                            <th>Participant</th>
                            <th>Email</th>
                            <th>Contact</th>
                            <th>Position</th>
                            <th>Organisation</th>
                            <th
                              onClick={() => {
                                if (participantSort === 'default') setParticipantSort('most_active');
                                else if (participantSort === 'most_active') setParticipantSort('least_active');
                                else setParticipantSort('default');
                              }}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              title="Click to sort by Status & Inactivity"
                            >
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <span style={{ color: participantSort !== 'default' ? '#4f46e5' : 'inherit', fontWeight: '700' }}>Status & inactivity</span>
                                <span style={{ fontSize: '0.85rem', fontWeight: '800', color: participantSort !== 'default' ? '#4f46e5' : '#94a3b8' }}>
                                  {participantSort === 'most_active' ? '▲' : participantSort === 'least_active' ? '▼' : '⇅'}
                                </span>
                              </div>
                            </th>
                            <th style={{ textAlign: 'left', minWidth: '180px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLearners.map((l) => (
                            <tr key={l.LearnerID}>
                              <td style={{ textAlign: 'center', fontWeight: '600', color: '#94a3b8' }}>{l.LearnerID}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div className="participant-avatar-circle">
                                    {getInitials(l.FullName)}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                                    <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.9rem', textAlign: 'left' }}>{l.FullName}</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'left' }}>{l.Username || l.Email.split('@')[0]}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ color: '#334155', fontSize: '0.85rem' }}>{l.Email}</td>
                              <td>{l.Contact ? <span style={{ fontSize: '0.85rem', color: '#475569' }}>{l.Contact}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                              <td>{l.Position ? <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: '500' }}>{l.Position}</span> : <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                              <td>
                                {l.OrganisationName ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span style={{ fontSize: '0.85rem', color: '#334155', fontWeight: '500' }}>
                                      {l.OrganisationName}
                                    </span>
                                    {l.Sector && (
                                      <span style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: '600' }}>
                                        {l.Sector}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span style={{ color: '#cbd5e1' }}>-</span>
                                )}
                              </td>
                              <td>
                                {l.IsActive === 0 ? (
                                  (() => {
                                    const daysRem = getDeactivationDaysRemaining(l.DeactivatedDate, l.Username, l.Email);
                                    return daysRem === 0 ? (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting today)
                                      </span>
                                    ) : (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting in {daysRem}d)
                                      </span>
                                    );
                                  })()
                                ) : (
                                  renderActiveStatusBadge(l)
                                )}
                              </td>
                              <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '5px', width: '105px' }}>
                                  <button
                                    className="btn-action-edit"
                                    style={{ width: '100%', justifyContent: 'center' }}
                                    title="Edit Learner Profile"
                                    onClick={() => handleOpenEditLearner(l)}
                                  >
                                    <SvgIcon name="edit" size={13} /> Edit
                                  </button>
                                  {l.isSuspended || l.AccountStatus === 'Suspended' ? (
                                    <button
                                      style={{
                                        width: '100%',
                                        justifyContent: 'center',
                                        padding: '0.35rem 0.65rem',
                                        background: '#f0fdf4',
                                        color: '#166534',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '6px',
                                        fontSize: '0.78rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease',
                                        boxSizing: 'border-box'
                                      }}
                                      title="Unsuspend Learner Account (Lift 2-month blackout)"
                                      onClick={() => setSuspensionModal({
                                        learner: l,
                                        isSuspended: true,
                                        reason: '',
                                        loading: false
                                      })}
                                    >
                                      <SvgIcon name="checkCircle" size={12} color="#166534" /> Unsuspend
                                    </button>
                                  ) : (
                                    <button
                                      style={{
                                        width: '100%',
                                        justifyContent: 'center',
                                        padding: '0.35rem 0.65rem',
                                        background: '#fff1f2',
                                        color: '#be123c',
                                        border: '1px solid #fecdd3',
                                        borderRadius: '6px',
                                        fontSize: '0.78rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease',
                                        boxSizing: 'border-box'
                                      }}
                                      title="Suspend Learner Account (2-month blackout)"
                                      onClick={() => setSuspensionModal({
                                        learner: l,
                                        isSuspended: false,
                                        reason: '',
                                        loading: false
                                      })}
                                    >
                                      <SvgIcon name="slash" size={12} color="#be123c" /> Suspend
                                    </button>
                                  )}
                                  {l.IsActive !== 0 ? (
                                    <button
                                      style={{
                                        width: '100%',
                                        justifyContent: 'center',
                                        padding: '0.35rem 0.65rem',
                                        background: '#fef2f2',
                                        color: '#991b1b',
                                        border: '1px solid #fecaca',
                                        borderRadius: '6px',
                                        fontSize: '0.78rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        transition: 'all 0.2s ease',
                                        boxSizing: 'border-box'
                                      }}
                                      title="Deactivate Learner Account"
                                      onClick={() => handleOpenStatusToggleModal('Learner', l.LearnerID, l.IsActive, l.daysInactive, l.FullName)}
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        style={{
                                          width: '100%',
                                          justifyContent: 'center',
                                          padding: '0.35rem 0.65rem',
                                          background: '#f0fdf4',
                                          color: '#166534',
                                          border: '1px solid #bbf7d0',
                                          borderRadius: '6px',
                                          fontSize: '0.78rem',
                                          fontWeight: '700',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          transition: 'all 0.2s ease',
                                          boxSizing: 'border-box'
                                        }}
                                        title="Activate Learner Account"
                                        onClick={() => handleOpenStatusToggleModal('Learner', l.LearnerID, l.IsActive, l.daysInactive, l.FullName)}
                                      >
                                        Activate
                                      </button>
                                      {getDeactivationDaysRemaining(l.DeactivatedDate, l.Username, l.Email) === 0 && (
                                        <button
                                          style={{
                                            width: '100%',
                                            justifyContent: 'center',
                                            padding: '0.35rem 0.65rem',
                                            background: '#fef2f2',
                                            color: '#dc2626',
                                            border: '1px solid #fecaca',
                                            borderRadius: '6px',
                                            fontSize: '0.78rem',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease',
                                            boxSizing: 'border-box'
                                          }}
                                          title="Permanently Delete Expired Account"
                                          onClick={() => handleOpenDeleteModal('Learner', l.LearnerID, l.FullName)}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : participantTab === 'trainers' ? (
                <div className="admin-widget-card" style={{ marginTop: '0px' }}>
                  <div className="widget-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div className="header-left">
                      <SvgIcon name="award" size={18} />
                      <h3>Certified Trainers</h3>
                      <span className="header-badge">{filteredTrainers.length}</span>
                    </div>

                    <div className="unified-search-box" style={{ width: '420px', maxWidth: '100%' }}>
                      <select
                        value={directorySearchScope}
                        onChange={(e) => setDirectorySearchScope(e.target.value)}
                        className="unified-search-select"
                        title="Select search category"
                      >
                        <option value="all">All Fields</option>
                        <option value="name">Name</option>
                        <option value="email">Email</option>
                        <option value="username">Username</option>
                      </select>
                      <div className="unified-search-input-wrap">
                        <span className="unified-search-icon">
                          <SvgIcon name="search" size={14} />
                        </span>
                        <input
                          type="text"
                          placeholder={
                            directorySearchScope === 'name' ? 'Search by trainer name...' :
                              directorySearchScope === 'email' ? 'Search by email...' :
                                directorySearchScope === 'username' ? 'Search by username...' :
                                  'Search trainers...'
                          }
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          className="unified-search-input"
                        />
                        {participantSearch && (
                          <button
                            type="button"
                            onClick={() => setParticipantSearch('')}
                            className="unified-search-clear"
                            title="Clear search"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {filteredTrainers.length === 0 ? (
                    <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
                      No trainers match your search query.
                    </div>
                  ) : (
                    <div className="admin-table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px', textAlign: 'center' }}>ID</th>
                            <th>Trainer Profile</th>
                            <th>Email</th>
                            <th
                              onClick={() => {
                                if (participantSort === 'default') setParticipantSort('most_active');
                                else if (participantSort === 'most_active') setParticipantSort('least_active');
                                else setParticipantSort('default');
                              }}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              title="Click to sort by Status & Inactivity"
                            >
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <span style={{ color: participantSort !== 'default' ? '#4f46e5' : 'inherit', fontWeight: '700' }}>Status & inactivity</span>
                                <span style={{ fontSize: '0.85rem', fontWeight: '800', color: participantSort !== 'default' ? '#4f46e5' : '#94a3b8' }}>
                                  {participantSort === 'most_active' ? '▲' : participantSort === 'least_active' ? '▼' : '⇅'}
                                </span>
                              </div>
                            </th>
                            <th style={{ textAlign: 'left', minWidth: '180px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTrainers.map((t) => (
                            <tr key={t.TrainerID}>
                              <td style={{ textAlign: 'center', fontWeight: '600', color: '#94a3b8' }}>{t.TrainerID}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div className="participant-avatar-circle participant-avatar-trainer">
                                    {getInitials(t.FullName)}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
                                    <div style={{ fontWeight: '600', color: '#0f172a', fontSize: '0.9rem', textAlign: 'left' }}>{t.FullName}</div>
                                    <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'left' }}>{t.Username || t.Email.split('@')[0]}</div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ color: '#334155', fontSize: '0.85rem' }}>{t.Email}</td>
                              <td>
                                {t.IsActive === 0 ? (
                                  (() => {
                                    const daysRem = getDeactivationDaysRemaining(t.DeactivatedDate, t.Username, t.Email);
                                    return daysRem === 0 ? (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting today)
                                      </span>
                                    ) : (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting in {daysRem}d)
                                      </span>
                                    );
                                  })()
                                ) : (
                                  renderActiveStatusBadge(t)
                                )}
                              </td>
                              <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '6px', whiteSpace: 'nowrap' }}>
                                  <button
                                    className="btn-action-edit"
                                    title="Edit Trainer Profile"
                                    onClick={() => handleOpenEditTrainer(t)}
                                  >
                                    <SvgIcon name="edit" size={13} /> Edit
                                  </button>
                                  {t.IsActive !== 0 ? (
                                    <button
                                      style={{ padding: '0.35rem 0.75rem', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                      title="Deactivate Trainer Account"
                                      onClick={() => handleOpenStatusToggleModal('Trainer', t.TrainerID, t.IsActive, t.daysInactive, t.FullName)}
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        style={{ padding: '0.35rem 0.75rem', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                        title="Activate Trainer Account"
                                        onClick={() => handleOpenStatusToggleModal('Trainer', t.TrainerID, t.IsActive, t.daysInactive, t.FullName)}
                                      >
                                        Activate
                                      </button>
                                      {getDeactivationDaysRemaining(t.DeactivatedDate, t.Username, t.Email) === 0 && (
                                        <button
                                          style={{ padding: '0.35rem 0.75rem', background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                          title="Permanently Delete Expired Account"
                                          onClick={() => handleOpenDeleteModal('Trainer', t.TrainerID, t.FullName)}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="admin-widget-card" style={{ marginTop: '0px' }}>
                  <div className="widget-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div className="header-left">
                      <SvgIcon name="shield" size={18} />
                      <h3>System Administrators</h3>
                      <span className="header-badge">
                        {filteredAdmins.length}
                      </span>
                    </div>

                    <div className="unified-search-box" style={{ width: '420px', maxWidth: '100%' }}>
                      <select
                        value={directorySearchScope}
                        onChange={(e) => setDirectorySearchScope(e.target.value)}
                        className="unified-search-select"
                        title="Select search category"
                      >
                        <option value="all">All Fields</option>
                        <option value="name">Name</option>
                        <option value="email">Email</option>
                        <option value="username">Username</option>
                        <option value="role">Role</option>
                      </select>
                      <div className="unified-search-input-wrap">
                        <span className="unified-search-icon">
                          <SvgIcon name="search" size={14} />
                        </span>
                        <input
                          type="text"
                          placeholder={
                            directorySearchScope === 'name' ? 'Search by staff name...' :
                              directorySearchScope === 'email' ? 'Search by email...' :
                                directorySearchScope === 'username' ? 'Search by username...' :
                                  directorySearchScope === 'role' ? 'Search by role (Admin, Editor, Report Viewer)...' :
                                    'Search administrators...'
                          }
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          className="unified-search-input"
                        />
                        {participantSearch && (
                          <button
                            type="button"
                            onClick={() => setParticipantSearch('')}
                            className="unified-search-clear"
                            title="Clear search"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {loadingAccounts ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                      Loading administrator accounts...
                    </div>
                  ) : filteredAdmins.length === 0 ? (
                    <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b' }}>
                      No administrator accounts match your search query.
                    </div>
                  ) : (
                    <div className="admin-table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th style={{ width: '50px', textAlign: 'center' }}>ID</th>
                            <th>Account Name</th>
                            <th>Username</th>
                            <th>Role</th>
                            <th
                              onClick={() => {
                                if (participantSort === 'default') setParticipantSort('most_active');
                                else if (participantSort === 'most_active') setParticipantSort('least_active');
                                else setParticipantSort('default');
                              }}
                              style={{ cursor: 'pointer', userSelect: 'none' }}
                              title="Click to sort by Status & Inactivity"
                            >
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <span style={{ color: participantSort !== 'default' ? '#4f46e5' : 'inherit', fontWeight: '700' }}>Status & inactivity</span>
                                <span style={{ fontSize: '0.85rem', fontWeight: '800', color: participantSort !== 'default' ? '#4f46e5' : '#94a3b8' }}>
                                  {participantSort === 'most_active' ? '▲' : participantSort === 'least_active' ? '▼' : '⇅'}
                                </span>
                              </div>
                            </th>
                            <th style={{ textAlign: 'left', minWidth: '140px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAdmins.map(acc => (
                            <tr key={`${acc.accountType}-${acc.id}`}>
                              <td style={{ textAlign: 'center', fontWeight: '600', color: '#94a3b8' }}>{acc.id}</td>
                              <td>
                                <div className="learner-avatar-cell">
                                  <div className="avatar-circle">
                                    {acc.FullName ? acc.FullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                  </div>
                                  <div className="learner-details">
                                    <span className="learner-name">{acc.FullName}</span>
                                    <span className="learner-email">{acc.Email}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#334155' }}>
                                  {acc.Username}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '700',
                                  backgroundColor: acc.Role === 'Admin' ? '#f3e8ff' : acc.Role === 'Report Viewer' ? '#ecfdf5' : '#e0f2fe',
                                  color: acc.Role === 'Admin' ? '#6b21a8' : acc.Role === 'Report Viewer' ? '#047857' : '#0369a1'
                                }}>
                                  {acc.Role}
                                </span>
                              </td>
                              <td>
                                {acc.IsActive === 0 ? (
                                  (() => {
                                    const daysRem = getDeactivationDaysRemaining(acc.DeactivatedDate, acc.Username, acc.Email);
                                    return daysRem === 0 ? (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting today)
                                      </span>
                                    ) : (
                                      <span style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Deactivated (Deleting in {daysRem}d)
                                      </span>
                                    );
                                  })()
                                ) : (
                                  renderActiveStatusBadge(acc)
                                )}
                              </td>
                              <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '6px', whiteSpace: 'nowrap' }}>
                                  {acc.IsActive !== 0 ? (
                                    <button
                                      style={{ padding: '0.35rem 0.75rem', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                      title="Deactivate Admin Account"
                                      onClick={() => handleOpenStatusToggleModal('Admin', acc.id, acc.IsActive, acc.daysInactive, acc.FullName)}
                                    >
                                      Deactivate
                                    </button>
                                  ) : (
                                    <>
                                      <button
                                        style={{ padding: '0.35rem 0.75rem', background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                        title="Activate Admin Account"
                                        onClick={() => handleOpenStatusToggleModal('Admin', acc.id, acc.IsActive, acc.daysInactive, acc.FullName)}
                                      >
                                        Activate
                                      </button>
                                      {getDeactivationDaysRemaining(acc.DeactivatedDate, acc.Username, acc.Email) === 0 && (
                                        <button
                                          style={{ padding: '0.35rem 0.75rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.2s ease' }}
                                          title="Permanently Delete Expired Account"
                                          onClick={() => handleOpenDeleteModal(acc.Role || 'Admin', acc.id, acc.FullName)}
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (activeView === 'adminApprovals' || activeView === 'adminSessionRegistration') ? (
          <>
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="clipboard" size={22} />
                <h1 className="admin-dashboard-title">Session Registration</h1>
              </div>
              <div className="profile-top-bar-right admin-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={handleSendAdminReminderDigest}
                  disabled={sendingAdminDigest}
                  title="Send email alert digest for overdue pending registrations to Admin team"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <SvgIcon name="mail" size={14} />
                  <span>{sendingAdminDigest ? 'Sending...' : 'Send Alert to Admins'}</span>
                </button>
                <button
                  className="admin-header-action-btn admin-btn-dark"
                  onClick={() => onSwitchView ? onSwitchView('adminRegistrationBulkImport') : null}
                  data-testid="nav-to-bulk-reg-import-btn"
                >
                  <SvgIcon name="download" size={15} />
                  <span>Import Registrations (CSV)</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">
              {/* ── Admin Reminder Alert Banner (5+ Days Aging) ── */}
              {pendingList.filter(r => (r.isOverdue || (r.daysPending !== undefined ? r.daysPending >= 5 : (Date.now() - new Date(r.RegistrationDate).getTime()) / (1000 * 60 * 60 * 24) >= 5))).length > 0 && (
                <div style={{
                  margin: '0 0 1.25rem 0',
                  padding: '1rem 1.25rem',
                  backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  color: '#92400e',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flex: 1, minWidth: '280px' }}>
                    <span style={{ fontSize: '1.4rem', lineHeight: '1' }}>⚠️</span>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '0.92rem', marginBottom: '0.2rem', color: '#78350f' }}>
                        Admin Reminder: {pendingList.filter(r => (r.isOverdue || (r.daysPending !== undefined ? r.daysPending >= 5 : (Date.now() - new Date(r.RegistrationDate).getTime()) / (1000 * 60 * 60 * 24) >= 5))).length} Pending Registration(s) Require Approval or Rejection (&gt; 5 Days)
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: '1.4' }}>
                        These registrations have been pending review for 5 or more days. Automatic rejection is disabled. Please review and approve or reject them.
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSendAdminReminderDigest}
                    disabled={sendingAdminDigest}
                    style={{
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      padding: '0.45rem 0.85rem',
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      cursor: sendingAdminDigest ? 'wait' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    title="Send an email alert digest to all Admin staff"
                  >
                    <SvgIcon name="mail" size={13} />
                    <span>{sendingAdminDigest ? 'Sending...' : 'Send Alert to Admins'}</span>
                  </button>
                </div>
              )}

              <div className="admin-widget-card" style={{ marginTop: '0px' }}>
                {/* ── Tabs for Pending Approvals vs Pending Payments ── */}
                <div className="admin-tab-group">
                  <button
                    className={`admin-tab-btn ${approvalTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setApprovalTab('pending')}
                  >
                    <span>Pending Approvals</span>
                    <span className="admin-tab-badge">
                      {pendingList.filter(r => r.Status === 'Pending Approval').length}
                    </span>
                  </button>
                  <button
                    className={`admin-tab-btn ${approvalTab === 'payment' ? 'active' : ''}`}
                    onClick={() => setApprovalTab('payment')}
                  >
                    <span>Pending Payments</span>
                    <span className="admin-tab-badge" style={{ backgroundColor: approvalTab === 'payment' ? '#fef3c7' : '#f1f5f9', color: approvalTab === 'payment' ? '#92400e' : '#475569' }}>
                      {pendingList.filter(r => r.Status === 'Pending Payment' || r.Status === 'Payment Submitted').length}
                    </span>
                  </button>
                </div>

                {approvalTab === 'pending' ? (
                  <>
                    <div className="widget-card-header" style={{ marginBottom: '1rem' }}>
                      <div className="header-left">
                        <h3>Pending Session Registrations</h3>
                        <span className="header-badge">{pendingList.filter(r => r.Status === 'Pending Approval').length}</span>
                      </div>
                    </div>

                    {loadingApprovals ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                        Loading pending registrations...
                      </div>
                    ) : pendingList.filter(r => r.Status === 'Pending Approval').length === 0 ? (
                      <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                        🎉 No pending session registrations require approval.
                      </div>
                    ) : (
                      <div className="admin-table-wrapper">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Learner</th>
                              <th>Course & Schedule</th>
                              <th>Submitted</th>
                              <th>Sector & Organisation</th>
                              <th>Review Status</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingList.filter(r => r.Status === 'Pending Approval').map(reg => {
                              const regDate = new Date(reg.RegistrationDate);
                              const elapsedMs = Date.now() - regDate.getTime();
                              const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
                              const daysPending = reg.daysPending !== undefined ? reg.daysPending : elapsedDays;

                              let slaBadgeClass = 'sla-normal';
                              let slaText = '';

                              if (daysPending >= 7 || reg.alertLevel === 'critical') {
                                slaBadgeClass = 'sla-danger';
                                slaText = `⚠️ Overdue ${daysPending}d (Needs Action)`;
                              } else if (daysPending >= 5 || reg.alertLevel === 'warning') {
                                slaBadgeClass = 'sla-warning';
                                slaText = `⚠️ Overdue ${daysPending}d`;
                              } else {
                                const remDays = 5 - daysPending;
                                slaBadgeClass = remDays <= 2 ? 'sla-warning' : 'sla-normal';
                                slaText = `Due in ${remDays}d`;
                              }

                              return (
                                <tr key={reg.RegistrationID}>
                                  <td>
                                    <div className="learner-avatar-cell">
                                      <div className="avatar-circle">
                                        {reg.LearnerName ? reg.LearnerName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                      </div>
                                      <div className="learner-details">
                                        <span className="learner-name">{reg.LearnerName}</span>
                                        <span className="learner-email">{reg.LearnerEmail}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span className="course-name-bold">{reg.CourseName}</span>
                                      <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                        {formatToDMY(reg.StartDate)} - {formatToDMY(reg.EndDate)} ({reg.StartTime ? reg.StartTime.toLowerCase() : ''})
                                      </span>
                                      {Boolean(reg.IsReEnrollment) && (
                                        <span style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          background: '#fef3c7',
                                          color: '#92400e',
                                          border: '1px solid #fde68a',
                                          borderRadius: '6px',
                                          padding: '2px 6px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          marginTop: '4px',
                                          width: 'fit-content'
                                        }}>
                                          ⚠️ Re-Enrollment (Previous: {reg.PreviousAttendanceRate !== null && reg.PreviousAttendanceRate !== undefined ? `${reg.PreviousAttendanceRate}%` : 'Incomplete'})
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <span className="datetime-cell">
                                      {regDate.toLocaleDateString('en-GB')}
                                    </span>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                                        {reg.OrganisationName || 'N/A'}
                                      </span>
                                      <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                        Sector: {reg.Sector || 'N/A'}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`sla-cell-badge ${slaBadgeClass}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      {daysPending < 5 && <SvgIcon name="clock" size={12} />}
                                      {slaText}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="action-buttons-group">
                                      <button
                                        className="action-btn-approve"
                                        onClick={() => handleUpdateStatus(reg.RegistrationID, 'Approved')}
                                      >
                                        Approve
                                      </button>
                                      <button
                                        className="action-btn-reject"
                                        onClick={() => handleUpdateStatus(reg.RegistrationID, 'Rejected')}
                                      >
                                        Reject
                                      </button>
                                      <button
                                        className="action-btn-payment"
                                        onClick={() => handleOpenPaymentEmailPreview(reg, false)}
                                        title="Preview and Send Payment Request Email"
                                      >
                                        Ask Payment
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  /* ── Pending Payments Tab ── */
                  <>
                    <div className="widget-card-header" style={{ marginBottom: '1rem' }}>
                      <div className="header-left">
                        <h3>Registrations Awaiting Payment & Verification</h3>
                        <span className="header-badge" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                          {pendingList.filter(r => r.Status === 'Pending Payment' || r.Status === 'Payment Submitted').length}
                        </span>
                      </div>
                    </div>

                    {loadingApprovals ? (
                      <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                        Loading pending payments...
                      </div>
                    ) : pendingList.filter(r => r.Status === 'Pending Payment' || r.Status === 'Payment Submitted').length === 0 ? (
                      <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                        ✨ No registrations are currently pending payment or verification.
                      </div>
                    ) : (
                      <div className="admin-table-wrapper">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Learner</th>
                              <th>Course</th>
                              <th>Fee & Stripe Link</th>
                              <th>Uploaded Bank Slip</th>
                              <th>Payment Status</th>
                              <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingList.filter(r => r.Status === 'Pending Payment' || r.Status === 'Payment Submitted').map(reg => (
                              <tr key={reg.RegistrationID}>
                                <td>
                                  <div className="learner-avatar-cell">
                                    <div className="avatar-circle" style={{ backgroundColor: reg.Status === 'Payment Submitted' ? '#e0f2fe' : '#fef3c7', color: reg.Status === 'Payment Submitted' ? '#0369a1' : '#b45309' }}>
                                      {reg.LearnerName ? reg.LearnerName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                    </div>
                                    <div className="learner-details">
                                      <span className="learner-name">{reg.LearnerName}</span>
                                      <span className="learner-email">{reg.LearnerEmail}</span>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span className="course-name-bold">{reg.CourseName}</span>
                                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                      {formatToDMY(reg.StartDate)} - {formatToDMY(reg.EndDate)}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{ fontWeight: '700', color: '#166534', fontSize: '0.85rem' }}>
                                      {reg.Fee || 'RM 2,100'}
                                    </span>
                                    <a
                                      href={reg.PaymentLink || 'https://buy.stripe.com/fZubJ0cfl5a91Vu3NPdjO1i'}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ fontSize: '11px', color: '#4f46e5', textDecoration: 'underline' }}
                                    >
                                      🔗 Stripe Link
                                    </a>
                                  </div>
                                </td>
                                <td>
                                  {reg.PaymentProofPath ? (
                                    <button
                                      onClick={() => setViewBankSlip({ proof: reg.PaymentProofPath, learner: reg.LearnerName, course: reg.CourseName })}
                                      style={{ padding: '0.35rem 0.65rem', background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.75rem', cursor: 'pointer' }}
                                    >
                                      View Bank Slip
                                    </button>
                                  ) : (
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', italic: 'true' }}>- No slip uploaded -</span>
                                  )}
                                </td>
                                <td>
                                  {reg.Status === 'Payment Submitted' ? (
                                    <span className="sla-cell-badge" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 700 }}>
                                      Bank Slip Uploaded
                                    </span>
                                  ) : (
                                    <span className="sla-cell-badge sla-warning" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                                      Awaiting Payment
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div className="action-buttons-group">
                                    <button
                                      className="action-btn-approve"
                                      onClick={() => handleUpdateStatus(reg.RegistrationID, 'Approved')}
                                      title="Verify Payment and Approve Registration"
                                    >
                                      ✓ Approve Payment
                                    </button>
                                    <button
                                      className="action-btn-reject"
                                      onClick={() => handleUpdateStatus(reg.RegistrationID, 'Rejected')}
                                    >
                                      Reject
                                    </button>
                                    <button
                                      className="action-btn-payment"
                                      onClick={() => handleOpenPaymentEmailPreview(reg, true)}
                                      title="Preview and Resend Payment Request Email"
                                    >
                                      Resend Email
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ── SCREEN: Dashboard Summary ── */}
            <header className="profile-top-bar admin-top-bar">
              <div className="profile-top-bar-left">
                <SvgIcon name="grid" size={22} />
                <h1 className="admin-dashboard-title">Admin Dashboard</h1>
              </div>
              <div className="profile-top-bar-right admin-header-actions">
                <button
                  className="admin-header-action-btn admin-btn-outline"
                  onClick={handleRefreshStatus}
                  disabled={refreshingStatus}
                  style={{
                    cursor: refreshingStatus ? 'not-allowed' : 'pointer',
                    opacity: refreshingStatus ? 0.7 : 1
                  }}
                  title="Refresh registration summary stats and status"
                >
                  <SvgIcon name="refresh" size={14} />
                  <span>{refreshingStatus ? 'Refreshing...' : 'Refresh Status'}</span>
                </button>
              </div>
            </header>

            <div className="admin-dashboard-body">

              {/* In-App Notification Centre */}
              <NotificationCentre user={user} />

              {/* Welcome back section */}
              <div className="admin-welcome-section">
                <div className="welcome-message">
                  <h2>Welcome back, Admin!</h2>
                  <p>Monitor and manage training operations and system activities.</p>
                </div>
              </div>

              {/* Stats Grid - 2 primary registration cards */}
              <div className="admin-stats-grid">
                {/* Card 1: Pending Session Registrations */}
                <div className="admin-stat-card" onClick={() => onSwitchView('adminApprovals')}>
                  <div className="stat-card-icon bg-light-blue">
                    <SvgIcon name="calendar" size={22} />
                  </div>
                  <div className="stat-card-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <h3>{pendingList.length > 0 ? pendingList.length : (stats.pendingCount || 0)}</h3>
                      {(stats.overduePendingCount > 0 || pendingList.some(r => r.isOverdue || (r.daysPending !== undefined ? r.daysPending >= 5 : false))) && (
                        <span style={{
                          backgroundColor: '#fef2f2',
                          color: '#dc2626',
                          border: '1px solid #fecaca',
                          padding: '2px 6px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700'
                        }}>
                          ⚠️ {stats.overduePendingCount || pendingList.filter(r => r.isOverdue || r.daysPending >= 5).length} Overdue
                        </span>
                      )}
                    </div>
                    <p className="stat-label">Pending Session Registrations</p>
                    <span className="stat-link">View details &rarr;</span>
                  </div>
                </div>

                {/* Card 2: Total Withdrawn (Within Next Lookahead Days) */}
                <div className="admin-stat-card" onClick={() => openDetailsModal('Withdrawn', `Withdrawn Registrations (Next ${withdrawalLookaheadDays} Days)`)}>
                  <div className="stat-card-icon bg-light-orange" style={{ backgroundColor: '#fff7ed', color: '#c2410c' }}>
                    <SvgIcon name="userMinus" size={22} />
                  </div>
                  <div className="stat-card-content">
                    <h3>{stats.withdrawnCount ?? 0}</h3>
                    <p className="stat-label">Total Withdrawn (Next {withdrawalLookaheadDays} Days)</p>
                    <span className="stat-link">View details &rarr;</span>
                  </div>
                </div>
              </div>

              {/* Two Columns Section */}
              <div className="admin-dashboard-grid">

                {/* Left Column (Tables) */}
                <div className="admin-main-column">

                  {/* Pending Session Registrations Table Card */}
                  <div className="admin-widget-card">
                    <div className="widget-card-header">
                      <div className="header-left">
                        <SvgIcon name="user" size={18} />
                        <h3>Pending Session Registrations</h3>
                        <span className="header-badge">{pendingList.length}</span>
                      </div>
                      <button className="header-link-btn" onClick={() => onSwitchView('adminApprovals')}>
                        View all &rarr;
                      </button>
                    </div>

                    <div className="admin-table-wrapper">
                      {pendingList.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                          No pending session registrations.
                        </div>
                      ) : (
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Learner</th>
                              <th>Course</th>
                              <th>Submitted</th>
                              <th>Eligibility</th>
                              <th>SLA</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingList.slice(0, 3).map(reg => {
                              const regDate = new Date(reg.RegistrationDate);
                              const elapsedMs = Date.now() - regDate.getTime();
                              const elapsedDays = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
                              const daysPending = reg.daysPending !== undefined ? reg.daysPending : elapsedDays;

                              let slaBadgeClass = 'sla-normal';
                              let slaText = '';
                              if (daysPending >= 7 || reg.alertLevel === 'critical') {
                                slaBadgeClass = 'sla-danger';
                                slaText = `⚠️ Overdue ${daysPending}d`;
                              } else if (daysPending >= 5 || reg.alertLevel === 'warning') {
                                slaBadgeClass = 'sla-warning';
                                slaText = `⚠️ Overdue ${daysPending}d`;
                              } else {
                                const remDays = 5 - daysPending;
                                slaBadgeClass = remDays <= 2 ? 'sla-warning' : 'sla-normal';
                                slaText = `Due in ${remDays}d`;
                              }

                              return (
                                <tr key={reg.RegistrationID}>
                                  <td>
                                    <div className="learner-avatar-cell">
                                      <div className="avatar-circle">
                                        {reg.LearnerName ? reg.LearnerName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                      </div>
                                      <div className="learner-details">
                                        <span className="learner-name">{reg.LearnerName}</span>
                                        <span className="learner-email">{reg.LearnerEmail}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="course-name-cell">{reg.CourseName}</span>
                                  </td>
                                  <td>
                                    <span className="datetime-cell">
                                      {regDate.toLocaleDateString('en-GB')}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="eligibility-badge verified">Verified</span>
                                  </td>
                                  <td>
                                    <span className={`sla-cell-badge ${slaBadgeClass}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      {daysPending < 5 && <SvgIcon name="clock" size={12} />}
                                      {slaText}
                                    </span>
                                  </td>
                                  <td>
                                    <button className="review-btn" onClick={() => onSwitchView('adminApprovals')}>
                                      Review
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: '4px' }}>
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  {/* Upcoming Courses Table / Card View Card */}
                  <div className="admin-widget-card">
                    <div className="widget-card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
                      <div className="header-left">
                        <SvgIcon name="calendar" size={18} />
                        <h3>Upcoming Courses (This Week)</h3>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <div className="cc-view-toggle-bar" style={{ display: 'inline-flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                          <button
                            className={`cc-view-toggle-btn ${upcomingViewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setUpcomingViewMode('table')}
                            style={{
                              padding: '0.3rem 0.65rem',
                              fontSize: '0.78rem',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: upcomingViewMode === 'table' ? '#ffffff' : 'transparent',
                              color: upcomingViewMode === 'table' ? '#1e293b' : '#64748b',
                              boxShadow: upcomingViewMode === 'table' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            Table View
                          </button>
                          <button
                            className={`cc-view-toggle-btn ${upcomingViewMode === 'cards' ? 'active' : ''}`}
                            onClick={() => setUpcomingViewMode('cards')}
                            style={{
                              padding: '0.3rem 0.65rem',
                              fontSize: '0.78rem',
                              fontWeight: '600',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              background: upcomingViewMode === 'cards' ? '#ffffff' : 'transparent',
                              color: upcomingViewMode === 'cards' ? '#1e293b' : '#64748b',
                              boxShadow: upcomingViewMode === 'cards' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            Card View
                          </button>
                        </div>
                        <button className="header-link-btn view-calendar" onClick={() => onSwitchView('calendar')}>
                          View calendar &rarr;
                        </button>
                      </div>
                    </div>

                    {upcomingViewMode === 'table' ? (
                      <div className="admin-table-wrapper">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Course</th>
                              <th>Trainer</th>
                              <th>Dates</th>
                              <th>Venue</th>
                              <th>Registered</th>
                              <th>Status</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loadingCourses ? (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                  Loading upcoming courses...
                                </td>
                              </tr>
                            ) : upcomingCourses.length === 0 ? (
                              <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                  No upcoming courses scheduled for this week.
                                </td>
                              </tr>
                            ) : (
                              upcomingCourses.map(course => (
                                <tr key={course.id}>
                                  <td>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span className="course-name-bold">{course.course}</span>
                                      <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                                        Class Level: <strong style={{ color: '#4338ca' }}>{course.level || 'Entry'}</strong>
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="trainer-cell">{course.trainer}</span>
                                  </td>
                                  <td>
                                    {course.sessionSegments && Array.isArray(course.sessionSegments) && course.sessionSegments.length > 1 ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        {course.sessionSegments.map((seg, sIdx) => (
                                          <span key={sIdx} style={{ fontSize: '0.78rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                                            <strong style={{ color: '#4338ca', marginRight: '4px' }}>Pt {sIdx + 1}:</strong>
                                            {formatToDMY(seg.startDate)} - {formatToDMY(seg.endDate)}
                                          </span>
                                        ))}
                                        <span style={{ fontSize: '0.7rem', color: '#047857', fontWeight: 600 }}>
                                          {calculateTotalTrainingDays(course.sessionSegments)} Days Total
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="dates-cell">{course.dates}</span>
                                    )}
                                  </td>
                                  <td>
                                    <span className="venue-cell">{course.venue}</span>
                                  </td>
                                  <td>
                                    <span className="registered-cell">{course.registered}</span>
                                  </td>
                                  <td>
                                    <span className={`status-badge ${course.statusClass}`}>
                                      {course.status}
                                    </span>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    {course.isCancelled ? (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenCreateSessionModal(course)}
                                        style={{
                                          padding: '0.35rem 0.75rem',
                                          borderRadius: '6px',
                                          border: '1px solid #059669',
                                          background: '#ecfdf5',
                                          color: '#047857',
                                          fontWeight: '700',
                                          fontSize: '0.75rem',
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.35rem',
                                          transition: 'all 0.15s ease'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.background = '#d1fae5'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                                        title="Reopen Session (Schedule New Dates for this Course)"
                                      >
                                        <SvgIcon name="plus" size={12} /> Reopen
                                      </button>
                                    ) : (
                                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', alignItems: 'center' }}>
                                        <button
                                          onClick={() => handleOpenCertModal(course)}
                                          style={{
                                            padding: '0.35rem 0.65rem',
                                            borderRadius: '6px',
                                            border: '1px solid #c7d2fe',
                                            background: '#eef2ff',
                                            color: '#4338ca',
                                            fontWeight: '700',
                                            fontSize: '0.75rem',
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '3px'
                                          }}
                                          title="Issue & Manage Certificates of Completion"
                                        >
                                          <SvgIcon name="graduationCap" size={13} /> Certs
                                        </button>
                                        <button
                                          className={course.isClosed ? 'action-btn-approve' : 'action-btn-reject'}
                                          onClick={() => handleToggleScheduleStatus(course.id)}
                                          style={{
                                            padding: '0.35rem 0.65rem',
                                            borderRadius: '6px',
                                            fontWeight: '700',
                                            fontSize: '0.75rem',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          {course.isClosed ? '🔓 Open' : '🔒 Close'}
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="admin-courses-card-grid">
                        {upcomingCourses.map(course => (
                          <div key={course.id} className="admin-course-card">
                            <div className="admin-card-head">
                              <div>
                                <h4 className="admin-card-title">{course.course}</h4>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', fontWeight: '500' }}>
                                  Class Level: <strong style={{ color: '#4338ca' }}>{course.level || 'Entry'}</strong>
                                </div>
                              </div>
                              <span className={`status-badge ${course.statusClass}`}>{course.status}</span>
                            </div>

                            <div className="admin-card-meta">
                              <div className="admin-meta-row">
                                <SvgIcon name="user" size={14} />
                                <span>Trainer: <strong>{course.trainer}</strong></span>
                              </div>

                              <div className="admin-meta-row" style={course.sessionSegments && course.sessionSegments.length > 1 ? { alignItems: 'flex-start' } : {}}>
                                <SvgIcon name="calendar" size={14} style={course.sessionSegments && course.sessionSegments.length > 1 ? { marginTop: '2px', flexShrink: 0 } : {}} />
                                {course.sessionSegments && course.sessionSegments.length > 1 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    {course.sessionSegments.map((s, idx) => (
                                      <span key={idx} style={{ fontSize: '0.78rem', color: '#1e293b' }}>
                                        <strong style={{ color: '#4338ca' }}>Pt {idx + 1}:</strong> {formatToDMY(s.startDate)} - {formatToDMY(s.endDate)}
                                      </span>
                                    ))}
                                    <span style={{ fontSize: '0.7rem', color: '#047857', fontWeight: 600 }}>
                                      {calculateTotalTrainingDays(course.sessionSegments)} Days Total
                                    </span>
                                  </div>
                                ) : (
                                  <span>Dates: {course.dates}</span>
                                )}
                              </div>

                              <div className="admin-meta-row">
                                <SvgIcon name="mapPin" size={14} />
                                <span>Venue: {course.venue}</span>
                              </div>

                              <div className="admin-meta-row registered-row">
                                <SvgIcon name="users" size={14} />
                                <span>Registration: <strong style={{ color: '#4f46e5' }}>{course.registered}</strong></span>
                              </div>

                              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                                {course.isCancelled ? (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenCreateSessionModal(course)}
                                    style={{
                                      padding: '0.35rem 0.75rem',
                                      borderRadius: '6px',
                                      border: '1px solid #059669',
                                      background: '#ecfdf5',
                                      color: '#047857',
                                      fontWeight: '700',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '0.35rem',
                                      transition: 'all 0.15s ease'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#d1fae5'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#ecfdf5'; }}
                                    title="Reopen Session (Schedule New Dates for this Course)"
                                  >
                                    <SvgIcon name="plus" size={12} /> Reopen Session
                                  </button>
                                ) : (
                                  <button
                                    className={course.isClosed ? 'action-btn-approve' : 'action-btn-reject'}
                                    onClick={() => handleToggleScheduleStatus(course.id)}
                                    style={{
                                      padding: '0.35rem 0.75rem',
                                      borderRadius: '6px',
                                      fontWeight: '700',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {course.isClosed ? '🔓 Open Registration' : '🔒 Close Registration'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* Right Column (Quick Actions) */}
                <div className="admin-side-column">

                  {/* Quick Actions Card */}
                  <div className="admin-widget-card quick-actions-card">
                    <div className="widget-card-header">
                      <div className="header-left">
                        <h3>Quick Actions</h3>
                      </div>
                    </div>

                    <div className="quick-actions-grid">
                      <button
                        className="quick-action-btn color-purple"
                        onClick={() => {
                          if (onSwitchView) onSwitchView('adminCourses');
                          handleOpenCreateCourseModal();
                        }}
                      >
                        <div className="action-btn-icon">
                          <SvgIcon name="book" size={20} />
                        </div>
                        <span>Add New Course</span>
                      </button>

                      <button
                        className="quick-action-btn color-green"
                        onClick={() => {
                          if (onSwitchView) onSwitchView('adminRegistrationBulkImport');
                        }}
                      >
                        <div className="action-btn-icon">
                          <SvgIcon name="download" size={20} />
                        </div>
                        <span>Import Registrations</span>
                      </button>

                      <button
                        className="quick-action-btn color-blue"
                        onClick={() => {
                          setOpenExportModalOnReportView(true);
                          if (onSwitchView) onSwitchView('adminReports');
                        }}
                      >
                        <div className="action-btn-icon">
                          <SvgIcon name="barChart" size={20} />
                        </div>
                        <span>Export Report</span>
                      </button>

                      <button className="quick-action-btn color-orange" onClick={() => onSwitchView ? onSwitchView('adminAuditLogs') : triggerAction('View Audit Logs')}>
                        <div className="action-btn-icon">
                          <SvgIcon name="shield" size={20} />
                        </div>
                        <span>View Audit Logs</span>
                      </button>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </>
        )}
      </div>

      {/* Registration Details Modal */}
      {modalConfig.isOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal-container modal-registration-details" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="modal-header-title">
                <SvgIcon name={modalConfig.status === 'Approved' ? 'checkCircle' : modalConfig.status === 'Withdrawn' ? 'userMinus' : 'xCircle'} size={22} />
                <h3>{modalConfig.title}</h3>
                <span className="header-badge">
                  {modalConfig.status === 'Withdrawn'
                    ? modalConfig.data.filter(item => {
                        if (!item.StartDate) return false;
                        const raw = String(item.StartDate).split('T')[0].split(' ')[0];
                        const parts = raw.split('-');
                        if (parts.length !== 3) return false;
                        const targetDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 0, 0, 0, 0);
                        const now = new Date();
                        const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
                        const diffDays = Math.round((targetDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
                        return diffDays >= 0 && diffDays <= 15;
                      }).length
                    : modalConfig.data.length}
                </span>
              </div>
              <button className="modal-close-btn" onClick={closeModal}>&times;</button>
            </div>

            <div className="admin-modal-body">
              {modalConfig.loading ? (
                <div style={{ padding: '2.5rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                  Loading registration details...
                </div>
              ) : (
                (() => {
                  const getFullCourseTitle = (certBody, courseName) => {
                    const cName = (courseName || '').trim();
                    const cBody = (certBody || '').trim();
                    if (!cBody) return cName;
                    if (cName.toLowerCase().startsWith(cBody.toLowerCase())) return cName;
                    return `${cBody} ${cName}`;
                  };

                  const parseDateStr = (str) => {
                    if (!str) return null;
                    if (typeof str === 'string' && str.includes(' ')) {
                      return new Date(str.replace(' ', 'T'));
                    }
                    return new Date(str);
                  };

                  const parseTimeToMinutes = (timeStr) => {
                    if (!timeStr) return 9999;
                    const clean = timeStr.trim().toUpperCase();
                    const match = clean.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                    if (!match) return 9999;
                    let hours = parseInt(match[1], 10);
                    const minutes = parseInt(match[2], 10);
                    const period = match[3];

                    if (period === 'PM' && hours < 12) hours += 12;
                    if (period === 'AM' && hours === 12) hours = 0;

                    return hours * 60 + minutes;
                  };

                  const formatDateVal = (dateStr) => {
                    if (!dateStr) return '-';
                    const d = parseDateStr(dateStr);
                    if (!d || isNaN(d.getTime())) return '-';
                    return d.toLocaleDateString('en-GB');
                  };

                  const getMonthInfo = (dateStr) => {
                    const d = parseDateStr(dateStr);
                    if (!d || isNaN(d.getTime())) {
                      return {
                        key: '9999-99',
                        shortMonth: 'OTHER',
                        fullLabel: 'Unscheduled / Other',
                        year: 9999,
                        monthNum: 99
                      };
                    }
                    const year = d.getFullYear();
                    const month = d.getMonth() + 1;
                    const key = `${year}-${String(month).padStart(2, '0')}`;
                    const shortMonth = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                    const fullLabel = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
                    return { key, shortMonth, fullLabel, year, monthNum: month, timestamp: d.getTime() };
                  };

                  const q = modalSearch.toLowerCase().trim();

                  // Smart field-scoped matcher for individual registrations
                  const itemMatchesScope = (item, cName, monthLabel) => {
                    if (!q) return true;
                    const nameMatch = Boolean(item.LearnerName && item.LearnerName.toLowerCase().includes(q));
                    const emailMatch = Boolean(item.LearnerEmail && item.LearnerEmail.toLowerCase().includes(q));
                    const courseMatch = Boolean(cName && cName.toLowerCase().includes(q));
                    const orgMatch = Boolean(
                      (item.OrganisationName && item.OrganisationName.toLowerCase().includes(q)) ||
                      (item.Sector && item.Sector.toLowerCase().includes(q))
                    );
                    const monthMatch = Boolean(monthLabel && monthLabel.toLowerCase().includes(q));

                    if (modalSearchScope === 'name') return nameMatch;
                    if (modalSearchScope === 'email') return emailMatch;
                    if (modalSearchScope === 'organisation') return orgMatch;
                    if (modalSearchScope === 'course') return courseMatch;
                    return nameMatch || emailMatch || orgMatch || courseMatch || monthMatch;
                  };

                  // Helper to check if a schedule date is within the configured lookahead days from today
                  const isDateWithinLookaheadDays = (dateStr) => {
                    if (!dateStr) return false;
                    const raw = String(dateStr).split('T')[0].split(' ')[0];
                    const parts = raw.split('-');
                    if (parts.length !== 3) return false;
                    const targetYear = parseInt(parts[0], 10);
                    const targetMonth = parseInt(parts[1], 10) - 1;
                    const targetDay = parseInt(parts[2], 10);

                    const targetDate = new Date(targetYear, targetMonth, targetDay, 0, 0, 0, 0);
                    const now = new Date();
                    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

                    const diffDays = Math.round((targetDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays >= 0 && diffDays <= withdrawalLookaheadDays;
                  };

                  // Step 1: Filter active dataset (strictly next lookahead days for Withdrawn)
                  const rawData = modalConfig.status === 'Withdrawn'
                    ? modalConfig.data.filter(item => isDateWithinLookaheadDays(item.StartDate))
                    : modalConfig.data;

                  // Group registrations into Month -> Course -> Items
                  const monthMap = {};

                  rawData.forEach(item => {
                    // Use schedule StartDate as primary event month
                    const targetDateStr = item.StartDate || item.WithdrawalDate || item.CancellationDate || item.ApprovalDate || item.RegistrationDate;
                    const monthInfo = getMonthInfo(targetDateStr);
                    const courseKey = getFullCourseTitle(item.CertificationBody, item.CourseName);
                    const groupKey = `${monthInfo.key}_${courseKey}`;

                    if (!monthMap[monthInfo.key]) {
                      monthMap[monthInfo.key] = {
                        info: monthInfo,
                        courses: {}
                      };
                    }

                    if (!monthMap[monthInfo.key].courses[courseKey]) {
                      monthMap[monthInfo.key].courses[courseKey] = {
                        courseKey,
                        groupKey,
                        courseName: courseKey,
                        certBody: item.CertificationBody,
                        startDate: item.StartDate,
                        endDate: item.EndDate,
                        startTime: item.StartTime,
                        endTime: item.EndTime,
                        items: []
                      };
                    }

                    monthMap[monthInfo.key].courses[courseKey].items.push(item);
                  });

                  // Also integrate courses from allSchedules (only within lookahead days for Withdrawn)
                  allSchedules.forEach(s => {
                    if (s.startDate && s.course) {
                      if (modalConfig.status === 'Withdrawn') {
                        // Strictly only include schedules starting within lookahead days
                        if (!isDateWithinLookaheadDays(s.startDate)) return;
                      }

                      const monthInfo = getMonthInfo(s.startDate);
                      const courseKey = getFullCourseTitle(s.certificationBody, s.courseName);
                      const groupKey = `${monthInfo.key}_${courseKey}`;

                      if (!monthMap[monthInfo.key]) {
                        monthMap[monthInfo.key] = {
                          info: monthInfo,
                          courses: {}
                        };
                      }

                      if (!monthMap[monthInfo.key].courses[courseKey]) {
                        monthMap[monthInfo.key].courses[courseKey] = {
                          courseKey,
                          groupKey,
                          courseName: courseKey,
                          certBody: s.certificationBody,
                          startDate: s.startDate,
                          endDate: s.endDate,
                          startTime: s.startTime,
                          endTime: s.endTime,
                          items: []
                        };
                      }
                    }
                  });

                  // Step 2: Categorize Months into Current & Upcoming, Past, and Unscheduled
                  const now = new Date();
                  const currentYear = now.getFullYear();
                  const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
                  const currentMonthKey = `${currentYear}-${currentMonth}`; // e.g. "2026-09"

                  const upcomingOrCurrentMonths = Object.keys(monthMap)
                    .filter(k => k >= currentMonthKey && k !== '9999-99')
                    .sort((a, b) => a.localeCompare(b)); // 2026-09 (Sep), 2026-10 (Oct), 2026-11 (Nov)...

                  const pastMonths = Object.keys(monthMap)
                    .filter(k => k < currentMonthKey)
                    .sort((a, b) => b.localeCompare(a)); // 2026-08 (Aug), 2026-06 (Jun)...

                  const unscheduledMonths = Object.keys(monthMap).filter(k => k === '9999-99');

                  const allSortedMonthKeys = modalConfig.status === 'Withdrawn'
                    ? Object.keys(monthMap).sort((a, b) => a.localeCompare(b))
                    : [...upcomingOrCurrentMonths, ...pastMonths, ...unscheduledMonths];

                  // Step 3: Within each month, sort courses by:
                  // 1) Start Date (earliest first)
                  // 2) End Date (earlier completion first, e.g. 1-3 Sept before 1-5 Sept)
                  // 3) Start Time (earliest daily start time first, e.g. 09:00 AM before 09:30 AM)
                  // 4) Highest Registration Count (if exact same date span and time)
                  // 5) Alphabetical fallback
                  const structuredMonthsAll = allSortedMonthKeys.map(mKey => {
                    const mObj = monthMap[mKey];
                    const courseList = Object.values(mObj.courses);

                    const sortedCourses = courseList.sort((cA, cB) => {
                      const startA = cA.startDate ? new Date(cA.startDate).getTime() : 0;
                      const startB = cB.startDate ? new Date(cB.startDate).getTime() : 0;

                      // Level 1: Course Start Date (earliest first)
                      if (startA !== startB) {
                        return startA - startB;
                      }

                      // Level 2: Course End Date (1-3 Sept finishes before 1-5 Sept)
                      const endA = cA.endDate ? new Date(cA.endDate).getTime() : 0;
                      const endB = cB.endDate ? new Date(cB.endDate).getTime() : 0;
                      if (endA !== endB) {
                        return endA - endB;
                      }

                      // Level 3: Course Start Time (09:00 AM before 09:30 AM)
                      const timeA = parseTimeToMinutes(cA.startTime);
                      const timeB = parseTimeToMinutes(cB.startTime);
                      if (timeA !== timeB) {
                        return timeA - timeB;
                      }

                      // Level 4: Highest Registration Count
                      const countA = cA.items.length;
                      const countB = cB.items.length;
                      if (countB !== countA) {
                        return countB - countA;
                      }

                      // Level 5: Alphabetical fallback
                      return cA.courseName.localeCompare(cB.courseName);
                    });

                    // Filter courses and items according to active search
                    const filteredCourses = sortedCourses.map(c => {
                      const isCourseMatch = (modalSearchScope === 'course' || modalSearchScope === 'all') &&
                        q && c.courseName.toLowerCase().includes(q);
                      const isMonthMatch = (modalSearchScope === 'all') &&
                        q && mObj.info.fullLabel.toLowerCase().includes(q);

                      const matchingItems = c.items.filter(item =>
                        itemMatchesScope(item, c.courseName, mObj.info.fullLabel)
                      );

                      return {
                        ...c,
                        allItems: c.items,
                        matchingItems: (isCourseMatch || isMonthMatch) ? c.items : matchingItems,
                        isCourseMatch,
                        isMonthMatch
                      };
                    }).filter(c => {
                      if (!q) return true;
                      return c.isCourseMatch || c.isMonthMatch || c.matchingItems.length > 0;
                    });

                    const totalMonthRegistrations = filteredCourses.reduce((sum, c) => sum + (q ? c.matchingItems.length : c.allItems.length), 0);

                    const isUpcomingOrCurrent = mKey >= currentMonthKey && mKey !== '9999-99';
                    const isPast = mKey < currentMonthKey;

                    return {
                      key: mKey,
                      monthInfo: mObj.info,
                      courses: filteredCourses,
                      totalRegistrations: totalMonthRegistrations,
                      isUpcomingOrCurrent,
                      isPast
                    };
                  }).filter(m => m.courses.length > 0);

                  // Calculate total counts per tab
                  const totalUpcomingRegistrations = structuredMonthsAll
                    .filter(m => m.isUpcomingOrCurrent)
                    .reduce((sum, m) => sum + m.totalRegistrations, 0);

                  const totalPastRegistrations = structuredMonthsAll
                    .filter(m => m.isPast)
                    .reduce((sum, m) => sum + m.totalRegistrations, 0);

                  // Filter visible months by active tab
                  const visibleMonths = structuredMonthsAll.filter(m => {
                    if (modalConfig.status === 'Withdrawn') return true;
                    if (modalTimeframeTab === 'past') return m.isPast;
                    return m.isUpcomingOrCurrent;
                  });

                  const visibleCoursesCount = visibleMonths.reduce((sum, m) => sum + m.courses.length, 0);
                  const visibleRegistrationsCount = visibleMonths.reduce((sum, m) => sum + m.totalRegistrations, 0);

                  const getStatusNoun = (count = 1) => {
                    if (modalConfig.status === 'Withdrawn') {
                      return count === 1 ? 'Withdrawal' : 'Withdrawals';
                    }
                    if (modalConfig.status === 'Cancelled') {
                      return count === 1 ? 'Cancellation' : 'Cancellations';
                    }
                    return count === 1 ? 'Registration' : 'Registrations';
                  };

                  const getMatchSummaryText = () => {
                    const nounWord = getStatusNoun(visibleRegistrationsCount).toLowerCase();
                    if (visibleRegistrationsCount > 0) {
                      const recordWord = visibleRegistrationsCount === 1 ? 'record' : 'records';
                      return `${visibleRegistrationsCount} ${recordWord} found (${visibleRegistrationsCount} ${nounWord})`;
                    }
                    if (visibleCoursesCount > 0) {
                      const courseWord = visibleCoursesCount === 1 ? 'course' : 'courses';
                      return `${visibleCoursesCount} ${courseWord} found (0 ${nounWord})`;
                    }
                    return '0 records found';
                  };

                  const renderSearchBar = () => (
                    <div className="modal-search-wrapper" style={{ marginBottom: '1.25rem' }}>
                      <div className="unified-search-box">
                        <select
                          value={modalSearchScope}
                          onChange={(e) => setModalSearchScope(e.target.value)}
                          className="unified-search-select"
                          title="Select search category"
                        >
                          <option value="all">All Fields</option>
                          <option value="name">Learner Name</option>
                          <option value="email">Email</option>
                          <option value="organisation">Organisation</option>
                          <option value="course">Course</option>
                        </select>
                        <div className="unified-search-input-wrap">
                          <span className="unified-search-icon">
                            <SvgIcon name="search" size={14} />
                          </span>
                          <input
                            type="text"
                            placeholder={
                              modalSearchScope === 'name' ? "Search learner name (e.g. Tan, John)..." :
                              modalSearchScope === 'email' ? "Search by learner email..." :
                                modalSearchScope === 'course' ? "Search by course title..." :
                                  modalSearchScope === 'organisation' ? "Search by organisation name..." :
                                    "Search by learner name, email, course, month, or organisation..."
                            }
                            value={modalSearch}
                            onChange={(e) => setModalSearch(e.target.value)}
                            className="unified-search-input"
                          />
                          {modalSearch && (
                            <button
                              type="button"
                              onClick={() => setModalSearch('')}
                              className="unified-search-clear"
                              title="Clear search"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Timeframe Filter Tabs (Hidden for Withdrawn since only next 15 days are displayed) */}
                      {modalConfig.status !== 'Withdrawn' && (
                        <div className="modal-timeframe-tabs">
                          <button
                            type="button"
                            className={`modal-timeframe-tab ${modalTimeframeTab === 'upcoming' ? 'active' : ''}`}
                            onClick={() => setModalTimeframeTab('upcoming')}
                          >
                            Current & Upcoming ({totalUpcomingRegistrations})
                          </button>
                          <button
                            type="button"
                            className={`modal-timeframe-tab ${modalTimeframeTab === 'past' ? 'active' : ''}`}
                            onClick={() => setModalTimeframeTab('past')}
                          >
                            Past Sessions ({totalPastRegistrations})
                          </button>
                        </div>
                      )}

                      {q && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
                          <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: '600' }}>
                            {getMatchSummaryText()}
                          </span>
                        </div>
                      )}
                    </div>
                  );

                  if (visibleMonths.length === 0) {
                    return (
                      <>
                        {renderSearchBar()}
                        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#64748b', fontSize: '0.95rem' }}>
                          <p style={{ margin: '0 0 0.75rem 0', fontWeight: '600' }}>
                            {q
                              ? "No registrations match your search criteria in this view."
                              : modalConfig.status === 'Withdrawn'
                                ? "No withdrawals found for classes starting within the next 15 days."
                                : modalTimeframeTab === 'upcoming'
                                  ? "No current or upcoming registrations found."
                                  : modalTimeframeTab === 'past'
                                    ? "No past registrations found."
                                    : "No registrations found."}
                          </p>
                          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {q && (
                              <button
                                type="button"
                                onClick={() => { setModalSearch(''); setModalSearchScope('all'); }}
                                style={{
                                  padding: '0.4rem 1rem',
                                  backgroundColor: '#e0e7ff',
                                  color: '#4338ca',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.825rem',
                                  fontWeight: '700',
                                  cursor: 'pointer'
                                }}
                              >
                                Reset Search Filters
                              </button>
                            )}
                            {modalTimeframeTab === 'upcoming' && totalPastRegistrations > 0 && (
                              <button
                                type="button"
                                onClick={() => setModalTimeframeTab('past')}
                                style={{
                                  padding: '0.4rem 1rem',
                                  backgroundColor: '#f1f5f9',
                                  color: '#334155',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '6px',
                                  fontSize: '0.825rem',
                                  fontWeight: '700',
                                  cursor: 'pointer'
                                }}
                              >
                                View Past Sessions ({totalPastRegistrations})
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  }

                  return (
                    <>
                      {renderSearchBar()}

                      <div className="modal-monthly-scroll-container">
                        {visibleMonths.map((m) => {
                          const monthLabel = m.monthInfo.fullLabel;
                          const shortMonth = m.monthInfo.shortMonth;
                          const totalReg = m.totalRegistrations;
                          const courseCount = m.courses.length;

                          return (
                            <div key={m.monthInfo.key} className="modal-month-box">
                              {/* Month Box Header */}
                              <div className="modal-month-box-header">
                                <div className="modal-month-header-left">
                                  <span className="modal-month-pill">
                                    <SvgIcon name="calendar" size={14} />
                                    <span>{shortMonth}</span>
                                  </span>
                                  <span className="modal-month-fullname">{monthLabel}</span>
                                </div>
                                <div className="modal-month-header-right">
                                  <span className="modal-month-summary-badge">
                                    {courseCount} {courseCount === 1 ? 'Course' : 'Courses'} • {totalReg} {getStatusNoun(totalReg)}
                                  </span>
                                </div>
                              </div>

                              {/* Month Box Body - Courses within this month */}
                              <div className="modal-month-box-body">
                                {m.courses.map((course) => {
                                  const allItems = course.allItems;
                                  const displayedItems = course.matchingItems;
                                  const isCollapsed = q ? false : !expandedCourses[course.groupKey];

                                  const badgeText = !q
                                    ? `${allItems.length} ${getStatusNoun(allItems.length)}`
                                    : displayedItems.length === allItems.length
                                      ? `${displayedItems.length} ${getStatusNoun(displayedItems.length)}`
                                      : `${displayedItems.length} of ${allItems.length} matching`;

                                  const datesRange = course.startDate
                                    ? `${formatToDMY(course.startDate)} - ${formatToDMY(course.endDate)}`
                                    : null;

                                  return (
                                    <div key={course.groupKey} className="modal-course-accordion-item">
                                      <div
                                        className="modal-course-accordion-header"
                                        onClick={() => toggleCourseExpand(course.groupKey)}
                                      >
                                        <div className="modal-course-header-left">
                                          <div className="modal-course-title-block">
                                            <span className="modal-course-title">{course.courseName}</span>
                                            {datesRange && (
                                              <span className="modal-course-schedule-tag">
                                                {datesRange}{course.startTime ? ` (${course.startTime.toLowerCase()})` : ''}
                                              </span>
                                            )}
                                          </div>
                                          <span
                                            className="modal-course-badge"
                                            style={{
                                              backgroundColor: allItems.length === 0 ? '#f1f5f9' : '#e0e7ff',
                                              color: allItems.length === 0 ? '#64748b' : '#4338ca'
                                            }}
                                          >
                                            {badgeText}
                                          </span>
                                        </div>
                                        <div className="modal-course-header-right">
                                          <span className="accordion-plus-minus">
                                            {isCollapsed ? '+' : '–'}
                                          </span>
                                        </div>
                                      </div>

                                      {!isCollapsed && (
                                        <div className="modal-course-accordion-body">
                                          {allItems.length === 0 ? (
                                            <div style={{ padding: '1.75rem 1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                                              {modalConfig.status === 'Approved'
                                                ? "No approved registrations yet for this scheduled course."
                                                : modalConfig.status === 'Withdrawn'
                                                  ? "No withdrawals yet for this scheduled course."
                                                  : modalConfig.status === 'Cancelled'
                                                    ? "No cancellations yet for this scheduled course."
                                                    : `No ${modalConfig.status ? modalConfig.status.toLowerCase() : ''} registrations yet for this scheduled course.`}
                                            </div>
                                          ) : displayedItems.length === 0 ? (
                                            <div style={{ padding: '1.75rem 1.25rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
                                              No {getStatusNoun(0).toLowerCase()} match your search in this course.
                                            </div>
                                          ) : (
                                            <div className="admin-table-wrapper" style={{ margin: 0 }}>
                                              <table className="admin-table">
                                                <thead>
                                                  <tr>
                                                    <th>Learner</th>
                                                    <th>Schedule & Time</th>
                                                    <th>Organisation & Sector</th>
                                                    <th>Registration Date</th>
                                                    {modalConfig.status === 'Approved' && <th>Approval Date</th>}
                                                    {modalConfig.status === 'Withdrawn' && <th>Withdrawal Date</th>}
                                                    {modalConfig.status === 'Cancelled' && <th>Cancellation Date</th>}
                                                    <th>Status</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {displayedItems.map(reg => {
                                                    const formattedRegDate = formatDateVal(reg.RegistrationDate);
                                                    const formattedApprovalDate = formatDateVal(reg.ApprovalDate || reg.RegistrationDate);
                                                    const formattedWithdrawalDate = formatDateVal(reg.WithdrawalDate || reg.ApprovalDate || reg.RegistrationDate);
                                                    const formattedCancellationDate = formatDateVal(reg.CancellationDate || reg.WithdrawalDate || reg.ApprovalDate || reg.RegistrationDate);

                                                    return (
                                                      <tr key={reg.RegistrationID}>
                                                        <td>
                                                          <div className="learner-avatar-cell">
                                                            <div className="avatar-circle">
                                                              {reg.LearnerName ? reg.LearnerName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U'}
                                                            </div>
                                                            <div className="learner-details">
                                                              <span className="learner-name">{reg.LearnerName}</span>
                                                              <span className="learner-email">{reg.LearnerEmail}</span>
                                                            </div>
                                                          </div>
                                                        </td>
                                                        <td>
                                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
                                                              {formatToDMY(reg.StartDate)} - {formatToDMY(reg.EndDate)}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                              {reg.StartTime ? reg.StartTime.toLowerCase() : ''}
                                                            </span>
                                                          </div>
                                                        </td>
                                                        <td>
                                                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <span style={{ fontSize: '12px', fontWeight: '500', color: '#334155' }}>
                                                              {reg.OrganisationName || 'N/A'}
                                                            </span>
                                                            <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                                              Sector: {reg.Sector || 'N/A'}
                                                            </span>
                                                          </div>
                                                        </td>
                                                        <td>
                                                          <span className="datetime-cell">
                                                            {formattedRegDate}
                                                          </span>
                                                        </td>
                                                        {modalConfig.status === 'Approved' && (
                                                          <td>
                                                            <span className="datetime-cell" style={{ fontWeight: '600', color: '#059669' }}>
                                                              {formattedApprovalDate}
                                                            </span>
                                                          </td>
                                                        )}
                                                        {modalConfig.status === 'Withdrawn' && (
                                                          <td>
                                                            <span className="datetime-cell" style={{ fontWeight: '600', color: '#ea580c' }}>
                                                              {formattedWithdrawalDate}
                                                            </span>
                                                          </td>
                                                        )}
                                                        {modalConfig.status === 'Cancelled' && (
                                                          <td>
                                                            <span className="datetime-cell" style={{ fontWeight: '600', color: '#dc2626' }}>
                                                              {formattedCancellationDate}
                                                            </span>
                                                          </td>
                                                        )}
                                                        <td>
                                                          <span className={`sla-cell-badge ${reg.Status === 'Approved' ? 'sla-success' : reg.Status === 'Withdrawn' ? 'sla-warning' : 'sla-danger'}`}>
                                                            {reg.Status === 'Withdrawn' ? 'Withdrawn' : reg.Status === 'Approved' ? 'Approved' : 'Cancelled'}
                                                          </span>
                                                        </td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAccountModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCreateAccountModal(false); }}>
          <div className="modal-content" style={{ maxWidth: '520px', width: '92%' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="user" size={18} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>Create New Staff/Trainer Account</h2>
                  <span style={{ fontSize: '0.78rem', color: '#c7d2fe' }}>Create and send login credentials for a new admin or trainer</span>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowCreateAccountModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleCreateAccount} style={{ padding: '1.5rem 1.75rem' }}>
              {createError && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '0.65rem 0.85rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                  {createError}
                </div>
              )}

              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#334155', marginBottom: '0.45rem', textAlign: 'center' }}>
                  Role & Account Type
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isEditor ? 3 : 4}, 1fr)`, gap: '8px', background: '#f8fafc', padding: '6px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                  {[
                    ...(!isEditor ? [{ role: 'Admin', label: 'Admin' }] : []),
                    { role: 'Editor', label: 'Editor' },
                    { role: 'Report Viewer', label: 'Report Viewer' },
                    { role: 'Trainer', label: 'Trainer' }
                  ].map(item => {
                    const isSelected = newAccountData.role === item.role;
                    return (
                      <button
                        key={item.role}
                        type="button"
                        onClick={() => setNewAccountData(prev => ({ ...prev, role: item.role }))}
                        style={{
                          padding: '0.55rem 0.5rem',
                          borderRadius: '8px',
                          fontSize: '0.825rem',
                          fontWeight: '700',
                          border: isSelected ? '1px solid #4f46e5' : '1px solid transparent',
                          backgroundColor: isSelected ? '#4f46e5' : 'transparent',
                          color: isSelected ? '#ffffff' : '#64748b',
                          boxShadow: isSelected ? '0 2px 6px rgba(79, 70, 229, 0.25)' : 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                  Full Name
                </label>
                <input
                  type="text"
                  className="modal-search-input"
                  style={{ marginBottom: 0 }}
                  placeholder="e.g. Sarah Lim"
                  value={newAccountData.fullName}
                  onChange={(e) => setNewAccountData(prev => ({ ...prev, fullName: e.target.value }))}
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  className="modal-search-input"
                  style={{ marginBottom: 0 }}
                  placeholder="e.g. sarah@blackberry.com"
                  value={newAccountData.email}
                  onChange={(e) => setNewAccountData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#334155', marginBottom: '0.35rem' }}>
                  Username
                </label>
                <input
                  type="text"
                  className="modal-search-input"
                  style={{ marginBottom: 0 }}
                  placeholder="e.g. sarahlim"
                  value={newAccountData.username}
                  onChange={(e) => setNewAccountData(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ position: 'relative', marginBottom: '0.35rem' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#334155', textAlign: 'center', margin: 0 }}>
                    Temporary Password
                  </label>
                  <button
                    type="button"
                    onClick={generateAutoPassword}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      background: 'none',
                      border: 'none',
                      color: '#4f46e5',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: 0
                    }}
                  >
                    Auto-generate
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showTempPassword ? 'text' : 'password'}
                    className="modal-search-input"
                    style={{ marginBottom: 0, paddingRight: '2.5rem' }}
                    placeholder="Auto-generate temporary password"
                    value={newAccountData.password}
                    onChange={(e) => setNewAccountData(prev => ({ ...prev, password: e.target.value }))}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowTempPassword(prev => !prev)}
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                    title={showTempPassword ? "Hide password" : "Show password"}
                  >
                    <SvgIcon name={showTempPassword ? 'eyeOff' : 'eye'} size={16} />
                  </button>
                </div>
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block', textAlign: 'center' }}>
                  User will be required to change this password on their 1st login.
                </span>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setShowCreateAccountModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreatingAccount}
                  style={{ padding: '0.6rem 1.2rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: isCreatingAccount ? 'not-allowed' : 'pointer' }}
                >
                  {isCreatingAccount ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Learner Profile Modal */}
      {editingLearner && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEditingLearner(null); }}>
          <div className="modal-content" style={{ maxWidth: '640px', width: '92%', maxHeight: '90vh', overflow: 'visible' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="edit" size={18} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>Edit Learner Profile</h2>
                  <span style={{ fontSize: '0.78rem', color: '#c7d2fe' }}>Update participant profile information</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setEditingLearner(null)}>&times;</button>
            </div>

            <form onSubmit={handleSaveLearnerProfile} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', textAlign: 'left' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label">Full Name</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editLearnerForm.fullName}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                  />
                </div>
                <div className="modal-form-field">
                  <label className="modal-label">Username</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editLearnerForm.username}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, username: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label">Email Address</label>
                  <input
                    type="email"
                    className="modal-form-input"
                    value={editLearnerForm.email}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="modal-form-field">
                  <label className="modal-label">Contact Number</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editLearnerForm.contact}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, contact: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label">Position / Job Title</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editLearnerForm.position}
                    onChange={(e) => setEditLearnerForm(prev => ({ ...prev, position: e.target.value }))}
                  />
                </div>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={adminOrgDropdownRef}>
                  <label className="modal-label">Organisation Name</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    placeholder="Search or enter organisation..."
                    value={isAdminOrgDropdownOpen ? adminOrgSearchTerm : (editLearnerForm.organisationName || adminOrgSearchTerm)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAdminOrgSearchTerm(val);
                      setIsAdminOrgDropdownOpen(true);
                      setEditLearnerForm(prev => ({ ...prev, organisationName: val }));

                      // Check for immediate exact or acronym match to auto-set sector
                      if (val.trim()) {
                        const cleanTerm = val.trim().toLowerCase();
                        const matched = adminOrgList.find(o =>
                          (o.OrganisationName && o.OrganisationName.toLowerCase() === cleanTerm) ||
                          (o.OrganisationName && o.OrganisationName.toLowerCase().includes(`(${cleanTerm})`))
                        );
                        if (matched && matched.Sector) {
                          setEditLearnerForm(prev => ({ ...prev, sector: matched.Sector }));
                        }
                      }
                    }}
                    onFocus={() => {
                      setAdminOrgSearchTerm(editLearnerForm.organisationName || '');
                      setIsAdminOrgDropdownOpen(true);
                    }}
                  />
                  {isAdminOrgDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        zIndex: 9999
                      }}
                    >
                      {(() => {
                        const term = (adminOrgSearchTerm || '').trim().toLowerCase();
                        let filteredOrgs = [];

                        if (term) {
                          const words = term.split(/\s+/).filter(Boolean);

                          filteredOrgs = adminOrgList
                            .map(org => {
                              const name = (org.OrganisationName || '').toLowerCase();
                              const domain = (org.EmailDomain || '').toLowerCase();
                              
                              // Check name direct inclusion or domain inclusion
                              const nameMatch = name.includes(term);
                              const domainMatch = domain && (domain.includes(term) || term.includes(domain.split('.')[0]));
                              
                              // Check if all search words are present in name or domain
                              const allWordsMatch = words.every(w => name.includes(w) || (domain && domain.includes(w)));
                              
                              // Check acronym/parentheses match e.g. "(jpj)" or "(maybank)"
                              const acronymMatch = name.includes(`(${term})`) || name.startsWith(term);

                              if (!nameMatch && !domainMatch && !allWordsMatch) return null;

                              let score = 0;
                              if (acronymMatch) score += 60;
                              if (nameMatch) score += 40;
                              if (domainMatch) score += 35;
                              if (name.startsWith(term)) score += 25;
                              if (allWordsMatch) score += 15;

                              return { org, score };
                            })
                            .filter(Boolean)
                            .sort((a, b) => b.score - a.score)
                            .map(item => item.org);
                        } else {
                          filteredOrgs = adminOrgList.slice(0, 40);
                        }

                        const handleSelectOrg = (org) => {
                          setEditLearnerForm(prev => ({
                            ...prev,
                            organisationName: org.OrganisationName,
                            sector: org.Sector || prev.sector || 'Government'
                          }));
                          setAdminOrgSearchTerm(org.OrganisationName);
                          setIsAdminOrgDropdownOpen(false);
                        };

                        if (filteredOrgs.length === 0) {
                          return (
                            <div style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#64748b' }}>
                              No organisations found matching &quot;{adminOrgSearchTerm}&quot;
                            </div>
                          );
                        }

                        return (
                          <>
                            {filteredOrgs.slice(0, 60).map(org => (
                              <div
                                key={org.OrganisationID}
                                style={{
                                  padding: '0.65rem 1rem',
                                  fontSize: '0.85rem',
                                  color: '#1e293b',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  gap: '0.75rem',
                                  background: editLearnerForm.organisationName === org.OrganisationName ? '#f0fdf4' : 'transparent',
                                  transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (editLearnerForm.organisationName !== org.OrganisationName) {
                                    e.currentTarget.style.backgroundColor = '#f8fafc';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (editLearnerForm.organisationName !== org.OrganisationName) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleSelectOrg(org);
                                }}
                              >
                                <span style={{ fontWeight: 500, flex: 1, textAlign: 'left' }}>{org.OrganisationName}</span>
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  flexShrink: 0,
                                  background: org.Sector === 'Government' ? '#dcfce7' : org.Sector === 'Private' ? '#e0e7ff' : '#fef3c7',
                                  color: org.Sector === 'Government' ? '#15803d' : org.Sector === 'Private' ? '#4338ca' : '#b45309'
                                }}>
                                  {org.Sector}
                                </span>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={adminGenderDropdownRef}>
                  <label className="modal-label">Gender</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isAdminGenderDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isAdminGenderDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isAdminGenderDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsAdminGenderDropdownOpen(prev => !prev)}
                  >
                    <span style={{ color: editLearnerForm.gender ? '#0f172a' : '#64748b' }}>
                      {editLearnerForm.gender || 'Select Gender'}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isAdminGenderDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isAdminGenderDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                        overflow: 'hidden'
                      }}
                    >
                      {['Male', 'Female', 'Prefer not to say'].map(opt => (
                        <div
                          key={opt}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: editLearnerForm.gender === opt ? '#f0fdf4' : 'transparent',
                            fontWeight: editLearnerForm.gender === opt ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editLearnerForm.gender !== opt) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (editLearnerForm.gender !== opt) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, gender: opt }));
                            setIsAdminGenderDropdownOpen(false);
                          }}
                        >
                          <span>{opt}</span>
                          {editLearnerForm.gender === opt && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-form-field" style={{ position: 'relative' }} ref={adminSectorDropdownRef}>
                  <label className="modal-label">Sector</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isAdminSectorDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isAdminSectorDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isAdminSectorDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsAdminSectorDropdownOpen(prev => !prev)}
                  >
                    <span style={{ color: editLearnerForm.sector ? '#0f172a' : '#64748b' }}>
                      {editLearnerForm.sector || 'Select Sector'}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isAdminSectorDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isAdminSectorDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                        overflow: 'hidden'
                      }}
                    >
                      {['Government', 'Private', 'Student'].map(sec => (
                        <div
                          key={sec}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: editLearnerForm.sector === sec ? '#f0fdf4' : 'transparent',
                            fontWeight: editLearnerForm.sector === sec ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (editLearnerForm.sector !== sec) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if (editLearnerForm.sector !== sec) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, sector: sec }));
                            setIsAdminSectorDropdownOpen(false);
                          }}
                        >
                          <span>{sec}</span>
                          {editLearnerForm.sector === sec && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field" style={{ position: 'relative' }} ref={adminExpDropdownRef}>
                  <label className="modal-label">Years of IT Experience</label>
                  <div
                    className="modal-form-input"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      userSelect: 'none',
                      backgroundColor: isAdminExpDropdownOpen ? '#ffffff' : '#f8fafc',
                      borderColor: isAdminExpDropdownOpen ? '#4f46e5' : '#cbd5e1',
                      boxShadow: isAdminExpDropdownOpen ? '0 0 0 3px rgba(79, 70, 229, 0.15)' : 'none'
                    }}
                    onClick={() => setIsAdminExpDropdownOpen(prev => !prev)}
                  >
                    <span style={{ color: editLearnerForm.experienceYears ? '#0f172a' : '#64748b' }}>
                      {editLearnerForm.experienceYears || 'Not Applicable'}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#64748b"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isAdminExpDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                  {isAdminExpDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '8px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                        maxHeight: '135px',
                        overflowY: 'auto',
                        zIndex: 9999
                      }}
                    >
                      {['Not Applicable', 'Less than 1 Years', 'Between 1 to 5 Years', 'More than 5 Years'].map(opt => (
                        <div
                          key={opt}
                          style={{
                            padding: '0.65rem 1rem',
                            fontSize: '0.85rem',
                            color: '#1e293b',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: (editLearnerForm.experienceYears || 'Not Applicable') === opt ? '#f0fdf4' : 'transparent',
                            fontWeight: (editLearnerForm.experienceYears || 'Not Applicable') === opt ? 600 : 400,
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if ((editLearnerForm.experienceYears || 'Not Applicable') !== opt) e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            if ((editLearnerForm.experienceYears || 'Not Applicable') !== opt) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setEditLearnerForm(prev => ({ ...prev, experienceYears: opt }));
                            setIsAdminExpDropdownOpen(false);
                          }}
                        >
                          <span>{opt}</span>
                          {(editLearnerForm.experienceYears || 'Not Applicable') === opt && (
                            <span style={{ color: '#15803d', fontSize: '0.85rem', fontWeight: 'bold' }}>✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1.25rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.25rem', background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onClick={() => setEditingLearner(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.6rem 1.4rem', background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)' }}
                >
                  Save Learner Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CREATE MASTER COURSE MODAL ── */}
      {isMasterCourseModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsMasterCourseModalOpen(false)} style={{ overflowX: 'hidden' }}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px', width: '90%', overflowX: 'hidden', boxSizing: 'border-box', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)', color: '#ffffff', padding: '1.25rem 1.75rem', boxSizing: 'border-box' }}>
              <div className="modal-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
                  <SvgIcon name="book" size={20} />
                </div>
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0, textAlign: 'left' }}>
                    {editingMasterCourse ? 'Edit Master Course' : 'Create Master Course'}
                  </h3>
                  <p style={{ color: '#c7d2fe', fontSize: '0.8rem', margin: '2px 0 0 0', textAlign: 'left' }}>
                    {editingMasterCourse ? 'Update course certification, details, delivery mode & email template.' : 'Define course certification, details, delivery mode (Online/Onsite) & email template.'}
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff', fontSize: '1.5rem', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsMasterCourseModalOpen(false)}>&times;</button>
            </div>

            {/* Modal Body & Form */}
            <form onSubmit={handleSaveMasterCourse} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', overflowX: 'hidden', maxHeight: '70vh', background: '#ffffff', textAlign: 'left', boxSizing: 'border-box', width: '100%' }}>

                {/* ── Section 1: Course Info ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="book" size={16} />
                  <span>Course Basic Information</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Certification Body *
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={masterCertBody}
                      onChange={e => setMasterCertBody(e.target.value)}
                      placeholder="e.g. ISC2, CompTIA, BlackBerry"
                      required
                    />
                  </div>

                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Course Title / Name *
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={masterCourseTitle}
                      onChange={e => setMasterCourseTitle(e.target.value)}
                      placeholder="e.g. Certified in Cybersecurity (CC)"
                      required
                    />
                  </div>
                </div>

                {/* Class Level Radio Selection */}
                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.5rem', textAlign: 'left' }}>
                    Class Level *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.75rem' }}>
                    {[
                      { label: 'Entry', value: 'Entry' },
                      { label: 'Intermediate', value: 'Intermediate' },
                      { label: 'Advanced', value: 'Advanced' },
                      { label: 'Awareness', value: 'Awareness' }
                    ].map((lvl) => (
                      <label
                        key={lvl.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.65rem 0.85rem',
                          borderRadius: '8px',
                          border: masterLevel === lvl.value ? '2px solid #4f46e5' : '1px solid #cbd5e1',
                          background: masterLevel === lvl.value ? '#eef2ff' : '#ffffff',
                          color: masterLevel === lvl.value ? '#4338ca' : '#334155',
                          fontWeight: masterLevel === lvl.value ? '700' : '600',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <input
                          type="radio"
                          name="masterClassLevel"
                          value={lvl.value}
                          checked={masterLevel === lvl.value}
                          onChange={() => setMasterLevel(lvl.value)}
                          style={{ accentColor: '#4f46e5', cursor: 'pointer' }}
                        />
                        <span>{lvl.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Delivery Mode Selection: Online vs Onsite */}
                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Delivery Mode *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => handleMasterDeliveryModeChange('Onsite')}
                      style={{
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        border: masterDeliveryMode === 'Onsite' ? '2px solid #4f46e5' : '1px solid #cbd5e1',
                        background: masterDeliveryMode === 'Onsite' ? '#eef2ff' : '#ffffff',
                        color: masterDeliveryMode === 'Onsite' ? '#4338ca' : '#475569',
                        fontWeight: '700',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span>Onsite (CCoE Cyberjaya Physical)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMasterDeliveryModeChange('Online')}
                      style={{
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        border: masterDeliveryMode === 'Online' ? '2px solid #4f46e5' : '1px solid #cbd5e1',
                        background: masterDeliveryMode === 'Online' ? '#eef2ff' : '#ffffff',
                        color: masterDeliveryMode === 'Online' ? '#4338ca' : '#475569',
                        fontWeight: '700',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span>Online (Virtual Live Classroom)</span>
                    </button>
                  </div>
                </div>

                {/* Awarded Completion Certificate Templates */}
                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Awarded Completion Certificate(s) *
                  </label>
                  <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.65rem' }}>
                      Select which certificate template(s) will be automatically generated upon 100% course attendance:
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.65rem' }}>
                      {(availableCertTemplates.length > 0 ? availableCertTemplates : [
                        { TemplateCode: 'MCMC', TemplateName: 'BlackBerry CCoE Standard' },
                        { TemplateCode: 'RCC', TemplateName: 'Rogers Cyber Catalyst Joint' }
                      ]).map((t) => {
                        const isSelected = masterAwardedTemplates.includes(t.TemplateCode);
                        return (
                          <label
                            key={t.TemplateCode}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '6px',
                              border: isSelected ? '1.5px solid #4f46e5' : '1px solid #cbd5e1',
                              background: isSelected ? '#eef2ff' : '#ffffff',
                              cursor: 'pointer',
                              fontSize: '0.825rem',
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? '#3730a3' : '#334155',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMasterAwardedTemplates([...masterAwardedTemplates, t.TemplateCode]);
                                } else {
                                  if (masterAwardedTemplates.length > 1) {
                                    setMasterAwardedTemplates(masterAwardedTemplates.filter(c => c !== t.TemplateCode));
                                  } else {
                                    setToast({ type: 'warning', msg: 'Course must award at least one certificate template.' });
                                  }
                                }
                              }}
                              style={{ accentColor: '#4f46e5', cursor: 'pointer' }}
                            />
                            <span style={{ flex: 1 }}>{t.TemplateName}</span>
                            <span style={{ fontSize: '0.7rem', padding: '1px 5px', borderRadius: '4px', background: '#e0e7ff', color: '#4338ca', fontWeight: 800 }}>
                              {t.TemplateCode}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <RichTextEditor
                  label="Course Overview / Description"
                  value={masterCourseDesc}
                  onChange={setMasterCourseDesc}
                  placeholder="e.g. 3 days training. Including Lunch, e-Student guide and exam voucher."
                />

                {/* ── Section 2: Fee & Payment Settings ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="dollarSign" size={16} />
                  <span>Course Fee & Payment Link</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Course Fee
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={masterFee}
                      onChange={e => setMasterFee(e.target.value)}
                      placeholder="e.g. RM 2,100"
                    />
                  </div>

                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Payment Link URL
                    </label>
                    <input
                      type="url"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={masterPaymentLink}
                      onChange={e => setMasterPaymentLink(e.target.value)}
                      placeholder="https://buy.stripe.com/..."
                    />
                  </div>
                </div>

                {/* ── Section 3: Payment Email Template ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="mail" size={16} />
                  <span>Payment Email Template ({masterDeliveryMode === 'Online' ? 'Online Virtual Mode' : 'Onsite Physical Mode'})</span>
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Payment Email Subject
                  </label>
                  <input
                    type="text"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                    value={masterPaymentSubject}
                    onChange={e => setMasterPaymentSubject(e.target.value)}
                    placeholder="{CourseName} Course Payment"
                  />
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.3rem', textAlign: 'left' }}>
                    Payment Email Body Template
                  </label>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: '#64748b', textAlign: 'left' }}>
                    Available Placeholders: <code>{"{LearnerName}"}</code>, <code>{"{CourseName}"}</code>, <code>{"{Fee}"}</code>, <code>{"{PaymentLink}"}</code>, <code>{"{Venue}"}</code>
                  </p>
                  <textarea
                    rows="8"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', minHeight: '140px' }}
                    value={masterPaymentBody}
                    onChange={e => setMasterPaymentBody(e.target.value)}
                  />
                </div>

              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setIsMasterCourseModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingMasterCourse}
                  style={{ padding: '0.6rem 1.4rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: savingMasterCourse ? 'not-allowed' : 'pointer' }}
                >
                  {savingMasterCourse ? 'Saving Course...' : (editingMasterCourse ? 'Update Master Course' : 'Save Master Course')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CREATE COURSE SESSION MODAL ── */}
      {isCreateSessionModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsCreateSessionModalOpen(false)} style={{ overflowX: 'hidden' }}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px', width: '90%', overflowX: 'hidden', boxSizing: 'border-box', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)', color: '#ffffff', padding: '1.25rem 1.75rem', boxSizing: 'border-box' }}>
              <div className="modal-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
                  <SvgIcon name="calendar" size={20} />
                </div>
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0, textAlign: 'left' }}>
                    Create Class
                  </h3>
                  <p style={{ color: '#a7f3d0', fontSize: '0.8rem', margin: '2px 0 0 0', textAlign: 'left' }}>
                    Select a course, schedule class dates, assign a trainer, and set capacity.
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff', fontSize: '1.5rem', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsCreateSessionModalOpen(false)}>&times;</button>
            </div>

            {/* Modal Body & Form */}
            <form onSubmit={handleSaveSession} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', overflowX: 'hidden', maxHeight: '70vh', background: '#ffffff', textAlign: 'left', boxSizing: 'border-box', width: '100%' }}>

                {/* ── Section 1: Course Selection (Searchable Dropdown) ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#047857', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="book" size={16} />
                  <span>Select Master Course *</span>
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Course Name / Certification *
                  </label>
                  <div className="trainer-dropdown-wrapper" ref={courseDropdownRef} style={{ position: 'relative' }}>
                    <div className="trainer-dropdown-input-wrap">
                      <span className="trainer-dropdown-icon">
                        <SvgIcon name="book" size={15} />
                      </span>
                      <input
                        type="text"
                        className="trainer-dropdown-input"
                        placeholder="Search and select available course..."
                        value={courseSearchTerm}
                        onChange={(e) => {
                          setCourseSearchTerm(e.target.value);
                          setIsCourseDropdownOpen(true);
                          if (selectedCourseId) {
                            setSelectedCourseId('');
                          }
                        }}
                        onFocus={() => {
                          setIsCourseDropdownOpen(true);
                          if (masterCoursesList.length === 0) {
                            fetchMasterCourses();
                          }
                        }}
                        required={!selectedCourseId}
                      />
                      {(courseSearchTerm || selectedCourseId) && (
                        <button
                          type="button"
                          className="trainer-dropdown-clear-btn"
                          onClick={() => {
                            setSelectedCourseId('');
                            setCourseSearchTerm('');
                            setIsCourseDropdownOpen(true);
                          }}
                          title="Clear course selection"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {isCourseDropdownOpen && (
                      <div className="trainer-dropdown-list" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        {(() => {
                          const cleanQ = courseSearchTerm.trim().toLowerCase();
                          const filteredCourses = masterCoursesList.filter(c => {
                            if (!cleanQ) return true;
                            const name = (c.CourseName || '').toLowerCase();
                            const cert = (c.CertificationBody || '').toLowerCase();
                            const full = `${cert} ${name}`.toLowerCase();
                            return full.includes(cleanQ) || name.includes(cleanQ) || cert.includes(cleanQ);
                          });

                          if (filteredCourses.length === 0) {
                            return (
                              <div className="trainer-dropdown-item no-results" style={{ padding: '0.75rem 1rem', color: '#64748b' }}>
                                No matching courses found.
                              </div>
                            );
                          }

                          return filteredCourses.map(c => (
                            <div
                              key={c.CourseID}
                              className={`trainer-dropdown-item ${String(selectedCourseId) === String(c.CourseID) ? 'selected' : ''}`}
                              onClick={() => handleSelectCourseForSession(c)}
                              style={{ padding: '0.65rem 1rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#4f46e5', marginRight: '6px' }}>
                                      [{c.CertificationBody || 'Course'}]
                                    </span>
                                    <span style={{ fontWeight: '600', color: '#0f172a' }}>
                                      {c.CourseName}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                                    Class Level: <strong style={{ color: '#4338ca' }}>{c.Level || 'Entry'}</strong>
                                  </div>
                                </div>
                                <span style={{
                                  fontSize: '0.7rem',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontWeight: '700',
                                  backgroundColor: c.DeliveryMode === 'Online' ? '#eff6ff' : '#f0fdf4',
                                  color: c.DeliveryMode === 'Online' ? '#1d4ed8' : '#15803d',
                                  border: `1px solid ${c.DeliveryMode === 'Online' ? '#bfdbfe' : '#bbf7d0'}`
                                }}>
                                  {c.DeliveryMode || 'Onsite'}
                                </span>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Section 2: Schedule & Timing ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#047857', marginBottom: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SvgIcon name="calendar" size={16} />
                    <span>Class Schedule & Timing</span>
                  </div>
                  {sessionSegments.length > 1 && (
                    <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', fontWeight: '600' }}>
                      {sessionSegments.length} Parts Split Class ({calculateTotalTrainingDays(sessionSegments)} Days Total)
                    </span>
                  )}
                </div>

                {/* Dynamic Session Segments List */}
                {sessionSegments.map((seg, idx) => (
                  <div key={idx} style={{
                    background: sessionSegments.length > 1 ? '#f8fafc' : 'transparent',
                    border: sessionSegments.length > 1 ? '1px solid #e2e8f0' : 'none',
                    borderRadius: '10px',
                    padding: sessionSegments.length > 1 ? '1rem' : '0',
                    marginBottom: '1rem'
                  }}>
                    {sessionSegments.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                            Part {idx + 1}
                          </span>
                          {seg.startDate && seg.endDate && (
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              ({calculateSegmentDays(seg.startDate, seg.endDate)} Training Days)
                            </span>
                          )}
                        </div>
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveSegment(idx, false)}
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                          >
                            ✕ Remove Part
                          </button>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', marginBottom: '0.85rem', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          Start Date *
                        </label>
                        <input
                          type="date"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.startDate}
                          onChange={e => handleSegmentChange(idx, 'startDate', e.target.value, false)}
                          required
                        />
                      </div>

                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          End Date *
                        </label>
                        <input
                          type="date"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.endDate}
                          onChange={e => handleSegmentChange(idx, 'endDate', e.target.value, false)}
                          required
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          Start Time
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.startTime}
                          onChange={e => handleSegmentChange(idx, 'startTime', e.target.value, false)}
                          placeholder="09:00 AM"
                        />
                      </div>

                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          End Time
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.endTime}
                          onChange={e => handleSegmentChange(idx, 'endTime', e.target.value, false)}
                          placeholder="05:00 PM"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Split Part Button */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <button
                    type="button"
                    onClick={() => handleAddSegment(false)}
                    style={{
                      width: '100%',
                      padding: '0.65rem',
                      borderRadius: '8px',
                      border: '1.5px dashed #059669',
                      background: '#f0fdf4',
                      color: '#047857',
                      fontWeight: '600',
                      fontSize: '0.825rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <SvgIcon name="plus" size={15} />
                    <span>{sessionSegments.length === 1 ? '+ Add Split Part (e.g. Part 2 for Week 2)' : `+ Add Another Part (Part ${sessionSegments.length + 1})`}</span>
                  </button>
                  {sessionSegments.length > 1 && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.4rem', textAlign: 'center' }}>
                      Learners register once for all {sessionSegments.length} parts under this unified class.
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Assigned Trainer
                    </label>
                    <div className="trainer-dropdown-wrapper" ref={sessionTrainerDropdownRef}>
                      <div className="trainer-dropdown-input-wrap">
                        <span className="trainer-dropdown-icon">
                          <SvgIcon name="award" size={15} />
                        </span>
                        <input
                          type="text"
                          className="trainer-dropdown-input"
                          placeholder="Search Trainer"
                          value={
                            isSessionTrainerDropdownOpen
                              ? sessionTrainerSearchTerm
                              : (sessionTrainerId
                                  ? (() => {
                                      const sel = trainersList.find(t => String(t.TrainerID) === String(sessionTrainerId));
                                      return sel ? sel.FullName : sessionTrainerSearchTerm;
                                    })()
                                  : sessionTrainerSearchTerm)
                          }
                          onChange={(e) => {
                            setSessionTrainerSearchTerm(e.target.value);
                            setIsSessionTrainerDropdownOpen(true);
                            if (sessionTrainerId) {
                              setSessionTrainerId('');
                            }
                          }}
                          onFocus={() => {
                            setIsSessionTrainerDropdownOpen(true);
                            if (trainersList.length === 0) {
                              fetchTrainers();
                            }
                          }}
                        />
                        {(sessionTrainerSearchTerm || sessionTrainerId) && (
                          <button
                            type="button"
                            className="trainer-dropdown-clear-btn"
                            onClick={() => {
                              setSessionTrainerId('');
                              setSessionTrainerSearchTerm('');
                              setIsSessionTrainerDropdownOpen(true);
                            }}
                            title="Clear selection"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {isSessionTrainerDropdownOpen && (
                        <div className="trainer-dropdown-list">
                          {(() => {
                            const cleanQ = sessionTrainerSearchTerm.trim().toLowerCase();

                            const renderTbaOption = (
                              <div
                                key="tba"
                                className={`trainer-dropdown-item ${!sessionTrainerId ? 'selected' : ''}`}
                                onClick={() => {
                                  setSessionTrainerId('');
                                  setSessionTrainerSearchTerm('');
                                  setIsSessionTrainerDropdownOpen(false);
                                }}
                                style={{ fontWeight: '500', color: '#64748b' }}
                              >
                                <div>-- TBA / No Trainer Assigned --</div>
                              </div>
                            );

                            const filteredTrainers = trainersList.filter(t => {
                              if (!cleanQ) return true;
                              const name = (t.FullName || '').toLowerCase();
                              const email = (t.Email || '').toLowerCase();
                              return name.includes(cleanQ) || email.includes(cleanQ);
                            });

                            return (
                              <>
                                {(!cleanQ || cleanQ.includes('tba')) && renderTbaOption}

                                {filteredTrainers.length > 0 ? (
                                  filteredTrainers.map(t => (
                                    <div
                                      key={t.TrainerID}
                                      className={`trainer-dropdown-item ${String(sessionTrainerId) === String(t.TrainerID) ? 'selected' : ''}`}
                                      onClick={() => {
                                        setSessionTrainerId(String(t.TrainerID));
                                        setSessionTrainerSearchTerm(t.FullName);
                                        setIsSessionTrainerDropdownOpen(false);
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', color: '#0f172a' }}>{t.FullName}</div>
                                    </div>
                                  ))
                                ) : (
                                  <>
                                    <div className="trainer-dropdown-item no-results">No trainers found</div>
                                    {!cleanQ.includes('tba') && renderTbaOption}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ maxWidth: '300px', marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Capacity Limit (Default: 10)
                  </label>
                  <input
                    type="number"
                    min="1"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                    value={sessionMaxCap}
                    onChange={e => setSessionMaxCap(e.target.value)}
                  />
                </div>

              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setIsCreateSessionModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSession}
                  style={{ padding: '0.6rem 1.4rem', background: '#059669', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: savingSession ? 'not-allowed' : 'pointer' }}
                >
                  {savingSession ? 'Scheduling Class...' : 'Schedule Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── EDIT EXISTING COURSE SCHEDULE MODAL (Centered Card Popup) ── */}
      {isCourseModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsCourseModalOpen(false)} style={{ overflowX: 'hidden' }}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px', width: '90%', overflowX: 'hidden', boxSizing: 'border-box', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)', color: '#ffffff', padding: '1.25rem 1.75rem', boxSizing: 'border-box' }}>
              <div className="modal-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
                  <SvgIcon name="book" size={20} />
                </div>
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0, textAlign: 'left' }}>
                    {editingSchedule ? 'Edit Class & Payment Email Template' : 'Edit Training Class'}
                  </h3>
                  <p style={{ color: '#c7d2fe', fontSize: '0.8rem', margin: '2px 0 0 0', textAlign: 'left' }}>
                    Update schedule, capacity limit, fee, payment link, and editable email template.
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff', fontSize: '1.5rem', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsCourseModalOpen(false)}>&times;</button>
            </div>

            {/* Modal Body & Form */}
            <form onSubmit={handleSaveCourse} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', overflowX: 'hidden', maxHeight: '70vh', background: '#ffffff', textAlign: 'left', boxSizing: 'border-box', width: '100%' }}>

                {/* ── Section 1: Course Info ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="book" size={16} />
                  <span>Course Basic Information</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Certification Body *
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={certBody}
                      onChange={e => setCertBody(e.target.value)}
                      placeholder="e.g. ISC2, CompTIA, BlackBerry"
                      required
                    />
                  </div>

                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Course Title / Name *
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={courseTitle}
                      onChange={e => setCourseTitle(e.target.value)}
                      placeholder="e.g. Certified in Cybersecurity (CC)"
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ width: '100%', maxWidth: '280px', marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Capacity Limit (Default: 10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={maxCap}
                      onChange={e => setMaxCap(e.target.value)}
                    />
                  </div>

                  <RichTextEditor
                    label="Course Overview / Description"
                    value={courseDesc}
                    onChange={setCourseDesc}
                    placeholder="e.g. 3 days training. Including Lunch, e-Student guide and exam voucher."
                  />
                </div>

                {/* ── Section 2: Schedule & Timing ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '0.75rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SvgIcon name="calendar" size={16} />
                    <span>Class Schedule & Timing</span>
                  </div>
                  {editSegments.length > 1 && (
                    <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', fontWeight: '600' }}>
                      {editSegments.length} Parts Split Class ({calculateTotalTrainingDays(editSegments)} Days Total)
                    </span>
                  )}
                </div>

                {/* Dynamic Edit Segments List */}
                {editSegments.map((seg, idx) => (
                  <div key={idx} style={{
                    background: editSegments.length > 1 ? '#f8fafc' : 'transparent',
                    border: editSegments.length > 1 ? '1px solid #e2e8f0' : 'none',
                    borderRadius: '10px',
                    padding: editSegments.length > 1 ? '1rem' : '0',
                    marginBottom: '1rem'
                  }}>
                    {editSegments.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>
                            Part {idx + 1}
                          </span>
                          {seg.startDate && seg.endDate && (
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              ({calculateSegmentDays(seg.startDate, seg.endDate)} Training Days)
                            </span>
                          )}
                        </div>
                        {idx > 0 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveSegment(idx, true)}
                            style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                          >
                            ✕ Remove Part
                          </button>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', marginBottom: '0.85rem', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          Start Date *
                        </label>
                        <input
                          type="date"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.startDate}
                          onChange={e => handleSegmentChange(idx, 'startDate', e.target.value, true)}
                          required
                        />
                      </div>

                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          End Date *
                        </label>
                        <input
                          type="date"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.endDate}
                          onChange={e => handleSegmentChange(idx, 'endDate', e.target.value, true)}
                          required
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          Start Time
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.startTime}
                          onChange={e => handleSegmentChange(idx, 'startTime', e.target.value, true)}
                          placeholder="09:00 AM"
                        />
                      </div>

                      <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                          End Time
                        </label>
                        <input
                          type="text"
                          style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                          value={seg.endTime}
                          onChange={e => handleSegmentChange(idx, 'endTime', e.target.value, true)}
                          placeholder="05:00 PM"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Split Part Button */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <button
                    type="button"
                    onClick={() => handleAddSegment(true)}
                    style={{
                      width: '100%',
                      padding: '0.65rem',
                      borderRadius: '8px',
                      border: '1.5px dashed #4338ca',
                      background: '#eef2ff',
                      color: '#4338ca',
                      fontWeight: '600',
                      fontSize: '0.825rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <SvgIcon name="plus" size={15} />
                    <span>{editSegments.length === 1 ? '+ Add Split Part (e.g. Part 2 for Week 2)' : `+ Add Another Part (Part ${editSegments.length + 1})`}</span>
                  </button>
                  {editSegments.length > 1 && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.4rem', textAlign: 'center' }}>
                      Learners register once for all {editSegments.length} parts under this unified class.
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Assigned Trainer
                    </label>
                    <div className="trainer-dropdown-wrapper" ref={trainerDropdownRef}>
                      <div className="trainer-dropdown-input-wrap">
                        <span className="trainer-dropdown-icon">
                          <SvgIcon name="award" size={15} />
                        </span>
                        <input
                          type="text"
                          className="trainer-dropdown-input"
                          placeholder="Search"
                          value={
                            isTrainerDropdownOpen
                              ? trainerSearchTerm
                              : (selectedTrainerId
                                  ? (() => {
                                      const sel = trainersList.find(t => String(t.TrainerID) === String(selectedTrainerId));
                                      return sel ? sel.FullName : trainerSearchTerm;
                                    })()
                                  : trainerSearchTerm)
                          }
                          onChange={(e) => {
                            setTrainerSearchTerm(e.target.value);
                            setIsTrainerDropdownOpen(true);
                            if (selectedTrainerId) {
                              setSelectedTrainerId('');
                            }
                          }}
                          onFocus={() => setIsTrainerDropdownOpen(true)}
                          onBlur={() => {
                            setTimeout(() => {
                              if (!selectedTrainerId && trainerSearchTerm.trim()) {
                                const clean = trainerSearchTerm.trim().toLowerCase();
                                const match = trainersList.find(t => (t.FullName || '').toLowerCase() === clean);
                                if (match) {
                                  setSelectedTrainerId(String(match.TrainerID));
                                  setTrainerSearchTerm(match.FullName);
                                } else if (clean === 'tba' || clean.includes('no trainer')) {
                                  setSelectedTrainerId('');
                                  setTrainerSearchTerm('');
                                } else {
                                  setTrainerSearchTerm('');
                                }
                              }
                            }, 250);
                          }}
                        />
                        {(trainerSearchTerm || selectedTrainerId) && (
                          <button
                            type="button"
                            className="trainer-dropdown-clear-btn"
                            onClick={() => {
                              setSelectedTrainerId('');
                              setTrainerSearchTerm('');
                              setIsTrainerDropdownOpen(true);
                            }}
                            title="Clear selection"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {isTrainerDropdownOpen && (
                        <div className="trainer-dropdown-list">
                          {(() => {
                            const cleanQ = trainerSearchTerm.trim().toLowerCase();

                            const renderTbaOption = (
                              <div
                                key="tba"
                                className={`trainer-dropdown-item ${!selectedTrainerId ? 'selected' : ''}`}
                                onClick={() => {
                                  setSelectedTrainerId('');
                                  setTrainerSearchTerm('');
                                  setIsTrainerDropdownOpen(false);
                                }}
                                style={{ fontWeight: '500', color: '#64748b' }}
                              >
                                <div>-- TBA / No Trainer Assigned --</div>
                              </div>
                            );

                            const filteredTrainers = trainersList.filter(t => {
                              if (!cleanQ) return true;
                              const name = (t.FullName || '').toLowerCase();
                              const email = (t.Email || '').toLowerCase();
                              return name.includes(cleanQ) || email.includes(cleanQ);
                            });

                            return (
                              <>
                                {(!cleanQ || cleanQ.includes('tba')) && renderTbaOption}

                                {filteredTrainers.length > 0 ? (
                                  filteredTrainers.map(t => (
                                    <div
                                      key={t.TrainerID}
                                      className={`trainer-dropdown-item ${String(selectedTrainerId) === String(t.TrainerID) ? 'selected' : ''}`}
                                      onClick={() => {
                                        setSelectedTrainerId(String(t.TrainerID));
                                        setTrainerSearchTerm(t.FullName);
                                        setIsTrainerDropdownOpen(false);
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', color: '#0f172a' }}>{t.FullName}</div>
                                    </div>
                                  ))
                                ) : (
                                  <>
                                    <div className="trainer-dropdown-item no-results">No trainers found</div>
                                    {!cleanQ.includes('tba') && renderTbaOption}
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Section 3: Fee & Payment Settings ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="dollarSign" size={16} />
                  <span>Course Fee & Payment Link</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.25rem', marginBottom: '1.5rem', width: '100%', boxSizing: 'border-box' }}>
                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Course Fee
                    </label>
                    <input
                      type="text"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={fee}
                      onChange={e => setFee(e.target.value)}
                      placeholder="e.g. RM 2,100"
                    />
                  </div>

                  <div style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                      Payment Link URL
                    </label>
                    <input
                      type="url"
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                      value={paymentLink}
                      onChange={e => setPaymentLink(e.target.value)}
                      placeholder="https://buy.stripe.com/..."
                    />
                  </div>
                </div>

                {/* ── Section 4: Payment Email Template ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#4338ca', marginBottom: '1rem', marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="mail" size={16} />
                  <span>Payment Email Template (Editable Per Course)</span>
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Payment Email Subject
                  </label>
                  <input
                    type="text"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                    value={paymentSubject}
                    onChange={e => setPaymentSubject(e.target.value)}
                    placeholder="{CourseName} Course Payment"
                  />
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.3rem', textAlign: 'left' }}>
                    Payment Email Body Template
                  </label>
                  <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.75rem', color: '#64748b', textAlign: 'left' }}>
                    Available Placeholders: <code>{"{LearnerName}"}</code>, <code>{"{CourseName}"}</code>, <code>{"{Fee}"}</code>, <code>{"{PaymentLink}"}</code>, <code>{"{Venue}"}</code>
                  </p>
                  <textarea
                    rows="8"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5', minHeight: '140px' }}
                    value={paymentBody}
                    onChange={e => setPaymentBody(e.target.value)}
                  />
                </div>

              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setIsCourseModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingCourse}
                  style={{ padding: '0.6rem 1.4rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: savingCourse ? 'not-allowed' : 'pointer' }}
                >
                  {savingCourse ? 'Saving...' : editingSchedule ? 'Update Course & Template' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CANCEL COURSE SESSION & EMAIL TEMPLATE MODAL ── */}
      {cancelModalSchedule && (
        <div className="admin-modal-overlay" onClick={() => setCancelModalSchedule(null)} style={{ overflowX: 'hidden' }}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '760px', width: '90%', overflowX: 'hidden', boxSizing: 'border-box', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #b91c1c 100%)', color: '#ffffff', padding: '1.25rem 1.75rem', boxSizing: 'border-box' }}>
              <div className="modal-header-title" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', flexShrink: 0 }}>
                  <SvgIcon name="alertCircle" size={20} />
                </div>
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0, textAlign: 'left' }}>
                    Cancel Class & Email Notification
                  </h3>
                  <p style={{ color: '#fecaca', fontSize: '0.8rem', margin: '2px 0 0 0', textAlign: 'left' }}>
                    Review and customize the cancellation email notice sent to registered learners.
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff', fontSize: '1.5rem', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }} onClick={() => setCancelModalSchedule(null)}>&times;</button>
            </div>

            {/* Modal Body & Form */}
            <form onSubmit={handleConfirmCancelSchedule} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxSizing: 'border-box', width: '100%' }}>
              <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', overflowX: 'hidden', maxHeight: '70vh', background: '#ffffff', textAlign: 'left', boxSizing: 'border-box', width: '100%' }}>
                
                {/* Session Summary Card */}
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target Class</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>{cancelModalSchedule.course}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>Dates: {cancelModalSchedule.dates} | Trainer: {cancelModalSchedule.trainer}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ display: 'inline-block', padding: '4px 10px', background: '#fee2e2', color: '#991b1b', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '700' }}>
                      {cancelModalSchedule.enrolledCount} Registered {cancelModalSchedule.enrolledCount === 1 ? 'Learner' : 'Learners'}
                    </span>
                  </div>
                </div>

                {/* ── Section: Cancellation Email Template ── */}
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#991b1b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SvgIcon name="mail" size={16} />
                  <span>Cancellation Email Template</span>
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Email Subject
                  </label>
                  <input
                    type="text"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff' }}
                    value={cancelEmailSubject}
                    onChange={e => setCancelEmailSubject(e.target.value)}
                    placeholder="Class Cancelled - Course Name"
                    required
                  />
                </div>

                <div style={{ marginBottom: '1.25rem', width: '100%', boxSizing: 'border-box' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.4rem', textAlign: 'left' }}>
                    Email Body Template
                  </label>
                  <textarea
                    rows="12"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.75rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.875rem', outline: 'none', background: '#ffffff', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6', minHeight: '260px' }}
                    value={cancelEmailBody}
                    onChange={e => setCancelEmailBody(e.target.value)}
                    required
                  />
                </div>

              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', boxSizing: 'border-box' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setCancelModalSchedule(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCancellingSchedule}
                  style={{ padding: '0.6rem 1.4rem', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: isCancellingSchedule ? 'not-allowed' : 'pointer' }}
                >
                  {isCancellingSchedule ? 'Cancelling & Sending Emails...' : 'Send Email & Cancel Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Trainer Profile Modal */}
      {editingTrainer && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEditingTrainer(null); }}>
          <div className="modal-content" style={{ maxWidth: '640px', width: '92%' }}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="award" size={18} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>Edit Trainer Profile</h2>
                  <span style={{ fontSize: '0.78rem', color: '#c7d2fe' }}>Update trainer credentials and profile background</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setEditingTrainer(null)}>&times;</button>
            </div>

            <form onSubmit={handleSaveTrainerProfile} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', textAlign: 'left' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="modal-form-field">
                  <label className="modal-label">Full Name</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editTrainerForm.fullName}
                    onChange={(e) => setEditTrainerForm(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                  />
                </div>
                <div className="modal-form-field">
                  <label className="modal-label">Username</label>
                  <input
                    type="text"
                    className="modal-form-input"
                    value={editTrainerForm.username}
                    onChange={(e) => setEditTrainerForm(prev => ({ ...prev, username: e.target.value }))}
                  />
                </div>
              </div>

              <div className="modal-form-field">
                <label className="modal-label">Email Address</label>
                <input
                  type="email"
                  className="modal-form-input"
                  value={editTrainerForm.email}
                  onChange={(e) => setEditTrainerForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div className="modal-form-field">
                <label className="modal-label">Certifications & Credentials</label>
                <input
                  type="text"
                  className="modal-form-input"
                  placeholder="e.g. CompTIA Security+, CySA+, CISSP"
                  value={editTrainerForm.certifications}
                  onChange={(e) => setEditTrainerForm(prev => ({ ...prev, certifications: e.target.value }))}
                />
              </div>

              <div className="modal-form-field">
                <label className="modal-label">Biography / Professional Summary</label>
                <textarea
                  rows={4}
                  className="modal-form-input"
                  style={{ resize: 'vertical' }}
                  placeholder="Key in trainer's bio and background..."
                  value={editTrainerForm.bio}
                  onChange={(e) => setEditTrainerForm(prev => ({ ...prev, bio: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1.25rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.25rem', background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onClick={() => setEditingTrainer(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '0.6rem 1.4rem', background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)' }}
                >
                  Save Trainer Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Recovery Confirmation Modal */}
      {
        recoveryTarget && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setRecoveryTarget(null); }}>
            <div className="modal-content" style={{ maxWidth: '480px', width: '90%' }}>
              <div className="modal-header">
                <h2>Account Recovery</h2>
                <button className="modal-close" onClick={() => setRecoveryTarget(null)}>&times;</button>
              </div>
              <div style={{ padding: '1.25rem 0 0 0' }}>
                <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '1rem', lineHeight: '1.5' }}>
                  Are you sure you want to send an account recovery email to <strong>{recoveryTarget.name}</strong> ({recoveryTarget.email})?
                </p>
                <div style={{ background: '#f8fafc', borderLeft: '4px solid #4f46e5', padding: '12px 16px', borderRadius: '6px', fontSize: '0.85rem', color: '#334155', marginBottom: '1.25rem', lineHeight: '1.6' }}>
                  • Generates a new secure temporary password for this user.<br />
                  • Automatically sends a recovery email with login instructions directly to their email inbox.
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    type="button"
                    style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                    onClick={() => setRecoveryTarget(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isRecovering}
                    onClick={handleExecuteAccountRecovery}
                    style={{ padding: '0.6rem 1.2rem', background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: isRecovering ? 'not-allowed' : 'pointer' }}
                  >
                    {isRecovering ? 'Sending Recovery Email...' : 'Send Recovery Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Account Status Toggle Confirmation Modal */}
      {statusToggleTarget && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setStatusToggleTarget(null); }}>
          <div className="modal-content" style={{ maxWidth: '520px', width: '92%' }}>
            <div className="modal-header" style={{ background: statusToggleTarget.currentIsActive !== 0 ? 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%)' : 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name={statusToggleTarget.currentIsActive !== 0 ? 'alertCircle' : 'checkCircle'} size={18} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>
                    {statusToggleTarget.currentIsActive !== 0 ? 'Deactivate Account' : 'Activate Account'}
                  </h2>
                  <span style={{ fontSize: '0.78rem', color: statusToggleTarget.currentIsActive !== 0 ? '#fecaca' : '#bbf7d0' }}>
                    {statusToggleTarget.role} Account Status Update
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setStatusToggleTarget(null)}>&times;</button>
            </div>

            <div style={{ padding: '1.5rem', textAlign: 'left' }}>
              <p style={{ fontSize: '0.95rem', color: '#1e293b', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
                Are you sure you want to {statusToggleTarget.currentIsActive !== 0 ? 'deactivate' : 'activate'} the account for <strong>{statusToggleTarget.name}</strong>?
              </p>

              <div style={{
                background: statusToggleTarget.currentIsActive !== 0 ? '#fff7ed' : '#f0fdf4',
                borderLeft: statusToggleTarget.currentIsActive !== 0 ? '4px solid #f97316' : '4px solid #22c55e',
                padding: '14px 16px',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: '#334155',
                marginBottom: '1.5rem',
                lineHeight: '1.6'
              }}>
                {statusToggleTarget.currentIsActive !== 0 ? (
                  <>
                    • Suspends login access to the learning management platform for <strong>{statusToggleTarget.name}</strong>.<br />
                    • Automatically dispatches an account suspension notification email to their email address.
                  </>
                ) : (
                  <>
                    • Restores full login access to the learning management platform for <strong>{statusToggleTarget.name}</strong>.<br />
                    • Automatically dispatches an account reactivation confirmation email.
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.25rem', background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onClick={() => setStatusToggleTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isUpdatingStatus}
                  style={{
                    padding: '0.6rem 1.4rem',
                    background: statusToggleTarget.currentIsActive !== 0 ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' : 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '700',
                    cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                  }}
                  onClick={handleConfirmStatusToggle}
                >
                  {isUpdatingStatus ? 'Updating Status...' : statusToggleTarget.currentIsActive !== 0 ? 'Confirm Deactivation' : 'Confirm Activation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {
        deletingTarget && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeletingTarget(null); }}>
            <div className="modal-content" style={{ maxWidth: '440px', width: '90%' }}>
              <div className="modal-header">
                <h2 style={{ color: '#dc2626' }}>Delete {deletingTarget.role} Profile</h2>
                <button className="modal-close" onClick={() => setDeletingTarget(null)}>&times;</button>
              </div>
              <div style={{ padding: '1.25rem 0 0 0' }}>
                <p style={{ fontSize: '0.9rem', color: '#475569', marginBottom: '1rem', lineHeight: '1.5' }}>
                  Are you sure you want to permanently delete the profile for <strong>{deletingTarget.name}</strong>?
                </p>
                <div style={{ background: '#fef2f2', borderLeft: '4px solid #ef4444', padding: '12px 16px', borderRadius: '6px', fontSize: '0.85rem', color: '#991b1b', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                  ⚠️ This action cannot be undone and will permanently remove this {deletingTarget.role.toLowerCase()}'s record from the system.
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    type="button"
                    style={{ padding: '0.6rem 1.2rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                    onClick={() => setDeletingTarget(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    style={{ padding: '0.6rem 1.2rem', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Yes, Delete Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* View Bank Slip Preview Modal */}
      {viewBankSlip && (
        <div className="admin-modal-overlay" onClick={() => setViewBankSlip(null)}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px', width: '90%' }}>
            <div className="admin-modal-header" style={{ background: '#1e293b', color: '#ffffff', padding: '1.25rem 1.75rem' }}>
              <div className="modal-header-title">
                <SvgIcon name="clipboard" size={20} />
                <h3 style={{ color: '#ffffff', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                  Bank Slip Verification: {viewBankSlip.learner}
                </h3>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff' }} onClick={() => setViewBankSlip(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', textAlign: 'center', background: '#f8fafc', maxHeight: '75vh', overflowY: 'auto' }}>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>
                Course: {viewBankSlip.course}
              </p>
              {viewBankSlip.proof && (viewBankSlip.proof.toLowerCase().includes('.pdf') || viewBankSlip.proof.startsWith('data:application/pdf')) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                  <iframe
                    src={viewBankSlip.proof}
                    title="Bank Slip Document"
                    style={{ width: '100%', height: '480px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#ffffff' }}
                  />
                  <a
                    href={viewBankSlip.proof}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', background: '#4f46e5', color: '#ffffff', borderRadius: '6px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    <SvgIcon name="download" size={16} />
                    <span>Download / Open Bank Slip in New Tab</span>
                  </a>
                </div>
              ) : viewBankSlip.proof && (viewBankSlip.proof.startsWith('data:image/') || viewBankSlip.proof.startsWith('http') || viewBankSlip.proof.startsWith('/uploads')) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
                  <img src={viewBankSlip.proof} alt="Bank Slip Proof" style={{ maxWidth: '100%', maxHeight: '480px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }} />
                  <a
                    href={viewBankSlip.proof}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem' }}
                  >
                    <SvgIcon name="search" size={15} />
                    <span>View Full Resolution Image</span>
                  </a>
                </div>
              ) : (
                <div style={{ padding: '2rem', background: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                  <p style={{ margin: '0 0 1rem 0', color: '#334155', fontWeight: 600 }}>Uploaded Payment Proof File:</p>
                  <a href={viewBankSlip.proof} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem', background: '#4f46e5', color: '#ffffff', borderRadius: '6px', textDecoration: 'none', fontWeight: 600 }}>
                    <SvgIcon name="download" size={16} />
                    <span>Download / Open Bank Slip Document</span>
                  </a>
                </div>
              )}
            </div>
            <div style={{ padding: '1rem 1.5rem', background: '#ffffff', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button
                style={{ padding: '0.5rem 1.2rem', background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                onClick={() => setViewBankSlip(null)}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '92%' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="alertCircle" size={20} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.1rem', margin: 0, color: '#ffffff' }}>Confirm Permanent Deletion</h2>
                  <span style={{ fontSize: '0.78rem', color: '#fecaca' }}>Action is immediate & dispatches email notification</span>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setDeleteTarget(null)}>&times;</button>
            </div>

            <div style={{ padding: '1.5rem 1.75rem' }}>
              <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '1rem', marginBottom: '1.25rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b', lineHeight: 1.5 }}>
                  Are you sure you want to permanently delete <strong>{deleteTarget.name}</strong>'s {deleteTarget.accountType} account?
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: '#b91c1c', lineHeight: 1.4 }}>
                  ⚠️ This account has reached 365 days of deactivation. An automated email will be sent notifying the user that their account has been deleted.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  style={{ padding: '0.6rem 1.2rem', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isDeletingAccount}
                  onClick={handleConfirmDeleteAccount}
                  style={{ padding: '0.6rem 1.2rem', background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: isDeletingAccount ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(220, 38, 38, 0.25)' }}
                >
                  {isDeletingAccount ? 'Deleting...' : 'Delete Account Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Request Email Preview & Edit Modal */}
      {paymentEmailModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isSendingPaymentEmail) setPaymentEmailModal(null); }}>
          <div className="modal-content" style={{ maxWidth: '720px', width: '92%' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <SvgIcon name="mail" size={20} />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <h2 style={{ fontSize: '1.15rem', margin: 0, color: '#ffffff', fontWeight: '800' }}>
                    {paymentEmailModal.isResend ? 'Resend Payment Request Email' : 'Payment Request Email'}
                  </h2>
                </div>
              </div>
              <button
                className="modal-close"
                onClick={() => { if (!isSendingPaymentEmail) setPaymentEmailModal(null); }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', textAlign: 'left', maxHeight: '75vh', overflowY: 'auto' }}>
              
              {/* Recipient & Course Summary Pill */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#334155' }}>
                    <strong>To:</strong> <span style={{ color: '#0f172a', fontWeight: '700' }}>{paymentEmailModal.learnerName}</span> &lt;{paymentEmailModal.learnerEmail}&gt;
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#15803d', fontWeight: '700', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '2px 8px', borderRadius: '6px' }}>
                    Fee: {paymentEmailModal.fee}
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  <strong>Course:</strong> {paymentEmailModal.courseName}
                </div>
              </div>

              {/* Editable Email Subject */}
              <div className="modal-form-field">
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem' }}>
                  Email Subject
                </label>
                <input
                  type="text"
                  className="modal-form-input"
                  style={{ width: '100%', boxSizing: 'border-box', background: '#ffffff' }}
                  value={paymentEmailModal.subject}
                  onChange={(e) => setPaymentEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </div>

              {/* Editable Email Body */}
              <div className="modal-form-field">
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#334155', marginBottom: '0.35rem' }}>
                  Email Body Content
                </label>
                <textarea
                  rows="12"
                  className="modal-form-input"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#ffffff',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: '1.6',
                    fontSize: '0.85rem',
                    minHeight: '220px'
                  }}
                  value={paymentEmailModal.body}
                  onChange={(e) => setPaymentEmailModal(prev => ({ ...prev, body: e.target.value }))}
                  required
                />
              </div>

            </div>

            {/* Modal Footer with Cancel and Send buttons on the bottom right */}
            <div style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', boxSizing: 'border-box' }}>
              <button
                type="button"
                disabled={isSendingPaymentEmail}
                style={{ padding: '0.6rem 1.25rem', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: isSendingPaymentEmail ? 'not-allowed' : 'pointer' }}
                onClick={() => setPaymentEmailModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSendingPaymentEmail || !paymentEmailModal.subject.trim() || !paymentEmailModal.body.trim()}
                onClick={handleSendPaymentEmail}
                style={{
                  padding: '0.6rem 1.4rem',
                  background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '700',
                  cursor: isSendingPaymentEmail ? 'not-allowed' : 'pointer',
                  boxShadow: '0 2px 4px rgba(79, 70, 229, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <SvgIcon name="send" size={14} />
                {isSendingPaymentEmail ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CERTIFICATE MANAGEMENT & ISSUANCE MODAL ── */}
      {isCertModalOpen && selectedCertSchedule && (
        <div className="admin-modal-overlay" onClick={() => setIsCertModalOpen(false)} style={{ overflowX: 'hidden' }}>
          <div className="admin-modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '920px', width: '95%', overflowX: 'hidden', boxSizing: 'border-box', margin: 'auto' }}>
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)', color: '#ffffff', padding: '1.25rem 1.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fbbf24', flexShrink: 0 }}>
                  <SvgIcon name="award" size={22} />
                </div>
                <div>
                  <h3 style={{ color: '#ffffff', fontSize: '1.15rem', fontWeight: 800, margin: 0, textAlign: 'left' }}>
                    Class Certificates of Completion
                  </h3>
                  <p style={{ color: '#cbd5e1', fontSize: '0.825rem', margin: '3px 0 0 0', textAlign: 'left' }}>
                    {selectedCertSchedule.course || `${selectedCertSchedule.certificationBody || ''} ${selectedCertSchedule.courseName || ''}`} &bull; {selectedCertSchedule.dates || `${formatToDMY(selectedCertSchedule.startDate)} - ${formatToDMY(selectedCertSchedule.endDate)}`}
                  </p>
                </div>
              </div>
              <button className="modal-close-btn" style={{ color: '#ffffff', fontSize: '1.5rem', background: 'transparent', border: 'none', cursor: 'pointer', flexShrink: 0 }} onClick={() => setIsCertModalOpen(false)}>&times;</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.5rem 1.75rem', overflowY: 'auto', maxHeight: '72vh', background: '#ffffff', textAlign: 'left' }}>
              {/* Course Meta Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.25rem', background: '#f8fafc', padding: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Template Scheme</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: 800, color: certScheduleData?.schedule?.isRccCourse ? '#4338ca' : '#047857', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <SvgIcon name="award" size={15} />
                    {certScheduleData?.schedule?.isRccCourse ? 'RCC Dual Cert (MCMC & RCC)' : 'Standard CCoE (MCMC Logo)'}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Training Duration</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a', marginTop: '2px' }}>
                    {certScheduleData?.schedule?.totalDays || 1} Days ({certScheduleData?.schedule?.durationHours || 8} Hours)
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Issuance Requirement</span>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#047857', marginTop: '2px' }}>
                    100% Attendance Required
                  </div>
                </div>
              </div>

              {loadingCertData ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⏳</div>
                  <p style={{ margin: 0, fontWeight: 600 }}>Loading enrolled learners and attendance records...</p>
                </div>
              ) : !certScheduleData || !certScheduleData.learners || certScheduleData.learners.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>No enrolled learners found for this course schedule.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9', color: '#334155' }}>
                        <th>Learner Name</th>
                        <th>Email / Organisation</th>
                        <th>Attendance Rate</th>
                        <th>Cert Status</th>
                        <th style={{ textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certScheduleData.learners.map((learner) => {
                        const hasCerts = learner.certificates && learner.certificates.length > 0;
                        const isIssuing = issuingCertRegId === learner.registrationId;

                        return (
                          <tr key={learner.registrationId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ fontWeight: 700, color: '#0f172a' }}>
                              {learner.fullName}
                            </td>
                            <td>
                              <div style={{ color: '#334155', fontSize: '0.8rem' }}>{learner.email}</div>
                              {learner.organisationName && (
                                <div style={{ color: '#64748b', fontSize: '0.72rem' }}>{learner.organisationName}</div>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontSize: '0.75rem',
                                  fontWeight: 700,
                                  background: learner.attendanceRate >= 100 ? '#ecfdf5' : '#fef2f2',
                                  color: learner.attendanceRate >= 100 ? '#047857' : '#b91c1c',
                                  border: `1px solid ${learner.attendanceRate >= 100 ? '#a7f3d0' : '#fecaca'}`
                                }}>
                                  {learner.attendanceRate}% ({learner.presentDays}/{learner.totalDays} Days)
                                </span>
                              </div>
                            </td>
                            <td>
                              {hasCerts ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {learner.certificates.map(c => (
                                    <span key={c.CertificateID} style={{ fontSize: '0.72rem', color: '#1e293b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'inline-block' }}>
                                      <strong style={{ color: '#4338ca' }}>[{c.TemplateType}]</strong> {c.CertificateNumber}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>Not Issued</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', flexWrap: 'wrap' }}>
                                {!hasCerts ? (
                                  <button
                                    type="button"
                                    disabled={!learner.isEligible || isIssuing}
                                    onClick={() => handleIssueCertificate(learner.registrationId)}
                                    style={{
                                      padding: '0.4rem 0.8rem',
                                      borderRadius: '6px',
                                      border: learner.isEligible ? '1px solid #059669' : '1px solid #cbd5e1',
                                      background: learner.isEligible ? '#059669' : '#f1f5f9',
                                      color: learner.isEligible ? '#ffffff' : '#94a3b8',
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      cursor: learner.isEligible && !isIssuing ? 'pointer' : 'not-allowed',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                    title={learner.isEligible ? 'Issue official Certificate of Completion' : 'Learner has not met required attendance threshold'}
                                  >
                                    {isIssuing ? 'Generating...' : (
                                      <>
                                        <SvgIcon name="graduationCap" size={14} /> Generate Cert
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <>
                                    {learner.certificates.map(c => (
                                      <a
                                        key={c.CertificateID}
                                        href={`/api/certificates/download/${c.CertificateNumber}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          padding: '0.35rem 0.65rem',
                                          borderRadius: '6px',
                                          border: '1px solid #0284c7',
                                          background: '#f0f9ff',
                                          color: '#0369a1',
                                          fontWeight: 700,
                                          fontSize: '0.75rem',
                                          textDecoration: 'none',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '3px'
                                        }}
                                        title={`View / Download ${c.TemplateType} Certificate`}
                                      >
                                        📄 View ({c.TemplateType})
                                      </a>
                                    ))}
                                    <button
                                      type="button"
                                      disabled={regeneratingCertId === learner.certificates[0]?.CertificateID}
                                      onClick={() => handleRegenerateCert(learner.certificates[0].CertificateID)}
                                      style={{
                                        padding: '0.35rem 0.65rem',
                                        borderRadius: '6px',
                                        border: '1px solid #cbd5e1',
                                        background: '#ffffff',
                                        color: '#334155',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                      }}
                                      title="Regenerate Certificate with current Learner Full Name from Profile"
                                    >
                                      🔄 Regenerate
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div style={{ padding: '0.85rem 1.75rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', boxSizing: 'border-box' }}>
              <button
                type="button"
                style={{ padding: '0.55rem 1.4rem', background: '#334155', color: '#ffffff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                onClick={() => setIsCertModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learner Suspension Modal (Suspend / Unsuspend) */}
      {suspensionModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            maxWidth: '480px',
            width: '100%',
            padding: '2rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                backgroundColor: suspensionModal.isSuspended ? '#dcfce7' : '#fee2e2',
                color: suspensionModal.isSuspended ? '#16a34a' : '#ef4444',
                borderRadius: '50%',
                width: '46px',
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <SvgIcon name={suspensionModal.isSuspended ? 'checkCircle' : 'slash'} size={24} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a', fontWeight: 800 }}>
                  {suspensionModal.isSuspended ? 'Unsuspend Learner Account' : 'Suspend Learner Account'}
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.825rem', color: '#64748b' }}>
                  {suspensionModal.isSuspended
                    ? 'Restore account to Active status & lift blackout'
                    : 'Enforce 2-month blackout suspension across all courses'}
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '0.85rem 1rem',
              fontSize: '0.875rem'
            }}>
              <div><strong>Learner:</strong> {suspensionModal.learner.FullName}</div>
              <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '2px' }}>
                {suspensionModal.learner.Email} ({suspensionModal.learner.Username || 'No username'})
              </div>
              {suspensionModal.isSuspended && suspensionModal.learner.blackoutDaysRemaining && (
                <div style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '6px', fontWeight: 600 }}>
                  Active suspension has {suspensionModal.learner.blackoutDaysRemaining} days remaining.
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>
                {suspensionModal.isSuspended ? 'Override / Unsuspension Reason:' : 'Suspension Reason / Administrative Note:'}
              </label>
              <textarea
                value={suspensionModal.reason}
                onChange={(e) => setSuspensionModal(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={suspensionModal.isSuspended
                  ? 'e.g. Valid medical excuse provided, waiver approved by supervisor.'
                  : 'e.g. Repeated no attendance'}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.65rem 0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                {suspensionModal.isSuspended
                  ? 'The learner will receive an email notice that their account has been restored to Active.'
                  : 'The learner will be logged out and notified via email. Account suspended for 2 months.'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <button
                type="button"
                disabled={suspensionModal.loading}
                onClick={() => setSuspensionModal(null)}
                style={{
                  padding: '0.6rem 1.1rem',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={suspensionModal.loading}
                onClick={handleExecuteToggleSuspension}
                style={{
                  padding: '0.6rem 1.25rem',
                  backgroundColor: suspensionModal.isSuspended ? '#16a34a' : '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                {suspensionModal.loading
                  ? 'Processing...'
                  : suspensionModal.isSuspended ? 'Confirm Unsuspend' : 'Confirm Suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Toast Notification */}
      {
        toast && (
          <div className={`admin-toast-message ${toast.type}`}>
            <div className="toast-icon-wrapper">
              <SvgIcon name="info" size={18} />
            </div>
            <span className="toast-text">{toast.msg}</span>
          </div>
        )
      }
    </div>
  );
};

export default AdminDashboard;
